/**
 * Phylocanvas.gl Tree Renderer
 * Uses the official Phylocanvas library for advanced tree visualization
 */

import { colorManager } from '../color/color-manager';
import { TableauSettingsManager } from '../tableau/settings-manager';
import { HoverTracker } from './hover-tracker';
import { TreeMetadataSync } from '../sync/tree-metadata-sync';
import { ColorBrewerPalettes } from '../color/color-palettes';
import { hexToRgb as hexToRgbShared } from './color-utils';
import { sameMetadataBlockSelection } from './metadata-blocks';

declare const PhylocanvasGL: any;
declare const tableau: any;

export class PhylocanvasRenderer {
  container: HTMLElement;
  newick: string;
  originalNewick: string;
  app: any;
  tree: any;
  nodeStyles: Record<string, any>;
  selectedIds: string[];
  hoveredIds: string[];
  listeners: Record<string, Array<(data: any) => void>>;
  metadataBlocks: any[];
  metadata: Record<string, any>;
  blockSize: number;
  scalebarVisible: boolean;
  scalebarWidth: number;
  scalebarPosition: string | { x: number; y: number };
  scalebarElement: HTMLElement | null;
  scalebarDragging: boolean;
  scalebarDragOffset: { x: number; y: number };
  legendElement: HTMLElement | null;
  legendVisible: boolean;
  legendCollapsed: boolean;
  legendColumns: number;
  legendActiveTab: string | null;
  legendPosition: string | { x: number; y: number };
  legendDragging: boolean;
  legendDragOffset: { x: number; y: number };
  tooltipEnabled: boolean;
  tooltipFields: string[];
  highlightSyncMode: string;
  showLabels: boolean;
  alignLabels: boolean;
  treeLayout: string;
  alignmentLength: number | null;
  treeLengthUnit: string;
  decimalDigits: number;
  originalBranchScale: number | null;
  filteredTips: string[];
  tipLabelMap: Record<string, string>;
  showBranchLengths: boolean;
  hideNullBlocks: boolean;
  zoomBehavior: string;
  initialViewState: any;
  contextMenu: HTMLElement | null;
  internalNodeContextMenu: HTMLElement | null;
  contextMenuNode: any;

  leafNodeSize: number;
  labelSize: number;
  showLeafBorders: boolean;
  showLeafLabels: boolean;
  showInternalNodes: boolean;
  showInternalLabels: boolean;
  colourInternalEdges: boolean;
  solidEdges: boolean;

  lassoEnabled: boolean;
  lassoActive: boolean;
  lassoPoints: Array<{ x: number; y: number }>;
  lassoCanvas: HTMLCanvasElement | null;
  lassoCtx: CanvasRenderingContext2D | null;
  originalController: any;

  _resizeTimeout: ReturnType<typeof setTimeout> | null;
  _resizeScalebarTimeout: ReturnType<typeof setTimeout> | null;
  _containerResizeObserver: ResizeObserver | null;

  // Additional dynamic properties set during lifecycle
  menuContainer: HTMLElement | null;
  nodesLabelsMenuContainer: HTMLElement | null;
  layoutMenuContainer: HTMLElement | null;
  tooltipMenuContainer: HTMLElement | null;
  syncMenuContainer: HTMLElement | null;
  saveSettingsButtonContainer: HTMLElement | null;
  tooltipElement: HTMLElement | null;
  fieldPalettes: Map<string, string>;
  allTooltipFields: string[];
  savedSelectedMetadataFields: string[] | null;
  displayAsField: string | undefined;
  enableColorBy: boolean | undefined;
  colorByField: string | undefined;
  colorByPalette: any;
  horizontalZoom: number | undefined;
  verticalZoom: number | undefined;
  legendDragHandlers: any;
  scalebarDragHandlers: any;
  _scalebarUpdateTimeout: ReturnType<typeof setTimeout> | null;
  _hoverTracker: HoverTracker;
  _pendingHoverNodeId: string | null | undefined;
  _hoverFrame: number | null;
  _lassoMouseDown: ((e: MouseEvent) => void) | null;
  _lassoMouseMove: ((e: MouseEvent) => void) | null;
  _lassoMouseUp: ((e: MouseEvent) => void) | null;
  _lassoContextMenu: ((e: MouseEvent) => void) | null;
  _lassoTestClick: ((e: MouseEvent) => void) | null;

  constructor(container: HTMLElement, newick: string, app: any = null) {
    this.container = container;
    this.newick = newick;
    this.originalNewick = newick;
    this.app = app;
    this.tree = null;
    this.nodeStyles = {};
    this.selectedIds = [];
    this.hoveredIds = [];
    this.listeners = {};
    this.metadataBlocks = [];
    this.metadata = {};
    this.blockSize = 16;
    this.scalebarVisible = true;
    this.scalebarWidth = 100;
    this.scalebarPosition = 'bottom-left';
    this.scalebarElement = null;
    this.scalebarDragging = false;
    this.scalebarDragOffset = { x: 0, y: 0 };
    this.legendElement = null;
    this.legendVisible = true;
    this.legendCollapsed = false;
    this.legendColumns = 2;
    this.legendActiveTab = null;
    this.legendPosition = 'top-left';
    this.legendDragging = false;
    this.legendDragOffset = { x: 0, y: 0 };
    this.tooltipEnabled = true;
    this.highlightSyncMode = 'manual';
    this.tooltipFields = [];
    this.showLabels = true;
    this.alignLabels = true;
    this.treeLayout = 'rc';
    this.alignmentLength = null;
    this.treeLengthUnit = 'none';
    this.decimalDigits = 1;
    this.originalBranchScale = null;
    this.filteredTips = [];
    this.tipLabelMap = {};
    this.showBranchLengths = false;
    this.hideNullBlocks = true;
    this.zoomBehavior = 'zoom-and-pan';
    this.initialViewState = null;
    this.contextMenu = null;
    this.internalNodeContextMenu = null;
    this.contextMenuNode = null;

    this.leafNodeSize = 10;
    this.labelSize = 16;
    this.showLeafBorders = false;
    this.showLeafLabels = true;
    this.showInternalNodes = true;
    this.showInternalLabels = false;
    this.colourInternalEdges = false;
    this.solidEdges = true;

    this.lassoEnabled = false;
    this.lassoActive = false;
    this.lassoPoints = [];
    this.lassoCanvas = null;
    this.lassoCtx = null;
    this.originalController = null;

    this._resizeTimeout = null;
    this._resizeScalebarTimeout = null;
    this._containerResizeObserver = null;

    this.menuContainer = null;
    this.nodesLabelsMenuContainer = null;
    this.layoutMenuContainer = null;
    this.tooltipMenuContainer = null;
    this.syncMenuContainer = null;
    this.saveSettingsButtonContainer = null;
    this.tooltipElement = null;
    this.fieldPalettes = new Map();
    this.allTooltipFields = [];
    this.savedSelectedMetadataFields = null;
    this.displayAsField = undefined;
    this.enableColorBy = undefined;
    this.colorByField = undefined;
    this.colorByPalette = undefined;
    this.horizontalZoom = undefined;
    this.verticalZoom = undefined;
    this.legendDragHandlers = null;
    this.scalebarDragHandlers = null;
    this._scalebarUpdateTimeout = null;
    this._hoverTracker = new HoverTracker();
    this._pendingHoverNodeId = undefined;
    this._hoverFrame = null;
    this._lassoMouseDown = null;
    this._lassoMouseMove = null;
    this._lassoMouseUp = null;
    this._lassoContextMenu = null;
    this._lassoTestClick = null;
  }

  /**
   * Helper function to check if a value should be treated as empty
   */
  isEmptyValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    const strValue = String(value).trim().toLowerCase();
    return strValue === 'null' || strValue === 'n/a' || strValue === 'na' || strValue === '%null%';
  }

  /**
   * Calculate total branch length from Newick string
   * Used to detect if tree is SNP-based (total length > 50) or substitution-based
   */
  calculateTotalBranchLength(newick: string): number {
    if (!newick) {
      return 0;
    }

    let totalLength = 0;
    const branchLengthPattern = /:([0-9.eE+-]+)/g;
    let match;

    while ((match = branchLengthPattern.exec(newick)) !== null) {
      const length = parseFloat(match[1]!);
      if (!isNaN(length)) {
        totalLength += length;
      }
    }

    console.log('[PhylocanvasRenderer] Total branch length calculated:', totalLength);
    return totalLength;
  }

  /**
   * Calculate the maximum branch length from a Newick tree string
   * Used to detect if tree has very small branch values that shouldn't be rounded
   */
  calculateMaxBranchLength(newick: string): number {
    if (!newick) {
      return 0;
    }

    let maxLength = 0;
    const branchLengthPattern = /:([0-9.eE+-]+)/g;
    let match;

    while ((match = branchLengthPattern.exec(newick)) !== null) {
      const length = Math.abs(parseFloat(match[1]!));
      if (!isNaN(length) && length > maxLength) {
        maxLength = length;
      }
    }

    console.log('[PhylocanvasRenderer] Max branch length calculated:', maxLength);
    return maxLength;
  }

  /**
   * Check if decimal digits rounding should be disabled
   * Disabled when max branch length < 0.1 and SNP conversion is not active
   */
  shouldDisableDecimalDigits(): boolean {
    const maxLength = this.calculateMaxBranchLength(this.originalNewick);
    const isSNPConversionActive = this.alignmentLength && this.alignmentLength > 0;
    return maxLength < 0.1 && !isSNPConversionActive;
  }

  /**
   * Transform Newick tree branch lengths to SNP scale
   */
  transformNewickToSNP(newick: string, alignmentLength: number): string {
    if (!alignmentLength || alignmentLength <= 0) {
      return newick;
    }

    console.log('[PhylocanvasRenderer] Transforming Newick to SNP scale with alignment length:', alignmentLength, 'decimal digits:', this.decimalDigits);

    const transformed = newick.replace(/:([0-9.eE+-]+)/g, (_match: string, length: string) => {
      const rawLength = parseFloat(length);
      const snpLength = rawLength * alignmentLength;

      let formatted: string;
      if (this.decimalDigits === 0) {
        formatted = Math.round(snpLength).toString();
      } else {
        formatted = snpLength.toFixed(this.decimalDigits);
      }
      return ':' + formatted;
    });

    console.log('[PhylocanvasRenderer] Sample transformation:', {
      before: newick.substring(0, 100),
      after: transformed.substring(0, 100)
    });

    return transformed;
  }

  /**
   * Round branch lengths in Newick string based on decimal digits
   */
  roundNewickBranchLengths(newick: string, decimalDigits: number): string {
    if (decimalDigits === null || decimalDigits === undefined || decimalDigits < 0) {
      return newick;
    }

    console.log('[PhylocanvasRenderer] Rounding Newick branch lengths with decimal digits:', decimalDigits);

    return newick.replace(/:([0-9.eE+-]+)/g, (match: string, length: string) => {
      const value = parseFloat(length);
      if (isNaN(value)) return match;

      let rounded: string;
      if (decimalDigits === 0) {
        rounded = Math.round(value).toString();
      } else {
        rounded = value.toFixed(decimalDigits);
      }
      return ':' + rounded;
    });
  }

  /**
   * Get the Newick string to use based on current settings
   */
  getActiveNewick(): string {
    const shouldTransformSNP = this.showBranchLengths && this.alignmentLength && this.alignmentLength > 0;
    console.log('[PhylocanvasRenderer] getActiveNewick called:', {
      showBranchLengths: this.showBranchLengths,
      alignmentLength: this.alignmentLength,
      decimalDigits: this.decimalDigits,
      shouldTransformSNP: shouldTransformSNP
    });

    if (shouldTransformSNP) {
      const transformed = this.transformNewickToSNP(this.originalNewick, this.alignmentLength!);
      console.log('[PhylocanvasRenderer] Returning SNP-transformed Newick');
      return transformed;
    }

    const shouldRound = this.showBranchLengths && this.decimalDigits === 0;
    if (shouldRound) {
      const rounded = this.roundNewickBranchLengths(this.originalNewick, this.decimalDigits);
      console.log('[PhylocanvasRenderer] Returning visually-rounded Newick');
      return rounded;
    }

    console.log('[PhylocanvasRenderer] Returning original Newick');
    return this.originalNewick;
  }

  /**
   * Get a custom branch length formatter function based on decimal digits
   */
  getBranchLengthFormatter(): ((branchLength: any) => string) | undefined {
    const digits = this.decimalDigits;
    const isSNPTree = this.showBranchLengths && this.alignmentLength && this.alignmentLength > 0;

    console.log('[PhylocanvasRenderer] getBranchLengthFormatter called:', {
      showBranchLengths: this.showBranchLengths,
      alignmentLength: this.alignmentLength,
      isSNPTree: isSNPTree,
      willReturnFormatter: !isSNPTree
    });

    if (isSNPTree) {
      console.log('[PhylocanvasRenderer] Returning undefined formatter (SNP tree)');
      return undefined;
    }

    console.log('[PhylocanvasRenderer] Returning custom formatter (substitution tree) with', digits, 'decimal digits');
    return (branchLength: any) => {
      if (branchLength === null || branchLength === undefined) {
        return '';
      }

      const value = parseFloat(branchLength);
      if (isNaN(value)) {
        return '';
      }

      if (digits === 0) {
        return Math.round(value).toString();
      } else {
        return value.toFixed(digits);
      }
    };
  }

  /**
   * Render the tree using Phylocanvas
   */
  async render(): Promise<void> {
    if (!window.phylocanvas) {
      console.log('[PhylocanvasRenderer] Waiting for Phylocanvas to load...');
      await new Promise<void>((resolve) => {
        if ((window as any).phylocanvas) {
          resolve();
        } else {
          window.addEventListener('phylocanvas-loaded', () => resolve(), { once: true });
          setTimeout(() => resolve(), 5000);
        }
      });
    }

    let savedSettings: any = null;
    if (this.app && this.app.loadSettings) {
      savedSettings = this.app.loadSettings();
      console.log('[PhylocanvasRenderer] Loaded settings:', savedSettings);
      if (savedSettings && savedSettings.phylocanvas) {
        const saved = savedSettings.phylocanvas;
        console.log('[PhylocanvasRenderer] Phylocanvas settings:', saved);
        if (saved.treeLayout !== undefined) this.treeLayout = saved.treeLayout;
        if (saved.treeLengthUnit !== undefined) this.treeLengthUnit = saved.treeLengthUnit;
        if (saved.decimalDigits !== undefined) this.decimalDigits = saved.decimalDigits;
        if (saved.showLabels !== undefined) this.showLabels = saved.showLabels;
        if (saved.alignLabels !== undefined) this.alignLabels = saved.alignLabels;
        if (saved.scalebarVisible !== undefined) this.scalebarVisible = saved.scalebarVisible;
        if (saved.scalebarWidth !== undefined) this.scalebarWidth = saved.scalebarWidth;
        if (saved.scalebarPosition !== undefined) this.scalebarPosition = saved.scalebarPosition;
        if (saved.legendVisible !== undefined) this.legendVisible = saved.legendVisible;
        else if (saved.showLegend !== undefined) this.legendVisible = saved.showLegend;
        if (saved.legendCollapsed !== undefined) this.legendCollapsed = saved.legendCollapsed;
        if (saved.legendColumns !== undefined) this.legendColumns = saved.legendColumns;
        if (saved.legendPosition !== undefined) this.legendPosition = saved.legendPosition;
        if (saved.tooltipEnabled !== undefined) this.tooltipEnabled = saved.tooltipEnabled;
        if (saved.highlightSyncMode !== undefined) this.highlightSyncMode = saved.highlightSyncMode;
        if (saved.blockSize !== undefined) this.blockSize = saved.blockSize;
        if (saved.hideNullBlocks !== undefined) this.hideNullBlocks = saved.hideNullBlocks;
        if (saved.alignmentLength !== undefined) this.alignmentLength = saved.alignmentLength;
        if (saved.showBranchLengths !== undefined) this.showBranchLengths = saved.showBranchLengths;
        if (saved.zoomBehavior !== undefined) this.zoomBehavior = saved.zoomBehavior;
        if (saved.leafNodeSize !== undefined) this.leafNodeSize = saved.leafNodeSize;
        if (saved.labelSize !== undefined) this.labelSize = saved.labelSize;
        if (saved.showLeafBorders !== undefined) this.showLeafBorders = saved.showLeafBorders;
        else if (saved.leafBorders !== undefined) this.showLeafBorders = saved.leafBorders;
        if (saved.showLeafLabels !== undefined) this.showLeafLabels = saved.showLeafLabels;
        if (saved.showInternalNodes !== undefined) this.showInternalNodes = saved.showInternalNodes;
        if (saved.showInternalLabels !== undefined) this.showInternalLabels = saved.showInternalLabels;
        if (saved.colourInternalEdges !== undefined) this.colourInternalEdges = saved.colourInternalEdges;
        else if (saved.colourEdges !== undefined) this.colourInternalEdges = saved.colourEdges;
        if (saved.solidEdges !== undefined) this.solidEdges = saved.solidEdges;
        console.log('[PhylocanvasRenderer] Settings loaded into state, alignmentLength:', this.alignmentLength);
      }
    }

    if (!savedSettings || !savedSettings.phylocanvas || savedSettings.phylocanvas.treeLengthUnit === undefined) {
      this.treeLengthUnit = 'none';
      console.log('[PhylocanvasRenderer] Using default treeLengthUnit: none');
    }

    this.createCollapsibleMenuSystem();
    this.updateUIControls();

    console.log('[PhylocanvasRenderer] Phylocanvas available:', {
      'window.phylocanvas': typeof (window as any).phylocanvas,
      'phylocanvas keys': (window as any).phylocanvas ? Object.keys((window as any).phylocanvas) : 'N/A'
    });

    let PhylocanvasGLRef: any = null;

    if ((window as any).PhylocanvasGL) {
      PhylocanvasGLRef = (window as any).PhylocanvasGL;
    } else if ((window as any).phylocanvas) {
      PhylocanvasGLRef = (window as any).phylocanvas.PhylocanvasGL
                   || (window as any).phylocanvas.default
                   || (window as any).phylocanvas;
    }

    console.log('[PhylocanvasRenderer] PhylocanvasGL found:', {
      type: typeof PhylocanvasGLRef,
      isFunction: typeof PhylocanvasGLRef === 'function',
      keys: PhylocanvasGLRef ? Object.keys(PhylocanvasGLRef).slice(0, 10) : 'N/A'
    });

    if (!PhylocanvasGLRef || typeof PhylocanvasGLRef !== 'function') {
      console.error('[PhylocanvasRenderer] Phylocanvas library not loaded correctly', {
        'window.PhylocanvasGL': typeof (window as any).PhylocanvasGL,
        'window.phylocanvas': (window as any).phylocanvas ? Object.keys((window as any).phylocanvas) : 'N/A'
      });
      this.container.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Phylocanvas library not available. Switch to the TheiaPhyloViewer renderer.</div>';
      return;
    }

    try {
      console.log('[PhylocanvasRenderer] Creating Phylocanvas instance...');

      console.log('[PhylocanvasRenderer] Initializing tree with settings:', {
        type: this.treeLayout,
        showLabels: this.showLabels,
        alignLabels: this.alignLabels
      });

      const treeConfig: Record<string, any> = {
        source: this.getActiveNewick(),
        type: this.treeLayout,
        size: {
          width: this.container.clientWidth,
          height: this.container.clientHeight
        },
        showLabels: this.showLeafLabels || this.showInternalLabels,
        showLeafLabels: this.showLeafLabels,
        alignLabels: this.alignLabels,
        showBranchLengths: this.showBranchLengths,
        showInternalLabels: this.showInternalLabels,
        nodeSize: this.leafNodeSize,
        fontSize: this.labelSize,
        showShapes: this.showInternalNodes,
        showShapeBorders: this.showLeafBorders,
        shapeBorderWidth: this.showLeafBorders ? 1 : 0,
        styleNodeEdges: this.colourInternalEdges,
        interactive: true,
        highlightColour: [47, 196, 121, 255],
        padding: 20,
        styles: this.nodeStyles,
      };

      const formatter = this.getBranchLengthFormatter();
      if (formatter !== undefined) {
        treeConfig['branchLengthsFormat'] = formatter;
        console.log('[PhylocanvasRenderer] Added branchLengthsFormat to tree config');
      } else {
        console.log('[PhylocanvasRenderer] NOT adding branchLengthsFormat to tree config (SNP tree)');
      }

      console.log('[PhylocanvasRenderer] Tree config:', {
        hasFormatter: !!treeConfig['branchLengthsFormat'],
        showBranchLengths: treeConfig['showBranchLengths'],
        sourceLength: treeConfig['source']?.length || 0
      });

      this.tree = new PhylocanvasGLRef(this.container, treeConfig);

      if (Object.keys(this.nodeStyles).length > 0) {
        this.tree.setProps({ styles: this.nodeStyles });
      }

      console.log('[PhylocanvasRenderer] Tree instance:', this.tree);
      console.log('[PhylocanvasRenderer] Tree properties:', Object.keys(this.tree));
      console.log('[PhylocanvasRenderer] Tree props:', this.tree.props);

      console.log('[PhylocanvasRenderer] Creating scale bar with alignmentLength:', this.alignmentLength);
      this.createScaleBar();

      if (this.scalebarElement) {
        this.updateScalebarStyle();
      }

      this.createTooltip();
      this.createContextMenu();
      this.createInternalNodeContextMenu();

      setTimeout(() => {
        console.log('[PhylocanvasRenderer] Tree after init:', Object.keys(this.tree));

        const canvas = this.container.querySelector('canvas');
        console.log('[PhylocanvasRenderer] Canvas found:', canvas);

        if (this.tree.deck) {
          console.log('[PhylocanvasRenderer] Deck instance found');

          const originalOnClick = this.tree.deck.props.onClick;
          const originalOnViewStateChange = this.tree.deck.props.onViewStateChange;
          this.tree.deck.setProps({
            onClick: (info: any, event: any) => {
              if (originalOnClick) originalOnClick(info, event);

              if (info && info.object && info.object.isLeaf) {
                this.emit('click', { node: info.object });
              }
            },
            onHover: (info: any, event: any) => {
              this.handleTreeNodeHover(info, event);
            },
            onViewStateChange: (params: any) => {
              if (originalOnViewStateChange) originalOnViewStateChange(params);

              if (this._scalebarUpdateTimeout) clearTimeout(this._scalebarUpdateTimeout);
              this._scalebarUpdateTimeout = setTimeout(() => {
                this.updateScalebarLabel();
              }, 100);
            }
          });
          console.log('[PhylocanvasRenderer] Click, hover, and viewStateChange handlers installed');
        }

        if (canvas) {
          canvas.addEventListener('click', (e) => {
            console.log('[PhylocanvasRenderer] Canvas clicked');

            if (this.tree.deck && this.tree.deck.pickObject) {
              const info = this.tree.deck.pickObject({
                x: e.offsetX,
                y: e.offsetY
              });
              console.log('[PhylocanvasRenderer] Picked object:', info);

              if (info && info.object && info.object.isLeaf) {
                this.emit('click', { node: info.object });
              }
            }
          });

          canvas.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            console.log('[PhylocanvasRenderer] Right-click detected');

            let pickedNode: any = null;

            if (this.tree && this.tree.getHighlightedNode) {
              const highlightedNode = this.tree.getHighlightedNode();
              if (highlightedNode) {
                console.log('[PhylocanvasRenderer] Using highlighted node:', highlightedNode);
                pickedNode = highlightedNode;
              }
            }

            if (!pickedNode && this.tree.deck && this.tree.deck.pickObject) {
              const info = this.tree.deck.pickObject({
                x: e.offsetX,
                y: e.offsetY,
                radius: 10
              });
              console.log('[PhylocanvasRenderer] Picked object at cursor:', info);
              pickedNode = info && info.object;
            }

            console.log('[PhylocanvasRenderer] Final picked node:', pickedNode);
            this.showContextMenu(e.clientX, e.clientY, pickedNode);
          });
        }
      }, 500);

      setTimeout(() => {
        if (this.tree && this.tree.getView) {
          this.initialViewState = this.tree.getView();
          console.log('[PhylocanvasRenderer] Stored initial view state:', this.initialViewState);
        }
      }, 1000);

      console.log('[PhylocanvasRenderer] Tree rendered successfully');

      (window as any).phyloTree = this.tree;
      console.log('[PhylocanvasRenderer] DEBUG: Tree exposed as window.phyloTree');
      console.log('[PhylocanvasRenderer] DEBUG: Available tree methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(this.tree)));

      (window as any).testFilterDebug = () => {
        console.log('[DEBUG TEST] Testing filter with Sample_001 to Sample_010');
        const testTips = ['Sample_001', 'Sample_002', 'Sample_003', 'Sample_004', 'Sample_005',
                          'Sample_006', 'Sample_007', 'Sample_008', 'Sample_009', 'Sample_010'];
        if ((window as any).theiaViewer && (window as any).theiaViewer.treeRenderer) {
          (window as any).theiaViewer.treeRenderer.setFilteredTips(testTips);
          console.log('[DEBUG TEST] Filter applied - should only show Sample_001 to Sample_010');
        } else {
          console.error('[DEBUG TEST] Could not find tree renderer');
        }
      };

      (window as any).inspectPhyloTree = () => {
        console.log('[INSPECT] Phylocanvas tree object:', (window as any).phyloTree);
        console.log('[INSPECT] Tree props:', (window as any).phyloTree.props);
        console.log('[INSPECT] All tree keys:', Object.keys((window as any).phyloTree));
        console.log('[INSPECT] Tree prototype methods:', Object.getOwnPropertyNames(Object.getPrototypeOf((window as any).phyloTree)));

        console.log('[INSPECT] Label-related props in tree.props:', Object.keys((window as any).phyloTree.props).filter((k: string) => k.toLowerCase().includes('label')));

        if ((window as any).phyloTree.deck) {
          console.log('[INSPECT] Deck layers:', (window as any).phyloTree.deck.props.layers);
          if ((window as any).phyloTree.deck.props.layers) {
            (window as any).phyloTree.deck.props.layers.forEach((layer: any, i: number) => {
              console.log(`[INSPECT] Layer ${i}:`, {
                id: layer.id,
                type: layer.constructor.name,
                props: Object.keys(layer.props)
              });
            });
          }
        }
      };

      console.log('[PhylocanvasRenderer] DEBUG: Test function available - run window.testFilterDebug() in console');
      console.log('[PhylocanvasRenderer] DEBUG: Inspect function available - run window.inspectPhyloTree() in console');

      this.setupResizeHandlers();
    } catch (error: any) {
      console.error('[PhylocanvasRenderer] Failed to render tree:', error);
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #d32f2f;';
      errorDiv.textContent = `Failed to render tree: ${error.message}`;
      this.container.replaceChildren(errorDiv);
    }
  }

  /**
   * Update node styles
   */
  setNodeStyles(styles: Record<string, any>): void {
    console.log('[PhylocanvasRenderer] Setting node styles:', styles);

    const previousLabelStyles: Record<string, any> = {};

    Object.keys(this.nodeStyles).forEach(nodeId => {
      if (this.nodeStyles[nodeId].label) {
        previousLabelStyles[nodeId] = { label: this.nodeStyles[nodeId].label };
      }
    });

    Object.keys(this.tipLabelMap).forEach(nodeId => {
      if (this.tipLabelMap[nodeId]) {
        previousLabelStyles[nodeId] = { label: this.tipLabelMap[nodeId] };
      }
    });

    this.nodeStyles = {};

    if (this.tree && this.tree.setProps) {
      const phylocanvasStyles: Record<string, any> = {};

      Object.keys(styles).forEach(nodeId => {
        const style = styles[nodeId];
        const phyloStyle = {
          shape: style.shape || 'circle',
          size: style.size || 8,
          fillColour: this.hexToRgb(style.fillColor),
          strokeColour: this.hexToRgb(style.strokeColor),
          ...(previousLabelStyles[nodeId] || {})
        };

        phylocanvasStyles[nodeId] = phyloStyle;
        this.nodeStyles[nodeId] = phyloStyle;
      });

      Object.keys(previousLabelStyles).forEach(nodeId => {
        if (!phylocanvasStyles[nodeId]) {
          this.nodeStyles[nodeId] = previousLabelStyles[nodeId];
          phylocanvasStyles[nodeId] = previousLabelStyles[nodeId];
        }
      });

      console.log('[PhylocanvasRenderer] Converted styles for Phylocanvas:', phylocanvasStyles);

      this.applyFilteredStyles(phylocanvasStyles);
    }
  }

  /**
   * Apply filtered styles based on search
   */
  applyFilteredStyles(phylocanvasStyles: Record<string, any>): void {
    if (!this.tree || !this.tree.setProps) return;

    let finalStyles: Record<string, any> = { ...phylocanvasStyles };

    if (this.filteredTips.length > 0) {
      const matchedSet = new Set(this.filteredTips);

      if (this.tree.prerenderedTree && this.tree.prerenderedTree.leaves) {
        this.tree.prerenderedTree.leaves.forEach((leaf: any) => {
          const nodeId = leaf.id;
          if (!matchedSet.has(nodeId)) {
            finalStyles[nodeId] = {
              shape: 'none',
              size: 0,
              fillColour: [0, 0, 0, 0],
              strokeColour: [0, 0, 0, 0]
            };
          } else if (!finalStyles[nodeId]) {
            finalStyles[nodeId] = {
              shape: 'circle',
              size: 8,
              fillColour: [153, 153, 153],
              strokeColour: [153, 153, 153]
            };
          }
        });
      } else {
        Object.keys(phylocanvasStyles).forEach(nodeId => {
          if (!matchedSet.has(nodeId)) {
            finalStyles[nodeId] = {
              shape: 'none',
              size: 0,
              fillColour: [0, 0, 0, 0],
              strokeColour: [0, 0, 0, 0]
            };
          }
        });
      }

      console.log('[PhylocanvasRenderer] Filtered styles applied:', {
        matched: this.filteredTips.length,
        totalStyled: Object.keys(finalStyles).length,
        hidden: Object.keys(finalStyles).length - this.filteredTips.length
      });

      console.log('[PhylocanvasRenderer] DEBUG: Tree structure:', {
        hasPrerenderedTree: !!this.tree.prerenderedTree,
        hasTree: !!this.tree.tree,
        treeKeys: Object.keys(this.tree),
        prerenderedTreeKeys: this.tree.prerenderedTree ? Object.keys(this.tree.prerenderedTree) : null
      });

      let leaves: any[] | null = null;
      if (this.tree.prerenderedTree && this.tree.prerenderedTree.leaves) {
        leaves = this.tree.prerenderedTree.leaves;
      } else if (this.tree.tree && this.tree.tree.leaves) {
        leaves = this.tree.tree.leaves;
      } else if (this.tree.leaves) {
        leaves = this.tree.leaves;
      }

      console.log('[PhylocanvasRenderer] DEBUG: Found leaves:', leaves ? leaves.length : 'NO LEAVES FOUND');

      if (leaves) {
        console.log('[PhylocanvasRenderer] DEBUG: Total leaves:', leaves.length);
        console.log('[PhylocanvasRenderer] DEBUG: Matched tips:', this.filteredTips);
        console.log('[PhylocanvasRenderer] DEBUG: Sample leaf object:', leaves[0]);

        leaves.forEach((leaf: any) => {
          const nodeId = leaf.id;
          if (!matchedSet.has(nodeId) && finalStyles[nodeId]) {
            finalStyles[nodeId].labelStyle = { display: 'none' };
            finalStyles[nodeId].showLabel = false;
            console.log('[PhylocanvasRenderer] DEBUG: Hiding label for:', nodeId, 'style:', finalStyles[nodeId]);
          } else if (matchedSet.has(nodeId) && finalStyles[nodeId]) {
            finalStyles[nodeId].showLabel = this.showLabels;
            if (finalStyles[nodeId].labelStyle) {
              delete finalStyles[nodeId].labelStyle.display;
            }
          }
        });
      } else {
        console.warn('[PhylocanvasRenderer] WARNING: Could not find leaves in tree structure - labels cannot be hidden');
        console.log('[PhylocanvasRenderer] DEBUG: Attempting to hide labels via finalStyles only');

        Object.keys(finalStyles).forEach(nodeId => {
          if (!matchedSet.has(nodeId)) {
            finalStyles[nodeId].labelStyle = { display: 'none' };
            finalStyles[nodeId].showLabel = false;
            finalStyles[nodeId].label = '';
            finalStyles[nodeId].labelColour = [0, 0, 0, 0];
          } else {
            if (finalStyles[nodeId].label === '') {
              delete finalStyles[nodeId].label;
            }
          }
        });
      }

      console.log('[PhylocanvasRenderer] DEBUG: Final styles sample:', Object.entries(finalStyles).slice(0, 3));
      console.log('[PhylocanvasRenderer] DEBUG: Tree props before setProps:', this.tree.props);

      const labelFilter = (node: any) => {
        return matchedSet.has(node.id);
      };

      this.tree.setProps({
        styles: finalStyles,
        nodeStyles: finalStyles,
        showLeafLabels: this.showLabels,
        labelFilter: labelFilter,
        labels: this.filteredTips
      });

      console.log('[PhylocanvasRenderer] DEBUG: Tree props after setProps:', this.tree.props);
      console.log('[PhylocanvasRenderer] DEBUG: Applied labelFilter and labels:', this.filteredTips);
    } else {
      this.tree.setProps({
        styles: finalStyles,
        nodeStyles: finalStyles,
        showLeafLabels: this.showLabels
      });
    }

    console.log('[PhylocanvasRenderer] Styles applied to tree');
  }

  /**
   * Set filtered tips for search
   */
  setFilteredTips(tips: string[]): void {
    console.log('[PhylocanvasRenderer] Setting filtered tips:', tips ? tips.length : 0, 'tips');
    this.filteredTips = tips || [];

    const phylocanvasStyles: Record<string, any> = {};
    Object.keys(this.nodeStyles).forEach(nodeId => {
      phylocanvasStyles[nodeId] = { ...this.nodeStyles[nodeId] };
    });

    Object.keys(this.tipLabelMap).forEach(nodeId => {
      if (!phylocanvasStyles[nodeId]) {
        phylocanvasStyles[nodeId] = {};
      }
      phylocanvasStyles[nodeId].label = this.tipLabelMap[nodeId];
    });

    this.applyFilteredStyles(phylocanvasStyles);

    if (this.metadataBlocks.length > 0 && Object.keys(this.metadata).length > 0) {
      this.setMetadataBlocks(this.metadataBlocks, this.metadata);
    }
  }

  /**
   * Convert hex color to RGB array
   */
  hexToRgb(hex: string | number[]): number[] {
    return hexToRgbShared(hex);
  }

  /**
   * Update selected IDs
   */
  setSelectedIds(ids: string[]): void {
    this.selectedIds = ids;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ selectedIds: ids });
    }
  }

  /**
   * Update hovered IDs (use Phylocanvas built-in selection)
   */
  setHoveredIds(ids: string[]): void {
    this.hoveredIds = ids || [];

    if (!this.tree || !this.tree.setProps) return;
    if (!this._hoverTracker.hoveredIdsChanged(this.hoveredIds)) return;

    const allSelectedIds = [...new Set([...this.selectedIds, ...this.hoveredIds])];

    this.tree.setProps({ selectedIds: allSelectedIds });
  }

  /**
   * Select nodes by legend value (field + value combination)
   */
  selectNodesByLegendValue(field: string, value: any): void {
    const matchingNodeIds = TreeMetadataSync.labelsForFieldValue(field, String(value));

    console.log(`[PhylocanvasRenderer] Selecting ${matchingNodeIds.length} nodes with ${field}="${value}"`);

    if (matchingNodeIds.length > 0) {
      this.emit('lasso-select', { leafIds: matchingNodeIds, source: 'legend' });
    }
  }

  /**
   * Set metadata blocks
   */
  setMetadataBlocks(fields: any[], metadata: Record<string, any>, autoFit: boolean = false): void {
    console.log('[PhylocanvasRenderer] Setting metadata blocks:', fields, metadata);
    this.metadataBlocks = fields || [];
    this.metadata = metadata || {};

    let filteredMetadata = metadata;
    if (this.filteredTips.length > 0) {
      const matchedSet = new Set(this.filteredTips);
      filteredMetadata = {};
      Object.keys(metadata).forEach(nodeId => {
        if (matchedSet.has(nodeId)) {
          filteredMetadata[nodeId] = metadata[nodeId];
        }
      });
      console.log('[PhylocanvasRenderer] Filtered metadata blocks:', {
        original: Object.keys(metadata).length,
        filtered: Object.keys(filteredMetadata).length
      });
    }

    if (this.tree && this.tree.setProps) {
      this.tree.setProps({
        blocks: fields,
        metadata: filteredMetadata,
        blockLength: this.blockSize,
        blockSize: this.blockSize,
        blockWidth: this.blockSize,
        showBlockHeaders: true,
        blockHeaderFontSize: 12
      });
    }

    this.updateMetadataBlocksMenu(fields);
    this.updateLegend(fields, metadata);

    if (autoFit) {
      setTimeout(() => {
        if (this.tree?.fitInCanvas) {
          this.tree.fitInCanvas();
          this.initialViewState = this.tree?.getView?.();
          console.log('[PhylocanvasRenderer] Auto-fit canvas after metadata blocks applied');

          if (this.scalebarVisible) {
            this.createScaleBar();
          }
        }
      }, 100);
    }
  }

  /**
   * Update metadata blocks menu checkboxes
   */
  updateMetadataBlocksMenu(activeFields: any[]): void {
    if (!this.menuContainer) return;

    const checkboxes = this.menuContainer.querySelectorAll('.metadata-blocks-section input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      (checkbox as HTMLInputElement).checked = activeFields && activeFields.includes((checkbox as HTMLInputElement).value);
    });
  }

  /**
   * Add event listener
   */
  addListener(event: string, callback: (data: any) => void): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event]!.push(callback);
  }

  /**
   * Emit event
   */
  emit(event: string, data: any): void {
    console.log('[PhylocanvasRenderer] Emitting event:', event, 'data:', data, 'listeners:', this.listeners[event] ? this.listeners[event]!.length : 0);
    if (this.listeners[event]) {
      this.listeners[event]!.forEach(callback => callback(data));
    }
  }

  /**
   * Initialize lasso selection canvas overlay
   */
  initializeLassoCanvas(): void {
    if (this.lassoCanvas) {
      console.log('[PhylocanvasRenderer] Lasso canvas already exists');
      return;
    }

    let canvas: HTMLCanvasElement | null = null;

    if (this.tree && this.tree.canvas) {
      canvas = this.tree.canvas;
      console.log('[PhylocanvasRenderer] Found canvas via this.tree.canvas');
    }

    if (!canvas && this.container) {
      canvas = this.container.querySelector('canvas');
      console.log('[PhylocanvasRenderer] Found canvas via container.querySelector');
    }

    if (!canvas) {
      console.error('[PhylocanvasRenderer] Cannot initialize lasso canvas - no tree canvas found!', {
        tree: this.tree,
        treeCanvas: this.tree?.canvas,
        container: this.container,
        containerChildren: this.container?.children
      });
      return;
    }

    const rect = canvas.getBoundingClientRect();

    this.lassoCanvas = document.createElement('canvas');
    this.lassoCanvas.style.position = 'absolute';
    this.lassoCanvas.style.top = '0';
    this.lassoCanvas.style.left = '0';
    this.lassoCanvas.style.width = '100%';
    this.lassoCanvas.style.height = '100%';
    this.lassoCanvas.style.pointerEvents = 'none';
    this.lassoCanvas.style.zIndex = '1000';

    this.lassoCanvas.width = rect.width;
    this.lassoCanvas.height = rect.height;

    this.lassoCtx = this.lassoCanvas.getContext('2d');

    const parent = canvas.parentElement;
    if (parent) {
      parent.style.position = 'relative';
      parent.appendChild(this.lassoCanvas);
    }

    console.log('[PhylocanvasRenderer] Lasso canvas initialized:', {
      width: this.lassoCanvas.width,
      height: this.lassoCanvas.height,
      displayWidth: rect.width,
      displayHeight: rect.height,
      treeCanvas: canvas,
      parent: parent
    });
  }

  /**
   * Enable lasso selection mode
   */
  enableLassoMode(): void {
    if (this.lassoEnabled) return;

    this.lassoEnabled = true;
    this.initializeLassoCanvas();

    if (!this.lassoCanvas) {
      console.error('[PhylocanvasRenderer] Failed to enable lasso mode - lasso canvas not created');
      this.lassoEnabled = false;
      return;
    }

    this.lassoCanvas.style.pointerEvents = 'auto';
    this.lassoCanvas.style.cursor = 'crosshair';
    console.log('[PhylocanvasRenderer] Lasso canvas pointer events:', this.lassoCanvas.style.pointerEvents);
    console.log('[PhylocanvasRenderer] Lasso canvas dimensions:', {
      offsetWidth: this.lassoCanvas.offsetWidth,
      offsetHeight: this.lassoCanvas.offsetHeight,
      width: this.lassoCanvas.width,
      height: this.lassoCanvas.height
    });

    const treeCanvas: HTMLElement | null = this.tree?.canvas || this.container?.querySelector('canvas');
    if (treeCanvas) {
      treeCanvas.style.pointerEvents = 'none';
      treeCanvas.style.cursor = 'crosshair';
      console.log('[PhylocanvasRenderer] Tree canvas pointer events:', treeCanvas.style.pointerEvents);
    }

    this.setupLassoEventListeners();

    if (this.tree && this.tree.deck) {
      this.originalController = this.tree.deck.props.controller;

      this.tree.deck.setProps({
        controller: false
      });
      console.log('[PhylocanvasRenderer] Tree pan/zoom disabled');
    }

    if (this.container) {
      this.container.style.cursor = 'crosshair';
    }

    this.showDisableLassoButton();

    console.log('[PhylocanvasRenderer] Lasso mode enabled');
  }

  /**
   * Show disable lasso button in tree container
   */
  showDisableLassoButton(): void {
    let btn = document.getElementById('disable-lasso-btn');

    if (!btn) {
      btn = document.createElement('button');
      btn.id = 'disable-lasso-btn';
      btn.className = 'btn-disable-lasso';
      btn.title = 'Disable lasso selection mode';

      btn.innerHTML = `
        <span class="lasso-icon">⊘</span>
        <span class="lasso-label">Disable Lasso</span>
      `;

      btn.addEventListener('click', () => {
        this.disableLassoMode();
      });

      this.container.appendChild(btn);
    }

    btn.style.display = 'flex';
  }

  /**
   * Hide disable lasso button
   */
  hideDisableLassoButton(): void {
    const btn = document.getElementById('disable-lasso-btn');
    if (btn) {
      btn.style.display = 'none';
    }
  }

  /**
   * Disable lasso selection mode
   */
  disableLassoMode(): void {
    if (!this.lassoEnabled) return;

    this.lassoEnabled = false;
    this.clearLasso();
    this.removeLassoEventListeners();

    if (this.lassoCanvas) {
      this.lassoCanvas.style.pointerEvents = 'none';
      this.lassoCanvas.style.cursor = 'default';
    }

    const treeCanvas: HTMLElement | null = this.tree?.canvas || this.container?.querySelector('canvas');
    if (treeCanvas) {
      treeCanvas.style.pointerEvents = 'auto';
      treeCanvas.style.cursor = 'default';
    }

    if (this.tree && this.tree.deck && this.originalController !== undefined) {
      this.tree.deck.setProps({
        controller: this.originalController
      });
      console.log('[PhylocanvasRenderer] Tree pan/zoom re-enabled');
    }

    if (this.container) {
      this.container.style.cursor = 'default';
    }

    this.hideDisableLassoButton();

    console.log('[PhylocanvasRenderer] Lasso mode disabled');
  }

  /**
   * Setup lasso event listeners
   */
  setupLassoEventListeners(): void {
    if (!this.lassoCanvas) {
      console.error('[PhylocanvasRenderer] Cannot setup lasso listeners - no lasso canvas');
      return;
    }

    this._lassoMouseDown = this.handleLassoMouseDown.bind(this);
    this._lassoMouseMove = this.handleLassoMouseMove.bind(this);
    this._lassoMouseUp = this.handleLassoMouseUp.bind(this);
    this._lassoContextMenu = this.handleLassoContextMenu.bind(this);

    this._lassoTestClick = (e: MouseEvent) => {
      console.log('[PhylocanvasRenderer] TEST: Mouse event captured on lasso canvas!', {
        type: e.type,
        clientX: e.clientX,
        clientY: e.clientY,
        target: e.target
      });
    };

    this.lassoCanvas.addEventListener('mousedown', this._lassoTestClick, { capture: true });
    this.lassoCanvas.addEventListener('mousedown', this._lassoMouseDown);
    this.lassoCanvas.addEventListener('mousemove', this._lassoMouseMove);
    this.lassoCanvas.addEventListener('mouseup', this._lassoMouseUp);
    this.lassoCanvas.addEventListener('mouseleave', this._lassoMouseUp);
    this.lassoCanvas.addEventListener('contextmenu', this._lassoContextMenu);

    console.log('[PhylocanvasRenderer] Lasso event listeners attached to lasso canvas:', this.lassoCanvas);
  }

  /**
   * Remove lasso event listeners
   */
  removeLassoEventListeners(): void {
    if (!this.lassoCanvas) return;

    if (this._lassoMouseDown) {
      this.lassoCanvas.removeEventListener('mousedown', this._lassoTestClick!, { capture: true });
      this.lassoCanvas.removeEventListener('mousedown', this._lassoMouseDown);
      this.lassoCanvas.removeEventListener('mousemove', this._lassoMouseMove!);
      this.lassoCanvas.removeEventListener('mouseup', this._lassoMouseUp!);
      this.lassoCanvas.removeEventListener('mouseleave', this._lassoMouseUp!);
      this.lassoCanvas.removeEventListener('contextmenu', this._lassoContextMenu!);

      console.log('[PhylocanvasRenderer] Lasso event listeners removed');
    }
  }

  /**
   * Handle lasso mouse down
   */
  handleLassoMouseDown(e: MouseEvent): void {
    if (!this.lassoEnabled) return;

    this.lassoActive = true;
    this.lassoPoints = [];

    const rect = this.lassoCanvas!.getBoundingClientRect();
    this.lassoPoints.push({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    console.log('[PhylocanvasRenderer] Lasso started at', this.lassoPoints[0]);
  }

  /**
   * Handle lasso mouse move
   */
  handleLassoMouseMove(e: MouseEvent): void {
    if (!this.lassoActive) return;

    const rect = this.lassoCanvas!.getBoundingClientRect();
    this.lassoPoints.push({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    this.drawLasso();
  }

  /**
   * Handle lasso mouse up
   */
  handleLassoMouseUp(_e: MouseEvent): void {
    if (!this.lassoActive) return;

    this.lassoActive = false;

    if (this.lassoPoints.length > 2) {
      this.lassoPoints.push(this.lassoPoints[0]!);
      this.drawLasso();

      this.performLassoSelection();
    }

    console.log('[PhylocanvasRenderer] Lasso completed with', this.lassoPoints.length, 'points');

    setTimeout(() => this.clearLasso(), 500);
  }

  /**
   * Handle right-click on lasso canvas to show context menu
   */
  handleLassoContextMenu(e: MouseEvent): void {
    e.preventDefault();
    console.log('[PhylocanvasRenderer] Right-click on lasso canvas');

    this.showContextMenu(e.clientX, e.clientY, null);
  }

  /**
   * Draw lasso path on overlay canvas
   */
  drawLasso(): void {
    if (!this.lassoCtx || this.lassoPoints.length < 2) return;

    const ctx = this.lassoCtx;
    ctx.clearRect(0, 0, this.lassoCanvas!.width, this.lassoCanvas!.height);

    ctx.beginPath();
    ctx.moveTo(this.lassoPoints[0]!.x, this.lassoPoints[0]!.y);

    for (let i = 1; i < this.lassoPoints.length; i++) {
      ctx.lineTo(this.lassoPoints[i]!.x, this.lassoPoints[i]!.y);
    }

    ctx.strokeStyle = 'rgba(0, 123, 255, 0.8)';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.stroke();

    ctx.fillStyle = 'rgba(0, 123, 255, 0.1)';
    ctx.fill();
  }

  /**
   * Clear lasso drawing
   */
  clearLasso(): void {
    this.lassoPoints = [];
    if (this.lassoCtx) {
      this.lassoCtx.clearRect(0, 0, this.lassoCanvas!.width, this.lassoCanvas!.height);
    }
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm
   */
  isPointInPolygon(point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>): boolean {
    let inside = false;
    const x = point.x;
    const y = point.y;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i]!.x;
      const yi = polygon[i]!.y;
      const xj = polygon[j]!.x;
      const yj = polygon[j]!.y;

      const intersect = ((yi > y) !== (yj > y)) &&
                       (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Get bounding box of a polygon
   */
  getPolygonBoundingBox(polygon: Array<{ x: number; y: number }>): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const point of polygon) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }

    return { minX, minY, maxX, maxY };
  }

  /**
   * Perform lasso selection - find all leaves inside the lasso polygon
   */
  performLassoSelection(): void {
    if (!this.tree || !this.tree.deck) {
      console.warn('[PhylocanvasRenderer] Tree or deck not available for lasso selection');
      return;
    }

    console.log('[PhylocanvasRenderer] Starting lasso selection...');

    const bbox = this.getPolygonBoundingBox(this.lassoPoints);
    const width = bbox.maxX - bbox.minX;
    const height = bbox.maxY - bbox.minY;

    console.log('[PhylocanvasRenderer] Lasso bounding box:', bbox, 'size:', width, 'x', height);

    const selectedLeafIds = new Set<string>();

    const minSamples = 10;
    const maxSamples = 50;
    const samplesX = Math.min(maxSamples, Math.max(minSamples, Math.floor(width / 5)));
    const samplesY = Math.min(maxSamples, Math.max(minSamples, Math.floor(height / 5)));

    console.log('[PhylocanvasRenderer] Sampling', samplesX, 'x', samplesY, 'points');

    for (let i = 0; i <= samplesX; i++) {
      for (let j = 0; j <= samplesY; j++) {
        const x = bbox.minX + (i / samplesX) * width;
        const y = bbox.minY + (j / samplesY) * height;

        if (!this.isPointInPolygon({ x, y }, this.lassoPoints)) {
          continue;
        }

        try {
          const pickInfo = this.tree.deck.pickObject({
            x: Math.round(x),
            y: Math.round(y),
            radius: 3
          });

          if (pickInfo && pickInfo.object) {
            const obj = pickInfo.object;

            if (obj.isLeaf && obj.id) {
              selectedLeafIds.add(obj.id);
            }
          }
        } catch (error) {
          // Ignore picking errors
        }
      }
    }

    console.log('[PhylocanvasRenderer] Lasso selected', selectedLeafIds.size, 'leaves');

    if (selectedLeafIds.size > 0) {
      this.emit('lasso-select', { leafIds: Array.from(selectedLeafIds), source: 'lasso' });
    }
  }


  /**
   * Create collapsible menu system with main button and vertical options
   */
  createCollapsibleMenuSystem(): void {
    console.log('[Menu System] createCollapsibleMenuSystem called');

    const mainContainer = document.createElement('div');
    mainContainer.className = 'phylocanvas-collapsible-menu';
    mainContainer.innerHTML = `
      <button class="main-menu-toggle" id="main-menu-toggle">
        <span class="toggle-icon">⚙️</span>
      </button>
      <div class="menu-options-column" id="menu-options-column">
        <button class="menu-option-btn" data-menu="appearance">
          <span class="option-icon">🎨</span>
          <span class="option-label">Appearance</span>
        </button>
        <button class="menu-option-btn" data-menu="layout">
          <span class="option-icon">📐</span>
          <span class="option-label">Layout</span>
        </button>
        <button class="menu-option-btn" data-menu="metadata">
          <span class="option-icon">📊</span>
          <span class="option-label">Metadata</span>
        </button>
        <button class="menu-option-btn" data-menu="tooltip">
          <span class="option-icon">💬</span>
          <span class="option-label">Tooltip</span>
        </button>
        <button class="menu-option-btn" data-menu="sync" style="display: none;">
          <span class="option-icon">🔄</span>
          <span class="option-label">Sync</span>
        </button>
      </div>
    `;

    this.container.appendChild(mainContainer);

    this.createNodesLabelsMenu();
    this.createControlMenu();
    this.createMetadataBlocksMenu([]);
    this.createTooltipMenu();
    this.createSyncMenu();
    this.createSaveSettingsButton();

    // Only the Tableau app can act on the sync mode, so the option stays hidden elsewhere.
    if (this.supportsHighlightSync()) {
      const syncOption = mainContainer.querySelector('[data-menu="sync"]') as HTMLElement | null;
      if (syncOption) syncOption.style.display = '';
    }

    const mainToggle = mainContainer.querySelector('#main-menu-toggle') as HTMLElement;
    const optionsColumn = mainContainer.querySelector('#menu-options-column') as HTMLElement;
    const optionButtons = mainContainer.querySelectorAll('.menu-option-btn');
    const saveSettingsBtn = this.saveSettingsButtonContainer;

    let isExpanded = false;
    let activeMenu: HTMLElement | null = null;
    let isPositioning = false;

    mainToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      isExpanded = !isExpanded;

      if (isExpanded) {
        optionsColumn.classList.add('expanded');
        mainToggle.classList.add('active');
        if (saveSettingsBtn) {
          saveSettingsBtn.classList.add('visible');
        }
      } else {
        optionsColumn.classList.remove('expanded');
        mainToggle.classList.remove('active');
        if (saveSettingsBtn) {
          saveSettingsBtn.classList.remove('visible');
        }
        if (activeMenu) {
          activeMenu.classList.remove('open');
          activeMenu = null;
        }
        optionButtons.forEach(btn => btn.classList.remove('active'));
      }
    });

    const positionMenuPanel = (menuContent: HTMLElement, optionButton: HTMLElement, source: string = 'unknown') => {
      // Force browser reflow so getBoundingClientRect() returns accurate
      // dimensions after the panel becomes visible. Accessing these layout
      // properties is the intentional side-effect, there is no return value.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      menuContent.offsetHeight;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      menuContent.scrollHeight;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      menuContent.clientHeight;

      const btnRect = optionButton.getBoundingClientRect();
      const menuRect = menuContent.getBoundingClientRect();

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      console.log(`[Menu Positioning] Source: ${source}`, {
        menuWidth: menuRect.width,
        menuHeight: menuRect.height,
        scrollHeight: menuContent.scrollHeight,
        clientHeight: menuContent.clientHeight,
        hasScrollbar: menuContent.scrollHeight > menuContent.clientHeight,
        buttonLeft: btnRect.left,
        buttonTop: btnRect.top,
        viewportHeight: viewportHeight,
        isPositioning: isPositioning,
        stackTrace: new Error().stack!.split('\n').slice(2, 5).join('\n')
      });

      let leftPosition = btnRect.right + 10;

      const spaceBelow = viewportHeight - btnRect.top;
      const spaceAbove = btnRect.bottom;

      const maxMenuHeight = Math.max(300, viewportHeight - 100);
      menuContent.style.maxHeight = `${maxMenuHeight}px`;

      let topPosition = btnRect.top;

      if (btnRect.top + menuRect.height > viewportHeight) {
        const margin = 20;
        topPosition = Math.max(margin, viewportHeight - menuRect.height - margin);

        if (spaceAbove > spaceBelow && spaceAbove > menuRect.height) {
          topPosition = Math.max(margin, btnRect.bottom - menuRect.height);
        }
      }

      if (leftPosition + menuRect.width > viewportWidth) {
        leftPosition = btnRect.left - menuRect.width - 10;
      }

      leftPosition = Math.max(10, leftPosition);

      menuContent.style.left = `${leftPosition}px`;
      menuContent.style.top = `${topPosition}px`;
    };

    const handlerId = Math.random().toString(36).substr(2, 9);
    console.log(`[Menu System] Attaching click handlers with ID: ${handlerId}`);

    optionButtons.forEach(btn => {
      const btnEl = btn as HTMLElement;
      console.log(`[Menu System] Attaching handler ${handlerId} to button: ${btnEl.dataset['menu']}`);
      btnEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const menuType = btnEl.dataset['menu'];
        console.log(`[Menu Click] Handler ${handlerId} fired for ${menuType}, timestamp: ${Date.now()}`);

        if (this.app && this.app.isCompactMode) {
          this.app.showTabbedSettingsDialog(menuType);
          return;
        }

        let menuContent: HTMLElement | null = null;

        switch(menuType) {
          case 'appearance':
            menuContent = this.container.querySelector('#nodes-labels-menu');
            break;
          case 'layout':
            menuContent = this.container.querySelector('#tree-layout-menu');
            break;
          case 'metadata':
            menuContent = this.container.querySelector('#metadata-blocks-menu');
            break;
          case 'tooltip':
            menuContent = this.container.querySelector('#tooltip-menu');
            break;
          case 'sync':
            menuContent = this.container.querySelector('#sync-menu');
            break;
        }

        if (menuContent) {
          if (activeMenu === menuContent) {
            menuContent.classList.remove('open');
            activeMenu = null;
            btnEl.classList.remove('active');
          } else {
            if (activeMenu) {
              activeMenu.classList.remove('open');
            }
            optionButtons.forEach(b => b.classList.remove('active'));

            menuContent.classList.add('open');

            isPositioning = true;

            requestAnimationFrame(() => {
              positionMenuPanel(menuContent!, btnEl, 'fixed-width-360px');
              isPositioning = false;
            });

            activeMenu = menuContent;
            btnEl.classList.add('active');
          }
        }
      });
    });

    const handleReposition = () => {
      if (isPositioning) {
        console.log('[Menu Positioning] Skipping reposition - initial positioning in progress');
        return;
      }

      if (activeMenu && isExpanded) {
        const activeButton = mainContainer.querySelector('.menu-option-btn.active') as HTMLElement | null;
        if (activeButton) {
          console.log('[Menu Positioning] Repositioning due to resize/layout change');
          positionMenuPanel(activeMenu, activeButton, 'ResizeObserver-or-windowResize');
        }
      }
    };

    window.addEventListener('resize', handleReposition);

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(handleReposition);
      resizeObserver.observe(this.container);
    }

    document.addEventListener('click', (e) => {
      if (saveSettingsBtn && saveSettingsBtn.contains(e.target as Node)) {
        return;
      }

      if (!mainContainer.contains(e.target as Node)) {
        const allMenus = [
          this.container.querySelector('#nodes-labels-menu'),
          this.container.querySelector('#tree-layout-menu'),
          this.container.querySelector('#metadata-blocks-menu'),
          this.container.querySelector('#tooltip-menu'),
          this.container.querySelector('#sync-menu')
        ];

        const clickedOnMenu = allMenus.some(menu => menu && menu.contains(e.target as Node));

        if (!clickedOnMenu && isExpanded) {
          isExpanded = false;
          optionsColumn.classList.remove('expanded');
          mainToggle.classList.remove('active');
          if (saveSettingsBtn) {
            saveSettingsBtn.classList.remove('visible');
          }
          if (activeMenu) {
            activeMenu.classList.remove('open');
            activeMenu = null;
          }
          optionButtons.forEach(btn => btn.classList.remove('active'));
        }
      }
    });
  }

  /**
   * Create Nodes & Labels control menu
   */
  createNodesLabelsMenu(): void {
    const menuContainer = document.createElement('div');
    menuContainer.className = 'phylocanvas-control-menu nodes-labels-menu';

    menuContainer.innerHTML = `
      <button class="menu-trigger" id="nodes-labels-btn">
        <span>⚙</span> Nodes & Labels
      </button>
      <div class="menu-content compact-menu" id="nodes-labels-menu">
        <div class="menu-section compact">
          <div class="menu-subsection-title">Size</div>
          <label class="menu-item slider-item">
            <span style="font-size: 12px; color: #666; margin-bottom: 4px; display: block;">Node Size</span>
            <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
              <input type="range" id="leaf-node-size-slider" min="1" max="20" value="10" step="1" style="flex: 1; margin-right: 12px;">
              <span id="leaf-node-size-value" style="font-weight: 600; color: #1976d2; min-width: 35px; text-align: right;">10px</span>
            </div>
          </label>
          <label class="menu-item slider-item">
            <span style="font-size: 12px; color: #666; margin-bottom: 4px; display: block;">Label Size</span>
            <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
              <input type="range" id="label-size-slider" min="8" max="24" value="16" step="1" style="flex: 1; margin-right: 12px;">
              <span id="label-size-value" style="font-weight: 600; color: #1976d2; min-width: 35px; text-align: right;">16px</span>
            </div>
          </label>

          <div class="menu-subsection-title" style="margin-top: 12px;">Visibility</div>
          <label class="menu-item toggle-item compact">
            <span>Leaf Borders</span>
            <input type="checkbox" class="toggle-switch" id="leaf-borders-toggle">
          </label>
          <label class="menu-item toggle-item compact">
            <span>Leaf Labels</span>
            <input type="checkbox" class="toggle-switch" id="leaf-labels-toggle" checked>
          </label>
          <label class="menu-item compact" style="display: block; padding-left: 24px; margin-top: 4px;">
            <select id="displayAsField" style="width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;">
              <option value="">Tree file value</option>
            </select>
          </label>
          <label class="menu-item toggle-item compact">
            <span>Align Labels</span>
            <input type="checkbox" class="toggle-switch" id="align-labels-toggle-nodes" checked>
          </label>
          <label class="menu-item toggle-item compact">
            <span>Internal Labels</span>
            <input type="checkbox" class="toggle-switch" id="internal-labels-toggle">
          </label>
          <label class="menu-item toggle-item compact">
            <span>Colour Edges</span>
            <input type="checkbox" class="toggle-switch" id="colour-edges-toggle">
          </label>
          <label class="menu-item toggle-item compact" style="display: none;">
            <span>Solid Edges</span>
            <input type="checkbox" class="toggle-switch" id="solid-edges-toggle" checked>
          </label>

          <div class="menu-subsection-title" style="margin-top: 12px;">Leaf Color</div>
          <label class="menu-item toggle-item compact">
            <span>Enable Coloring</span>
            <input type="checkbox" class="toggle-switch" id="enableColorBy">
          </label>
          <label class="menu-item compact" style="display: block; margin-top: 4px;">
            <div style="display: grid; grid-template-columns: 1fr auto; gap: 6px; align-items: center;">
              <select id="colorByField" disabled style="padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;">
              </select>
              <button id="colorPaletteBtn" class="btn-secondary" disabled title="Select color palette" style="padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 16px; background: white; cursor: pointer; white-space: nowrap;">
                🎨
              </button>
            </div>
            <div class="palette-preview leaf-color-palette-preview" id="leafColorPalettePreview" style="margin-top: 6px; display: none;">
            </div>
          </label>
        </div>
      </div>
    `;

    this.container.appendChild(menuContainer);
    this.nodesLabelsMenuContainer = menuContainer;

    const trigger = menuContainer.querySelector('.menu-trigger') as HTMLElement;
    const menu = menuContainer.querySelector('.menu-content') as HTMLElement;
    const leafSizeSlider = menuContainer.querySelector('#leaf-node-size-slider') as HTMLInputElement | null;
    const leafSizeValue = menuContainer.querySelector('#leaf-node-size-value') as HTMLElement | null;
    const labelSizeSlider = menuContainer.querySelector('#label-size-slider') as HTMLInputElement | null;
    const labelSizeValue = menuContainer.querySelector('#label-size-value') as HTMLElement | null;
    const leafBordersToggle = menuContainer.querySelector('#leaf-borders-toggle') as HTMLInputElement | null;
    const leafLabelsToggle = menuContainer.querySelector('#leaf-labels-toggle') as HTMLInputElement | null;
    const alignLabelsToggle = menuContainer.querySelector('#align-labels-toggle-nodes') as HTMLInputElement | null;
    const internalLabelsToggle = menuContainer.querySelector('#internal-labels-toggle') as HTMLInputElement | null;
    const colourEdgesToggle = menuContainer.querySelector('#colour-edges-toggle') as HTMLInputElement | null;
    const solidEdgesToggle = menuContainer.querySelector('#solid-edges-toggle') as HTMLInputElement | null;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.classList.toggle('open');
    });

    document.addEventListener('click', (e) => {
      if (!menuContainer.contains(e.target as Node)) {
        menu.classList.remove('open');
      }
    });

    if (leafSizeSlider && leafSizeValue) {
      leafSizeSlider.addEventListener('input', (e) => {
        const size = parseInt((e.target as HTMLInputElement).value);
        leafSizeValue.textContent = `${size}px`;
        this.setLeafNodeSize(size);
      });
    }

    if (labelSizeSlider && labelSizeValue) {
      labelSizeSlider.addEventListener('input', (e) => {
        const size = parseInt((e.target as HTMLInputElement).value);
        labelSizeValue.textContent = `${size}px`;
        this.setLabelSize(size);
      });
    }

    if (leafBordersToggle) {
      leafBordersToggle.addEventListener('change', (e) => {
        this.toggleLeafBorders((e.target as HTMLInputElement).checked);
      });
    }

    if (leafLabelsToggle) {
      leafLabelsToggle.addEventListener('change', (e) => {
        const isChecked = (e.target as HTMLInputElement).checked;
        this.toggleLeafLabels(isChecked);

        if (alignLabelsToggle) {
          alignLabelsToggle.disabled = !isChecked;
          if (!isChecked) {
            alignLabelsToggle.style.opacity = '0.5';
            alignLabelsToggle.style.cursor = 'not-allowed';
          } else {
            alignLabelsToggle.style.opacity = '1';
            alignLabelsToggle.style.cursor = 'pointer';
          }
        }
      });
    }

    if (alignLabelsToggle) {
      alignLabelsToggle.addEventListener('change', (e) => {
        this.toggleLabelAlignment((e.target as HTMLInputElement).checked);
      });
    }

    if (internalLabelsToggle) {
      internalLabelsToggle.addEventListener('change', (e) => {
        this.toggleInternalLabels((e.target as HTMLInputElement).checked);
      });
    }

    if (colourEdgesToggle) {
      colourEdgesToggle.addEventListener('change', (e) => {
        this.toggleColourEdges((e.target as HTMLInputElement).checked);
      });
    }

    if (solidEdgesToggle) {
      solidEdgesToggle.addEventListener('change', (e) => {
        this.toggleSolidEdges((e.target as HTMLInputElement).checked);
      });
    }
  }

  /**
   * Create control menu for tree layout options
   */
  createControlMenu(): void {
    const menuContainer = document.createElement('div');
    menuContainer.className = 'phylocanvas-control-menu';

    menuContainer.innerHTML = `
      <button class="menu-trigger" id="tree-layout-btn">
        <span>⚙</span> Layout
      </button>
      <div class="menu-content compact-menu" id="tree-layout-menu">
        <div class="menu-section compact">
          <div class="menu-subsection-title">Tree Layout</div>
          <label class="menu-item compact" style="display: block;">
            <select id="tree-layout-select" style="width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;">
              <option value="rc">Rectangular</option>
              <option value="rd">Radial</option>
              <option value="cr">Circular</option>
              <option value="dg">Diagonal</option>
              <option value="hr">Hierarchical</option>
            </select>
          </label>

          <div class="menu-subsection-title" style="margin-top: 12px;">Scale Bar</div>
          <label class="menu-item toggle-item compact">
            <span>Show Scale Bar</span>
            <input type="checkbox" class="toggle-switch" id="show-scalebar-checkbox" checked>
          </label>
          <label class="menu-item compact" style="display: block;">
            <div style="display: grid; grid-template-columns: auto 1fr; gap: 8px; align-items: center;">
              <span style="font-size: 12px; white-space: nowrap;">Position:</span>
              <select id="scalebar-position-select" style="padding: 6px 10px; border-radius: 4px; border: 1px solid #ddd; font-size: 13px;">
                <option value="bottom-left" selected>Bottom Left</option>
                <option value="bottom-right">Bottom Right</option>
                <option value="top-left">Top Left</option>
                <option value="top-right">Top Right</option>
              </select>
            </div>
          </label>
          <label class="menu-item slider-item compact">
            <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
              <input type="range" id="scalebar-width-slider" min="50" max="200" value="100" step="10" style="flex: 1; margin-right: 12px;">
              <span id="scalebar-width-value" style="font-weight: 600; color: #1976d2; min-width: 45px; text-align: right;">100px</span>
            </div>
          </label>

          <div class="menu-subsection-title" style="margin-top: 12px;">Branch Length Scale</div>
          <label class="menu-item compact" style="display: block; margin-bottom: 8px;">
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Tree Length Unit</div>
            <select id="treeLengthUnit" style="width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;">
              <option value="none">None</option>
              <option value="substitution">Substitution Rate</option>
              <option value="snp">SNP</option>
              <option value="alleles">Alleles (cgMLST/wgMLST)</option>
            </select>
          </label>
          <label class="menu-item compact" style="display: block;">
            <div style="display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: start;">
              <div style="display: flex; flex-direction: column; gap: 4px;">
                <input type="number" id="phylo-alignment-length-nodes"
                       placeholder="Alignment length (bp)"
                       min="1"
                       step="1"
                       style="width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;">
                <div style="font-size: 10px; color: #666; line-height: 1.2;">Alignment length to convert to SNP scale</div>
              </div>
              <label class="toggle-item compact" style="display: flex; align-items: center; gap: 6px; margin: 0; white-space: nowrap; padding-top: 6px;">
                <span style="font-size: 12px;">Display</span>
                <input type="checkbox" class="toggle-switch" id="branch-lengths-toggle">
              </label>
            </div>
          </label>

          <label class="menu-item compact" style="display: block; margin-top: 8px;">
            <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Decimal Digits</div>
            <select id="decimalDigits" style="width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;">
              <option value="0">0 (round to nearest integer)</option>
              <option value="1">1 digit</option>
            </select>
          </label>

          <div class="menu-subsection-title" style="margin-top: 12px;">Zoom Controls</div>
          <label class="menu-item slider-item compact">
            <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #666;">Horizontal</span>
                <span id="horizontal-zoom-value" style="font-weight: 600; color: #1976d2; font-size: 12px;">1.0x</span>
              </div>
              <input type="range" id="horizontal-zoom-slider" min="0.1" max="5" value="1" step="0.1" style="width: 100%;">
            </div>
          </label>
          <label class="menu-item slider-item compact">
            <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #666;">Vertical</span>
                <span id="vertical-zoom-value" style="font-weight: 600; color: #1976d2; font-size: 12px;">1.0x</span>
              </div>
              <input type="range" id="vertical-zoom-slider" min="0.1" max="5" value="1" step="0.1" style="width: 100%;">
            </div>
          </label>
        </div>
      </div>
    `;

    this.container.appendChild(menuContainer);
    this.layoutMenuContainer = menuContainer;

    const trigger = menuContainer.querySelector('.menu-trigger') as HTMLElement;
    const menu = menuContainer.querySelector('.menu-content') as HTMLElement;
    const layoutSelect = menuContainer.querySelector('#tree-layout-select') as HTMLSelectElement | null;
    const scalebarCheckbox = menuContainer.querySelector('#show-scalebar-checkbox') as HTMLInputElement | null;
    const scalebarPositionSelect = menuContainer.querySelector('#scalebar-position-select') as HTMLSelectElement | null;
    const scalebarWidthSlider = menuContainer.querySelector('#scalebar-width-slider') as HTMLInputElement | null;
    const scalebarWidthValue = menuContainer.querySelector('#scalebar-width-value') as HTMLElement | null;
    const horizontalZoomSlider = menuContainer.querySelector('#horizontal-zoom-slider') as HTMLInputElement | null;
    const horizontalZoomValue = menuContainer.querySelector('#horizontal-zoom-value') as HTMLElement | null;
    const verticalZoomSlider = menuContainer.querySelector('#vertical-zoom-slider') as HTMLInputElement | null;
    const verticalZoomValue = menuContainer.querySelector('#vertical-zoom-value') as HTMLElement | null;
    const treeLengthUnitSelect = menuContainer.querySelector('#treeLengthUnit') as HTMLSelectElement | null;
    const decimalDigitsSelect = menuContainer.querySelector('#decimalDigits') as HTMLSelectElement | null;
    const alignmentLengthInput = menuContainer.querySelector('#phylo-alignment-length-nodes') as HTMLInputElement | null;
    const branchLengthsToggle = menuContainer.querySelector('#branch-lengths-toggle') as HTMLInputElement | null;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.classList.toggle('open');
    });

    document.addEventListener('click', (e) => {
      if (!menuContainer.contains(e.target as Node)) {
        menu.classList.remove('open');
      }
    });

    if (layoutSelect) {
      layoutSelect.value = this.treeLayout;
    }

    if (layoutSelect) {
      layoutSelect.addEventListener('change', (e) => {
        this.changeTreeLayout((e.target as HTMLSelectElement).value);
      });
    }

    if (scalebarCheckbox) {
      scalebarCheckbox.addEventListener('change', (e) => {
        this.toggleScalebar((e.target as HTMLInputElement).checked);
      });
    }

    if (scalebarPositionSelect) {
      scalebarPositionSelect.addEventListener('change', (e) => {
        this.updateScalebarPosition((e.target as HTMLSelectElement).value);
      });
    }

    if (scalebarWidthSlider && scalebarWidthValue) {
      scalebarWidthSlider.addEventListener('input', (e) => {
        const width = parseInt((e.target as HTMLInputElement).value);
        scalebarWidthValue.textContent = `${width}px`;
        this.updateScalebarWidth(width);
      });
    }

    if (horizontalZoomSlider && horizontalZoomValue) {
      horizontalZoomSlider.addEventListener('input', (e) => {
        const zoom = parseFloat((e.target as HTMLInputElement).value);
        horizontalZoomValue.textContent = `${zoom.toFixed(1)}x`;
        this.setHorizontalZoom(zoom);
      });
    }

    if (verticalZoomSlider && verticalZoomValue) {
      verticalZoomSlider.addEventListener('input', (e) => {
        const zoom = parseFloat((e.target as HTMLInputElement).value);
        verticalZoomValue.textContent = `${zoom.toFixed(1)}x`;
        this.setVerticalZoom(zoom);
      });
    }

    if (treeLengthUnitSelect) {
      treeLengthUnitSelect.value = this.treeLengthUnit || 'none';

      treeLengthUnitSelect.addEventListener('change', (e) => {
        this.setTreeLengthUnit((e.target as HTMLSelectElement).value);

        if (alignmentLengthInput) {
          if ((e.target as HTMLSelectElement).value === 'snp') {
            alignmentLengthInput.disabled = true;
            alignmentLengthInput.style.opacity = '0.5';
            alignmentLengthInput.style.cursor = 'not-allowed';
          } else {
            alignmentLengthInput.disabled = false;
            alignmentLengthInput.style.opacity = '1';
            alignmentLengthInput.style.cursor = 'text';
          }
        }
      });
    }

    if (decimalDigitsSelect) {
      decimalDigitsSelect.value = String(this.decimalDigits || 1);

      const shouldDisable = this.shouldDisableDecimalDigits();
      if (shouldDisable) {
        decimalDigitsSelect.disabled = true;
        decimalDigitsSelect.style.opacity = '0.5';
        decimalDigitsSelect.style.cursor = 'not-allowed';
        decimalDigitsSelect.title = 'Rounding disabled: branch values are too small (max < 0.1)';
      }

      decimalDigitsSelect.addEventListener('change', (e) => {
        this.setDecimalDigits(parseInt((e.target as HTMLSelectElement).value));
      });
    }

    if (alignmentLengthInput) {
      alignmentLengthInput.value = String(this.alignmentLength || '');

      alignmentLengthInput.addEventListener('change', (e) => {
        const length = parseInt((e.target as HTMLInputElement).value);
        this.setAlignmentLength(length > 0 ? length : null);
        if (treeLengthUnitSelect) {
          if (length > 0) {
            treeLengthUnitSelect.value = 'snp';
            this.setTreeLengthUnit('snp');
          } else {
            treeLengthUnitSelect.value = 'none';
            this.setTreeLengthUnit('none');
          }
        }

        if (decimalDigitsSelect) {
          const shouldDisable = this.shouldDisableDecimalDigits();
          decimalDigitsSelect.disabled = shouldDisable;
          decimalDigitsSelect.style.opacity = shouldDisable ? '0.5' : '1';
          decimalDigitsSelect.style.cursor = shouldDisable ? 'not-allowed' : 'pointer';
          decimalDigitsSelect.title = shouldDisable
            ? 'Rounding disabled: branch values are too small (max < 0.1)'
            : '';
        }
      });
    }

    if (branchLengthsToggle) {
      branchLengthsToggle.addEventListener('change', (e) => {
        this.toggleBranchLengths((e.target as HTMLInputElement).checked);
      });
    }
  }


  /**
   * Create metadata blocks menu
   */
  createMetadataBlocksMenu(metadataFields: string[] = []): void {
    const menuContainer = document.createElement('div');
    menuContainer.className = 'phylocanvas-control-menu metadata-blocks-menu';

    if (!this.fieldPalettes) {
      this.fieldPalettes = new Map();
    }

    const qualitativePalettes = Object.keys(ColorBrewerPalettes.qualitative || {});

    const metadataBlocksHTML = metadataFields.map(field => {
      const currentPalette = this.fieldPalettes.get(field) || 'Set1';
      const paletteOptions = qualitativePalettes.map(palette =>
        `<option value="${palette}" ${palette === currentPalette ? 'selected' : ''}>${palette}</option>`
      ).join('');

      const palettes = ColorBrewerPalettes.qualitative || {};
      const currentPaletteObj = palettes[currentPalette];
      const availableSizes = currentPaletteObj ? Object.keys(currentPaletteObj).map(Number).sort((a: number, b: number) => a - b) : [];
      const preferredSize = availableSizes.find((s: number) => s >= 8) ?? availableSizes[availableSizes.length - 1]!;
      const paletteColors = currentPaletteObj?.[preferredSize]?.slice(0, 8) || [];
      const palettePreview = paletteColors.map((color: string) => `<div class="palette-color" style="background-color: ${color}"></div>`).join('');

      return `
        <div class="menu-item field-color-item" data-field="${field}" data-field-lower="${field.toLowerCase()}">
          <div class="field-checkbox-row">
            <label>
              <input type="checkbox" class="metadata-block-checkbox" value="${field}">
              <span class="field-name">${field}</span>
            </label>
          </div>
          <div class="field-palette-controls" style="display: none;">
            <select class="field-palette-select" data-field="${field}">
              ${paletteOptions}
            </select>
            <div class="palette-preview field-palette-preview" data-field="${field}">
              ${palettePreview}
            </div>
          </div>
        </div>
      `;
    }).join('');

    menuContainer.innerHTML = `
      <button class="menu-trigger" id="metadata-blocks-btn">
        <span>📊</span> Metadata Blocks
      </button>
      <div class="menu-content" id="metadata-blocks-menu">
        ${metadataFields.length > 0 ? `
        <div class="menu-header">Select Fields & Colors</div>
        <div style="padding: 8px 12px;">
          <input type="text"
                 id="metadata-fields-search"
                 class="metadata-fields-search"
                 placeholder="Search fields..."
                 autocomplete="off">
          <div style="margin-top: 6px; font-size: 11px;">
            <a href="#" id="select-all-fields" style="color: #1976d2; text-decoration: none; margin-right: 8px;">Select all</a>
            <span style="color: #999;">|</span>
            <a href="#" id="deselect-all-fields" style="color: #1976d2; text-decoration: none; margin-left: 8px;">Deselect all</a>
          </div>
        </div>
        <div class="metadata-blocks-section">
          ${metadataBlocksHTML}
        </div>

        <hr class="menu-divider">

        <div class="menu-header">Legend</div>
        <div class="menu-section">
          <label class="menu-item">
            <input type="checkbox" id="show-legend-checkbox" checked>
            <span>Show Legend</span>
          </label>
        </div>
        <div class="menu-section">
          <label class="menu-item slider-item">
            <span>Columns</span>
            <input type="range" id="legend-columns-slider" min="1" max="4" value="2" step="1">
            <span id="legend-columns-value">2</span>
          </label>
        </div>
        <div class="menu-section">
          <label class="menu-item">
            <span>Position</span>
            <select id="legend-position-select" style="padding: 4px; border-radius: 4px; border: 1px solid #ddd; width: 100%; margin-top: 4px;">
              <option value="top-left" selected>Top Left</option>
              <option value="top-right">Top Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-right">Bottom Right</option>
            </select>
          </label>
        </div>

        <hr class="menu-divider">

        <div class="menu-section">
          <label class="menu-item slider-item">
            <span>Block Size</span>
            <input type="range" id="block-size-slider" min="8" max="32" value="16" step="2">
            <span id="block-size-value">16px</span>
          </label>
        </div>
        <div class="menu-section">
          <div class="toggle-item">
            <span>Hide empty values</span>
            <input type="checkbox" class="toggle-switch" id="hide-null-blocks-toggle" checked>
          </div>
        </div>
        ` : `
        <div class="menu-section">
          <div class="menu-item" style="color: #999;">No metadata available</div>
        </div>
        `}
      </div>
    `;

    this.container.appendChild(menuContainer);
    this.menuContainer = menuContainer;

    const trigger = menuContainer.querySelector('.menu-trigger') as HTMLElement;
    const menu = menuContainer.querySelector('.menu-content') as HTMLElement;
    const searchInput = menuContainer.querySelector('#metadata-fields-search') as HTMLInputElement | null;
    const fieldItems = menuContainer.querySelectorAll('.field-color-item');
    const metadataCheckboxes = menuContainer.querySelectorAll('.metadata-block-checkbox');
    const fieldPaletteSelects = menuContainer.querySelectorAll('.field-palette-select');
    const blockSizeSlider = menuContainer.querySelector('#block-size-slider') as HTMLInputElement | null;
    const blockSizeValue = menuContainer.querySelector('#block-size-value') as HTMLElement | null;
    const hideNullBlocksToggle = menuContainer.querySelector('#hide-null-blocks-toggle') as HTMLInputElement | null;
    const legendCheckbox = menuContainer.querySelector('#show-legend-checkbox') as HTMLInputElement | null;
    const legendPositionSelect = menuContainer.querySelector('#legend-position-select') as HTMLSelectElement | null;
    const legendColumnsSlider = menuContainer.querySelector('#legend-columns-slider') as HTMLInputElement | null;
    const legendColumnsValue = menuContainer.querySelector('#legend-columns-value') as HTMLElement | null;

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        const query = (e.target as HTMLInputElement).value.toLowerCase();
        fieldItems.forEach(item => {
          const el = item as HTMLElement;
          const fieldName = el.dataset['fieldLower'] || '';
          if (fieldName.includes(query)) {
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });
      });
    }

    const selectAllLink = menuContainer.querySelector('#select-all-fields') as HTMLElement | null;
    const deselectAllLink = menuContainer.querySelector('#deselect-all-fields') as HTMLElement | null;

    if (selectAllLink) {
      selectAllLink.addEventListener('click', (e) => {
        e.preventDefault();
        metadataCheckboxes.forEach(checkbox => {
          const cb = checkbox as HTMLInputElement;
          if (cb.closest('.field-color-item') && (cb.closest('.field-color-item') as HTMLElement).style.display !== 'none') {
            cb.checked = true;
            cb.dispatchEvent(new Event('change'));
          }
        });
        this.updateMetadataBlocksFromMenu();
      });
    }

    if (deselectAllLink) {
      deselectAllLink.addEventListener('click', (e) => {
        e.preventDefault();
        metadataCheckboxes.forEach(checkbox => {
          const cb = checkbox as HTMLInputElement;
          if (cb.closest('.field-color-item') && (cb.closest('.field-color-item') as HTMLElement).style.display !== 'none') {
            cb.checked = false;
            cb.dispatchEvent(new Event('change'));
          }
        });
        this.updateMetadataBlocksFromMenu();
      });
    }

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpening = !menu.classList.contains('open');
      menu.classList.toggle('open');

      if (isOpening) {
        requestAnimationFrame(() => {
          const triggerRect = trigger.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - triggerRect.bottom - 20;
          const spaceAbove = triggerRect.top - 20;

          menu.style.maxHeight = '';
          const menuRect = menu.getBoundingClientRect();
          const naturalHeight = menuRect.height;

          if (spaceBelow >= naturalHeight) {
            menu.style.top = '100%';
            menu.style.bottom = 'auto';
            menu.style.marginTop = '8px';
            menu.style.marginBottom = '0';
            menu.style.maxHeight = `${spaceBelow}px`;
          } else if (spaceAbove >= naturalHeight) {
            menu.style.top = 'auto';
            menu.style.bottom = '100%';
            menu.style.marginTop = '0';
            menu.style.marginBottom = '8px';
            menu.style.maxHeight = `${spaceAbove}px`;
          } else if (spaceBelow > spaceAbove) {
            menu.style.top = '100%';
            menu.style.bottom = 'auto';
            menu.style.marginTop = '8px';
            menu.style.marginBottom = '0';
            menu.style.maxHeight = `${spaceBelow}px`;
          } else {
            menu.style.top = 'auto';
            menu.style.bottom = '100%';
            menu.style.marginTop = '0';
            menu.style.marginBottom = '8px';
            menu.style.maxHeight = `${spaceAbove}px`;
          }
        });
      }
    });

    document.addEventListener('click', (e) => {
      if (!menuContainer.contains(e.target as Node)) {
        menu.classList.remove('open');
      }
    });

    metadataCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const field = (e.target as HTMLInputElement).value;
        const isChecked = (e.target as HTMLInputElement).checked;

        console.log(`[PhylocanvasRenderer] Field "${field}" checkbox changed to ${isChecked}`);

        const fieldItem = menuContainer.querySelector(`.field-color-item[data-field="${field}"]`);
        const paletteControls = fieldItem?.querySelector('.field-palette-controls') as HTMLElement | null;
        if (paletteControls) {
          paletteControls.style.display = isChecked ? 'flex' : 'none';
        }

        if (isChecked) {
          const paletteName = this.fieldPalettes.get(field) || 'Set1';
          console.log(`[PhylocanvasRenderer] Setting palette "${paletteName}" for field "${field}"`);
          TreeMetadataSync.currentPaletteCategory = 'qualitative';
          TreeMetadataSync.currentPaletteName = paletteName;
        }

        this.updateMetadataBlocksFromMenu();
        void this.persistMetadataBlockSelection();
      });
    });

    fieldPaletteSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        const sel = e.target as HTMLSelectElement;
        const field = sel.dataset['field']!;
        const palette = sel.value;
        this.fieldPalettes.set(field, palette);
        console.log(`[PhylocanvasRenderer] Field "${field}" palette changed to "${palette}"`);

        TreeMetadataSync.clearCustomColorsForField(field);
        void this.persistCustomColors({ customColors: TreeMetadataSync.getCustomColorsForSave() });
        void this.persistMetadataBlockSelection();

        const previewDiv = menuContainer.querySelector(`.field-palette-preview[data-field="${field}"]`) as HTMLElement | null;
        if (previewDiv) {
          const palettes = ColorBrewerPalettes.qualitative || {};
          const paletteObj = palettes[palette];
          if (paletteObj) {
            const sizes = Object.keys(paletteObj).map(Number).sort((a: number, b: number) => a - b);
            const size = sizes.find((s: number) => s >= 8) ?? sizes[sizes.length - 1]!;
            const colors = paletteObj[size]?.slice(0, 8) || [];
            previewDiv.textContent = '';
            colors.forEach((color: string) => {
              const swatch = document.createElement('div');
              swatch.className = 'palette-color';
              swatch.style.backgroundColor = color;
              previewDiv.appendChild(swatch);
            });
          }
        }

        colorManager.setPalette(field, palette as any, 'metadata');

        this.updateMetadataColors();
      });
    });

    if (blockSizeSlider) {
      blockSizeSlider.addEventListener('input', (e) => {
        this.blockSize = parseInt((e.target as HTMLInputElement).value);
        if (blockSizeValue) blockSizeValue.textContent = `${this.blockSize}px`;
        this.updateBlockSize();
      });
    }

    if (hideNullBlocksToggle) {
      hideNullBlocksToggle.checked = this.hideNullBlocks;
      hideNullBlocksToggle.addEventListener('change', (e) => {
        this.hideNullBlocks = (e.target as HTMLInputElement).checked;
        TreeMetadataSync.state.hideNullBlocks = this.hideNullBlocks;
        this.updateMetadataBlocksFromMenu();
      });
    }

    if (legendCheckbox) {
      legendCheckbox.addEventListener('change', (e) => {
        this.toggleLegend((e.target as HTMLInputElement).checked);
      });
    }

    if (legendPositionSelect) {
      legendPositionSelect.addEventListener('change', (e) => {
        this.updateLegendPosition((e.target as HTMLSelectElement).value);
      });
    }

    if (legendColumnsSlider) {
      legendColumnsSlider.addEventListener('input', (e) => {
        const columns = (e.target as HTMLInputElement).value;
        this.updateLegendColumns(columns);
        if (legendColumnsValue) {
          legendColumnsValue.textContent = columns;
        }
      });
    }
  }

  /**
   * Update metadata block colors based on selected palette
   * Note: This only updates metadata blocks, NOT leaf/tip colors (they are independent)
   */
  updateMetadataColors(): void {
    if (typeof TreeMetadataSync === 'undefined') {
      console.error('[PhylocanvasRenderer] TreeMetadataSync not available');
      return;
    }

    const selectedFields = this.getSelectedMetadataFields();
    console.log('[PhylocanvasRenderer] Updating metadata block colors for fields:', selectedFields);

    if (selectedFields.length > 0 && TreeMetadataSync.state.metadata) {
      selectedFields.forEach((field: string) => {
        const fieldPalette = this.fieldPalettes.get(field) || 'Set1';
        console.log(`[PhylocanvasRenderer] Field "${field}" using palette "${fieldPalette}"`);

        TreeMetadataSync.currentPaletteCategory = 'qualitative';
        TreeMetadataSync.currentPaletteName = fieldPalette;

        const columnTypes = TreeMetadataSync.state.columnTypes || {};
        TreeMetadataSync.generateColorMap(TreeMetadataSync.state.metadata as any, field, columnTypes as any);
      });

      const blocks = TreeMetadataSync.computeMetadataBlocks(selectedFields);
      console.log('[PhylocanvasRenderer] Recomputed metadata blocks:', blocks);
      this.setMetadataBlocks(selectedFields, blocks);
      this.updateLegend(selectedFields, blocks);
    }
  }

  /**
   * Get currently selected metadata fields
   */
  getSelectedMetadataFields(): string[] {
    if (!this.menuContainer) return [];

    const checkboxes = this.menuContainer.querySelectorAll('.metadata-block-checkbox:checked');
    return Array.from(checkboxes).map(cb => (cb as HTMLInputElement).value);
  }

  /**
   * Update menu with metadata fields
   */
  updateMenuWithMetadata(metadataFields: string[]): void {
    if (this.menuContainer) {
      this.menuContainer.remove();
    }

    this.createMetadataBlocksMenu(metadataFields);
    this.updateTooltipMenu(metadataFields);
    this.updateColorByDropdown(metadataFields);
    this.updateTipLabelDropdown(metadataFields);

    if (this.savedSelectedMetadataFields || this.colorByField || this.displayAsField) {
      console.log('[PhylocanvasRenderer] Dropdowns populated, restoring saved selections...');
      setTimeout(() => {
        this.updateUIControls();
      }, 50);
    }
  }

  /**
   * Update color by dropdown with metadata fields
   */
  updateColorByDropdown(metadataFields: string[]): void {
    if (!this.nodesLabelsMenuContainer) return;

    const colorBySelect = this.nodesLabelsMenuContainer.querySelector('#colorByField') as HTMLSelectElement | null;
    if (!colorBySelect) return;

    colorBySelect.innerHTML = '';
    metadataFields.forEach(field => {
      const option = document.createElement('option');
      option.value = field;
      option.textContent = field;
      colorBySelect.appendChild(option);
    });

    console.log('[PhylocanvasRenderer] Color by dropdown updated with', metadataFields.length, 'fields');
  }

  /**
   * Update leaf text dropdown with metadata fields
   */
  updateTipLabelDropdown(metadataFields: string[]): void {
    if (!this.nodesLabelsMenuContainer) return;

    const displayAsSelect = this.nodesLabelsMenuContainer.querySelector('#displayAsField') as HTMLSelectElement | null;
    if (!displayAsSelect) return;

    displayAsSelect.innerHTML = '<option value="">Tree file value</option>';
    metadataFields.forEach(field => {
      const option = document.createElement('option');
      option.value = field;
      option.textContent = field;
      displayAsSelect.appendChild(option);
    });

    console.log('[PhylocanvasRenderer] Leaf text dropdown updated with', metadataFields.length, 'fields');
  }

  /**
   * Create tooltip control menu
   */
  createTooltipMenu(): void {
    const menuContainer = document.createElement('div');
    menuContainer.className = 'phylocanvas-control-menu tooltip-menu';

    menuContainer.innerHTML = `
      <button class="menu-trigger" id="tooltip-menu-btn">
        <span>💬</span> Tooltip
      </button>
      <div class="menu-content compact-menu" id="tooltip-menu">
        <div class="menu-section compact">
          <label class="menu-item toggle-item compact">
            <span>Enable Tooltip</span>
            <input type="checkbox" class="toggle-switch" id="enable-tooltip-toggle" checked>
          </label>

          <div class="menu-subsection-title" style="margin-top: 12px;">Display Fields</div>
          <div class="tooltip-search-container" style="padding: 0 12px 6px;">
            <input type="text" id="tooltip-search" class="tooltip-search-input" placeholder="Search fields...">
          </div>
        </div>
        <div class="menu-section compact tooltip-fields-section" id="tooltip-fields-section">
          <div class="menu-item compact" style="color: #999;">No metadata available</div>
        </div>
      </div>
    `;

    this.container.appendChild(menuContainer);
    this.tooltipMenuContainer = menuContainer;

    const trigger = menuContainer.querySelector('.menu-trigger') as HTMLElement;
    const menu = menuContainer.querySelector('.menu-content') as HTMLElement;
    const enableToggle = menuContainer.querySelector('#enable-tooltip-toggle') as HTMLInputElement | null;
    const searchInput = menuContainer.querySelector('#tooltip-search') as HTMLInputElement | null;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.classList.toggle('open');
    });

    document.addEventListener('click', (e) => {
      if (!menuContainer.contains(e.target as Node)) {
        menu.classList.remove('open');
      }
    });

    if (enableToggle) {
      enableToggle.addEventListener('change', (e) => {
        this.tooltipEnabled = (e.target as HTMLInputElement).checked;
        console.log('[PhylocanvasRenderer] Tooltip enabled:', this.tooltipEnabled);
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.filterTooltipFields((e.target as HTMLInputElement).value);
      });
    }
  }

  /**
   * True when the hosting app can switch the Tableau highlight sync mode
   */
  supportsHighlightSync(): boolean {
    return typeof this.app?.setHighlightSyncMode === 'function';
  }

  /**
   * Create the Sync menu holding the Tableau highlight sync mode
   */
  createSyncMenu(): void {
    const menuContainer = document.createElement('div');
    menuContainer.className = 'phylocanvas-control-menu sync-menu';

    menuContainer.innerHTML = `
      <button class="menu-trigger" id="sync-menu-btn">
        <span>🔄</span> Sync
      </button>
      <div class="menu-content compact-menu" id="sync-menu">
        <div class="menu-section compact">
          <div class="menu-subsection-title">Highlight Sync</div>
          <label class="menu-item compact">
            <div style="display: grid; grid-template-columns: auto 1fr; gap: 8px; align-items: center;">
              <span style="font-size: 12px; white-space: nowrap;">Mode:</span>
              <select id="highlight-sync-mode-select" style="padding: 6px 10px; border-radius: 4px; border: 1px solid #ddd; font-size: 13px;">
                <option value="manual" selected>Manual (click tree)</option>
                <option value="automatic">Automatic</option>
              </select>
            </div>
          </label>
          <div class="menu-item compact" style="color: #666; font-size: 12px; line-height: 1.4; white-space: normal;">
            Manual: click the left half of the tree to pull the current Tableau highlight onto the tips.
            Automatic: poll Tableau in the background; each poll briefly shows Tableau's loading cursor.
          </div>
        </div>
      </div>
    `;

    this.container.appendChild(menuContainer);
    this.syncMenuContainer = menuContainer;

    const trigger = menuContainer.querySelector('.menu-trigger') as HTMLElement;
    const menu = menuContainer.querySelector('.menu-content') as HTMLElement;
    const select = menuContainer.querySelector('#highlight-sync-mode-select') as HTMLSelectElement | null;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.classList.toggle('open');
    });

    document.addEventListener('click', (e) => {
      if (!menuContainer.contains(e.target as Node)) {
        menu.classList.remove('open');
      }
    });

    if (select) {
      select.value = this.highlightSyncMode;
      select.addEventListener('change', (e) => {
        this.highlightSyncMode = (e.target as HTMLSelectElement).value;
        if (this.supportsHighlightSync()) {
          this.app.setHighlightSyncMode(this.highlightSyncMode);
        }
      });
    }
  }

  /**
   * Create save settings button
   */
  createSaveSettingsButton(): void {
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'phylocanvas-save-settings-btn';

    buttonContainer.innerHTML = `
      <button id="save-settings-btn" class="save-settings-button" title="Save Settings">
        <span>💾</span>
      </button>
    `;

    this.container.appendChild(buttonContainer);
    this.saveSettingsButtonContainer = buttonContainer;

    const saveBtn = buttonContainer.querySelector('#save-settings-btn') as HTMLElement | null;
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        if (this.app && this.app.saveAllCurrentSettings) {
          await this.app.saveAllCurrentSettings();
        }
      });
    }
  }

  /**
   * Update tooltip menu with metadata fields
   */
  updateTooltipMenu(metadataFields: string[]): void {
    if (!this.tooltipMenuContainer) return;

    const fieldsSection = this.tooltipMenuContainer.querySelector('#tooltip-fields-section') as HTMLElement | null;
    if (!fieldsSection) return;

    this.allTooltipFields = metadataFields || [];

    fieldsSection.innerHTML = '';

    if (!metadataFields || metadataFields.length === 0) {
      fieldsSection.innerHTML = '<div class="menu-item" style="color: #999;">No metadata available</div>';
      return;
    }

    if (this.tooltipFields.length === 0) {
      this.tooltipFields = [...metadataFields];
    }

    this.renderTooltipFields(metadataFields);
  }

  /**
   * Render tooltip fields (used by updateTooltipMenu and filterTooltipFields)
   */
  renderTooltipFields(fields: string[]): void {
    if (!this.tooltipMenuContainer) return;

    const fieldsSection = this.tooltipMenuContainer.querySelector('#tooltip-fields-section') as HTMLElement | null;
    if (!fieldsSection) return;

    fieldsSection.innerHTML = '';

    if (!fields || fields.length === 0) {
      fieldsSection.innerHTML = '<div class="menu-item compact" style="color: #999;">No fields match search</div>';
      return;
    }

    fields.forEach(field => {
      const menuItem = document.createElement('label');
      menuItem.className = 'menu-item tooltip-field-item compact';
      menuItem.setAttribute('data-field', field);

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'tooltip-field-checkbox';
      checkbox.value = field;
      checkbox.checked = this.tooltipFields.includes(field);

      const span = document.createElement('span');
      span.textContent = field;

      checkbox.addEventListener('change', () => {
        this.updateTooltipFields();
      });

      menuItem.appendChild(checkbox);
      menuItem.appendChild(span);
      fieldsSection.appendChild(menuItem);
    });
  }

  /**
   * Filter tooltip fields based on search query
   */
  filterTooltipFields(query: string): void {
    if (!this.allTooltipFields || this.allTooltipFields.length === 0) return;

    const lowerQuery = query.toLowerCase().trim();

    if (lowerQuery === '') {
      this.renderTooltipFields(this.allTooltipFields);
    } else {
      const filtered = this.allTooltipFields.filter(field =>
        field.toLowerCase().includes(lowerQuery)
      );
      this.renderTooltipFields(filtered);
    }
  }

  /**
   * Update tooltip fields from checkboxes
   */
  updateTooltipFields(): void {
    if (!this.tooltipMenuContainer) return;

    const checkboxes = this.tooltipMenuContainer.querySelectorAll('.tooltip-field-checkbox:checked');
    this.tooltipFields = Array.from(checkboxes).map(cb => (cb as HTMLInputElement).value);

    console.log('[PhylocanvasRenderer] Tooltip fields updated:', this.tooltipFields);
  }

  /**
   * Create context menu element
   */
  createContextMenu(): void {
    if (this.contextMenu) {
      this.contextMenu.remove();
    }

    this.contextMenu = document.createElement('div');
    this.contextMenu.className = 'tree-context-menu';

    this.contextMenu.innerHTML = `
      <div class="context-menu-item" data-action="zoom-fit">
        <span class="context-menu-icon">🔍</span>
        <span class="context-menu-label">Zoom to Fit</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="enable-lasso" id="lasso-menu-item">
        <span class="context-menu-icon">⭕</span>
        <span class="context-menu-label">Enable Lasso Selection</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="redraw-original">
        <span class="context-menu-icon">🔄</span>
        <span class="context-menu-label">Redraw Original Tree</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="hide-labels">
        <span class="context-menu-icon">👁</span>
        <span class="context-menu-label">Hide Leaf Labels</span>
      </div>
      <div class="context-menu-item" data-action="unalign-labels">
        <span class="context-menu-icon">⬅</span>
        <span class="context-menu-label">Unalign Leaf Labels</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item leaf-only-item" data-action="reroot-at-leaf" style="display: none;">
        <span class="context-menu-icon">🌳</span>
        <span class="context-menu-label">Set as Root (Re-root)</span>
      </div>
      <div class="context-menu-separator leaf-only-separator" style="display: none;"></div>
      <div class="context-menu-item" data-action="midpoint-root">
        <span class="context-menu-icon">⚖</span>
        <span class="context-menu-label">Midpoint Root</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="increasing-order">
        <span class="context-menu-icon">⬆</span>
        <span class="context-menu-label">Increasing Node Order</span>
      </div>
      <div class="context-menu-item" data-action="decreasing-order">
        <span class="context-menu-icon">⬇</span>
        <span class="context-menu-label">Decreasing Node Order</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="export-labels">
        <span class="context-menu-icon">📋</span>
        <span class="context-menu-label">Export Leaf Labels</span>
      </div>
      <div class="context-menu-item" data-action="export-newick">
        <span class="context-menu-icon">📄</span>
        <span class="context-menu-label">Export as Newick File</span>
      </div>
      <div class="context-menu-item" data-action="export-png">
        <span class="context-menu-icon">🖼</span>
        <span class="context-menu-label">Export as PNG Image</span>
      </div>
    `;

    document.body.appendChild(this.contextMenu);

    this.contextMenu.querySelectorAll('.context-menu-item').forEach(item => {
      item.addEventListener('click', () => {
        const action = (item as HTMLElement).dataset['action']!;
        this.handleContextMenuAction(action);
        this.hideContextMenu();
      });
    });

    document.addEventListener('click', (e) => {
      if (this.contextMenu && !this.contextMenu.contains(e.target as Node)) {
        this.hideContextMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideContextMenu();
      }
    });

    console.log('[PhylocanvasRenderer] Context menu created');
  }

  /**
   * Create internal node context menu element
   */
  createInternalNodeContextMenu(): void {
    if (this.internalNodeContextMenu) {
      this.internalNodeContextMenu.remove();
    }

    this.internalNodeContextMenu = document.createElement('div');
    this.internalNodeContextMenu.className = 'tree-context-menu';

    this.internalNodeContextMenu.innerHTML = `
      <div class="context-menu-item" data-action="enable-lasso" id="lasso-menu-item-internal">
        <span class="context-menu-icon">⭕</span>
        <span class="context-menu-label">Enable Lasso Selection</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="view-subtree">
        <span class="context-menu-icon">👁</span>
        <span class="context-menu-label">View Subtree</span>
      </div>
      <div class="context-menu-item" data-action="collapse-subtree">
        <span class="context-menu-icon">📁</span>
        <span class="context-menu-label">Collapse Subtree</span>
      </div>
      <div class="context-menu-item" data-action="rotate-subtree">
        <span class="context-menu-icon">🔄</span>
        <span class="context-menu-label">Rotate Subtree</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="reroot-node">
        <span class="context-menu-icon">🌳</span>
        <span class="context-menu-label">Set as Root (Re-root)</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="export-subtree-labels">
        <span class="context-menu-icon">📋</span>
        <span class="context-menu-label">Export Leaf Labels</span>
      </div>
      <div class="context-menu-item" data-action="export-subtree-newick">
        <span class="context-menu-icon">📄</span>
        <span class="context-menu-label">Export as Newick File</span>
      </div>
    `;

    document.body.appendChild(this.internalNodeContextMenu);

    this.internalNodeContextMenu.querySelectorAll('.context-menu-item').forEach(item => {
      item.addEventListener('click', () => {
        const action = (item as HTMLElement).dataset['action']!;
        this.handleInternalNodeContextMenuAction(action);
        this.hideContextMenu();
      });
    });

    console.log('[PhylocanvasRenderer] Internal node context menu created');
  }


  /**
   * Check if a node is an internal node (not a leaf)
   */
  isInternalNode(node: any): boolean {
    if (!node) return false;

    const hasChildrenArray = Array.isArray(node.children) && node.children.length > 0;
    const isNotLeaf = node.isLeaf === false;
    const noLeafProperty = node.isLeaf === undefined && hasChildrenArray;

    return isNotLeaf || hasChildrenArray || noLeafProperty;
  }

  /**
   * Show context menu at specific position
   */
  showContextMenu(x: number, y: number, node: any): void {
    console.log('[PhylocanvasRenderer] Showing context menu for node:', node);

    let menuToShow: HTMLElement | null = null;
    const isInternalNode = this.isInternalNode(node);

    console.log('[PhylocanvasRenderer] Node detection:', {
      hasNode: !!node,
      isLeaf: node?.isLeaf,
      hasChildren: node?.children?.length > 0,
      childrenCount: node?.children?.length,
      isInternalNode: isInternalNode
    });

    if (isInternalNode) {
      if (!this.internalNodeContextMenu) return;
      menuToShow = this.internalNodeContextMenu;
      this.updateInternalNodeContextMenuItems(node);
      console.log('[PhylocanvasRenderer] Showing INTERNAL NODE menu');
    } else {
      if (!this.contextMenu) return;
      menuToShow = this.contextMenu;
      this.updateContextMenuItems(node);
      console.log('[PhylocanvasRenderer] Showing GENERAL/LEAF menu');
    }

    if (this.contextMenu) {
      this.contextMenu.classList.remove('visible');
    }
    if (this.internalNodeContextMenu) {
      this.internalNodeContextMenu.classList.remove('visible');
    }

    this.contextMenuNode = node;

    menuToShow.style.left = `${x}px`;
    menuToShow.style.top = `${y}px`;
    menuToShow.classList.add('visible');

    setTimeout(() => {
      if (!menuToShow) return;
      const menuRect = menuToShow.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (menuRect.right > viewportWidth) {
        adjustedX = viewportWidth - menuRect.width - 10;
      }

      if (menuRect.bottom > viewportHeight) {
        adjustedY = viewportHeight - menuRect.height - 10;
      }

      if (adjustedX < 0) {
        adjustedX = 10;
      }

      if (adjustedY < 0) {
        adjustedY = 10;
      }

      menuToShow.style.left = `${adjustedX}px`;
      menuToShow.style.top = `${adjustedY}px`;
    }, 0);
  }

  /**
   * Hide context menu (hide both menus)
   */
  hideContextMenu(): void {
    if (this.contextMenu) {
      this.contextMenu.classList.remove('visible');
    }
    if (this.internalNodeContextMenu) {
      this.internalNodeContextMenu.classList.remove('visible');
    }
    this.contextMenuNode = null;
  }

  /**
   * Update context menu items based on current state
   */
  updateContextMenuItems(node: any = null): void {
    if (!this.contextMenu) return;

    const hideLabelsItem = this.contextMenu.querySelector('[data-action="hide-labels"]');
    if (hideLabelsItem) {
      const label = hideLabelsItem.querySelector('.context-menu-label');
      if (label) label.textContent = this.showLabels ? 'Hide Leaf Labels' : 'Show Leaf Labels';
    }

    const unalignLabelsItem = this.contextMenu.querySelector('[data-action="unalign-labels"]');
    if (unalignLabelsItem) {
      const label = unalignLabelsItem.querySelector('.context-menu-label');
      if (label) label.textContent = this.alignLabels ? 'Unalign Leaf Labels' : 'Align Leaf Labels';
      if (this.showLabels) {
        unalignLabelsItem.classList.remove('disabled');
      } else {
        unalignLabelsItem.classList.add('disabled');
      }
    }

    const rerootItem = this.contextMenu.querySelector('[data-action="reroot-at-leaf"]') as HTMLElement | null;
    const rerootSeparator = this.contextMenu.querySelector('.leaf-only-separator') as HTMLElement | null;

    if (rerootItem && rerootSeparator) {
      if (node && node.id) {
        rerootItem.style.display = 'flex';
        rerootSeparator.style.display = 'block';
        console.log('[PhylocanvasRenderer] Showing "Set as Root" option for leaf node:', node.id);
      } else {
        rerootItem.style.display = 'none';
        rerootSeparator.style.display = 'none';
      }
    }
  }

  /**
   * Update internal node context menu items based on node state
   */
  updateInternalNodeContextMenuItems(node: any): void {
    if (!this.internalNodeContextMenu || !node) return;

    const isCollapsed = this.isNodeCollapsed(node);

    const collapseItem = this.internalNodeContextMenu.querySelector('[data-action="collapse-subtree"]');
    if (collapseItem) {
      const label = collapseItem.querySelector('.context-menu-label');
      const icon = collapseItem.querySelector('.context-menu-icon');

      if (isCollapsed) {
        if (label) label.textContent = 'Expand Subtree';
        if (icon) icon.textContent = '📂';
      } else {
        if (label) label.textContent = 'Collapse Subtree';
        if (icon) icon.textContent = '📁';
      }
    }

    console.log('[PhylocanvasRenderer] Internal menu updated, node collapsed:', isCollapsed);
  }

  /**
   * Check if a node is currently collapsed
   */
  isNodeCollapsed(node: any): boolean {
    if (!node || !this.tree) return false;

    if (this.tree.props && Array.isArray(this.tree.props.collapsedIds)) {
      return this.tree.props.collapsedIds.includes(node.id);
    }

    if (node.collapsed !== undefined) {
      return node.collapsed === true;
    }

    return false;
  }

  /**
   * Handle context menu action
   */
  handleContextMenuAction(action: string): void {
    console.log('[PhylocanvasRenderer] Context menu action:', action, 'Node:', this.contextMenuNode);

    switch (action) {
      case 'zoom-fit':
        this.handleZoomToFit();
        break;
      case 'enable-lasso':
        this.handleEnableLasso();
        break;
      case 'redraw-original':
        this.handleRedrawOriginal();
        break;
      case 'hide-labels':
        this.handleToggleLabels();
        break;
      case 'unalign-labels':
        this.handleToggleLabelAlignment();
        break;
      case 'reroot-at-leaf':
        this.handleRerootAtLeaf();
        break;
      case 'midpoint-root':
        this.handleMidpointRoot();
        break;
      case 'increasing-order':
        this.handleIncreasingOrder();
        break;
      case 'decreasing-order':
        this.handleDecreasingOrder();
        break;
      case 'export-labels':
        this.handleExportLabels();
        break;
      case 'export-newick':
        this.handleExportNewick();
        break;
      case 'export-png':
        this.handleExportPNG();
        break;
      default:
        console.warn('[PhylocanvasRenderer] Unknown context menu action:', action);
    }
  }

  /**
   * Handle internal node context menu action
   */
  handleInternalNodeContextMenuAction(action: string): void {
    console.log('[PhylocanvasRenderer] Internal node context menu action:', action, 'Node:', this.contextMenuNode);

    switch (action) {
      case 'enable-lasso':
        this.handleEnableLasso();
        break;
      case 'view-subtree':
        this.handleViewSubtree();
        break;
      case 'collapse-subtree':
        this.handleCollapseSubtree();
        break;
      case 'rotate-subtree':
        this.handleRotateSubtree();
        break;
      case 'reroot-node':
        this.handleRerootNode();
        break;
      case 'export-subtree-labels':
        this.handleExportSubtreeLabels();
        break;
      case 'export-subtree-newick':
        this.handleExportSubtreeNewick();
        break;
      default:
        console.warn('[PhylocanvasRenderer] Unknown internal node context menu action:', action);
    }
  }

  /**
   * Context menu action handlers
   */
  handleZoomToFit(): void {
    console.log('[PhylocanvasRenderer] Zoom to fit');
    if (this.tree && this.tree.fitInCanvas) {
      this.tree.fitInCanvas();

      setTimeout(() => {
        if (this.tree && this.tree.getView) {
          this.initialViewState = this.tree.getView();
          console.log('[PhylocanvasRenderer] Updated initial view state after fit:', this.initialViewState);

          if (this.scalebarVisible) {
            this.createScaleBar();
          }
        }
      }, 200);
    }
  }

  /**
   * Handle enable lasso selection mode (from context menu)
   */
  handleEnableLasso(): void {
    if (!this.lassoEnabled) {
      this.enableLassoMode();
      console.log('[PhylocanvasRenderer] Lasso mode enabled via context menu');
    }
  }

  /**
   * Check if tree is at its original fitted state
   */
  isTreeAtFittedState(): boolean {
    if (!this.tree || !this.tree.getView || !this.initialViewState) {
      return false;
    }

    const currentView = this.tree.getView();

    const zoomMatch = Math.abs(currentView.zoom - this.initialViewState.zoom) < 0.01;

    const targetMatch = currentView.target && this.initialViewState.target &&
      Math.abs(currentView.target[0] - this.initialViewState.target[0]) < 1 &&
      Math.abs(currentView.target[1] - this.initialViewState.target[1]) < 1;

    return zoomMatch && targetMatch;
  }

  /**
   * Zoom to a specific tip (leaf node)
   */
  zoomToTip(leafId: string): void {
    console.log('[PhylocanvasRenderer] Zoom to tip:', leafId);

    if (!this.tree) {
      console.warn('[PhylocanvasRenderer] No tree available');
      return;
    }

    if (!this.isTreeAtFittedState()) {
      console.warn('[PhylocanvasRenderer] Zoom to tip only works when tree is fitted to extent');
      return;
    }

    let graph: any = null;
    let node: any = null;

    if (this.tree.getGraphAfterLayout) {
      graph = this.tree.getGraphAfterLayout();
      console.log('[PhylocanvasRenderer] Graph from getGraphAfterLayout:', graph);
    }

    if (!graph && this.tree.prerenderedTree) {
      graph = this.tree.prerenderedTree;
      console.log('[PhylocanvasRenderer] Using prerenderedTree:', graph);
    }

    if (!graph && this.tree.tree) {
      graph = this.tree.tree;
      console.log('[PhylocanvasRenderer] Using tree.tree:', graph);
    }

    if (!graph) {
      console.warn('[PhylocanvasRenderer] No graph data available');
      console.log('[PhylocanvasRenderer] Available tree properties:', Object.keys(this.tree));
      return;
    }

    if (graph.nodes && graph.nodes instanceof Map) {
      node = graph.nodes.get(leafId);
    } else if (graph.nodes && typeof graph.nodes === 'object') {
      node = graph.nodes[leafId];
    } else if (graph.leaves) {
      node = graph.leaves.find((leaf: any) => leaf.id === leafId);
    }

    if (!node) {
      console.warn('[PhylocanvasRenderer] Node not found:', leafId);
      console.log('[PhylocanvasRenderer] Graph structure:', {
        hasNodes: !!graph.nodes,
        hasLeaves: !!graph.leaves,
        nodesType: typeof graph.nodes
      });
      return;
    }

    console.log('[PhylocanvasRenderer] Found node:', node);

    const x = node.x;
    const y = node.y;

    if (x === undefined || y === undefined) {
      console.warn('[PhylocanvasRenderer] Node coordinates not available');
      console.log('[PhylocanvasRenderer] Node properties:', Object.keys(node));
      return;
    }

    console.log('[PhylocanvasRenderer] Node position:', { x, y });

    const bounds = graph.bounds || this.tree.getWorldBounds?.();
    if (!bounds) {
      console.warn('[PhylocanvasRenderer] Graph bounds not available');
      return;
    }

    const width = bounds.max[0] - bounds.min[0];
    const height = bounds.max[1] - bounds.min[1];

    let normalizedX = (x - bounds.min[0]) / width;
    let normalizedY = (y - bounds.min[1]) / height;

    normalizedX = Math.max(0, Math.min(1, normalizedX));
    normalizedY = Math.max(0, Math.min(1, normalizedY));

    console.log('[PhylocanvasRenderer] Zoom calculation:', {
      leafId,
      rawCoords: { x, y },
      bounds: {
        min: bounds.min,
        max: bounds.max,
        width,
        height
      },
      normalized: { x: normalizedX, y: normalizedY }
    });

    if (this.tree.deck && this.tree.deck.setProps) {
      const currentViewState = this.tree.deck.viewState || {};
      console.log('[PhylocanvasRenderer] Current deck.viewState:', currentViewState);
      console.log('[PhylocanvasRenderer] Zoom + Pan: Using deck.setProps with viewState.target (raw coords)');

      this.tree.deck.setProps({
        viewState: {
          ...currentViewState,
          target: [x, y, 0],
          zoom: 1.5,
          transitionDuration: 500,
          transitionInterpolator: this.tree.deck.FlyToInterpolator ? new this.tree.deck.FlyToInterpolator() : undefined
        }
      });

      console.log('[PhylocanvasRenderer] Set deck viewState target to:', [x, y, 0]);
    } else if (this.tree.setView) {
      console.log('[PhylocanvasRenderer] Fallback: Using setView with centre and zoom');

      this.tree.setView({
        centre: [normalizedX, normalizedY],
        zoom: 1.5
      });
      console.log('[PhylocanvasRenderer] Called setView with centre:', [normalizedX, normalizedY]);
    } else {
      console.warn('[PhylocanvasRenderer] No pan/zoom method available');
      this.showTemporaryMessage('Pan/zoom not available');
      return;
    }

    setTimeout(() => {
      const newView = this.tree.getView?.() || {};
      console.log('[PhylocanvasRenderer] View after setView:', newView);
      console.log('[PhylocanvasRenderer] Requested centre:', [normalizedX, normalizedY]);

      if (this.tree.props) {
        console.log('[PhylocanvasRenderer] Current props.centre:', this.tree.props.centre);
        console.log('[PhylocanvasRenderer] Current props.zoom:', this.tree.props.zoom);
      }
    }, 150);

    this.showTemporaryMessage(`Centered on ${leafId}`);
  }

  handleRedrawOriginal(): void {
    console.log('[PhylocanvasRenderer] Redraw original tree');
    if (this.tree) {
      if (this.tree.setRoot) {
        this.tree.setRoot(null);
        console.log('[PhylocanvasRenderer] Reset to original root');
      }

      if (this.tree.setProps && this.originalNewick) {
        this.tree.setProps({
          source: this.originalNewick
        });
        console.log('[PhylocanvasRenderer] Reloaded original tree source');
      }

      setTimeout(() => {
        if (this.tree && this.tree.fitInCanvas) {
          this.tree.fitInCanvas();

          setTimeout(() => {
            if (this.tree && this.tree.getView) {
              this.initialViewState = this.tree.getView();
              console.log('[PhylocanvasRenderer] Updated initial view state after restore:', this.initialViewState);

              if (this.scalebarVisible) {
                this.createScaleBar();
              }
            }
          }, 200);
        }
      }, 100);

      this.showTemporaryMessage('Original tree restored');

      this.emit('resetView', undefined);
    }
  }

  handleToggleLabels(): void {
    console.log('[PhylocanvasRenderer] Toggle labels');
    this.toggleLabels(!this.showLabels);

    const showLabelsCheckbox = document.getElementById('show-labels-checkbox') as HTMLInputElement | null;
    if (showLabelsCheckbox) {
      showLabelsCheckbox.checked = this.showLabels;
    }
  }

  handleToggleLabelAlignment(): void {
    if (!this.showLabels) return;

    console.log('[PhylocanvasRenderer] Toggle label alignment');
    this.toggleLabelAlignment(!this.alignLabels);

    const alignLabelsCheckbox = document.getElementById('align-labels-checkbox') as HTMLInputElement | null;
    if (alignLabelsCheckbox) {
      alignLabelsCheckbox.checked = this.alignLabels;
    }
  }

  handleMidpointRoot(): void {
    console.log('[PhylocanvasRenderer] Midpoint root');
    if (this.tree && this.tree.midpointRoot) {
      this.tree.midpointRoot();
    } else {
      console.warn('[PhylocanvasRenderer] midpointRoot method not available');
    }
  }

  handleRerootAtLeaf(): void {
    console.log('[PhylocanvasRenderer] Re-root at leaf node:', this.contextMenuNode);

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    if (this.tree && this.tree.rerootNode) {
      this.tree.rerootNode(this.contextMenuNode.id);
      console.log('[PhylocanvasRenderer] Tree re-rooted at leaf:', this.contextMenuNode.id);
    } else {
      console.warn('[PhylocanvasRenderer] rerootNode method not available');
    }
  }

  handleIncreasingOrder(): void {
    console.log('[PhylocanvasRenderer] Increasing node order');
    if (this.tree && this.tree.ascendingNodeOrder) {
      this.tree.ascendingNodeOrder();
    } else {
      console.warn('[PhylocanvasRenderer] ascendingNodeOrder method not available');
    }
  }

  handleDecreasingOrder(): void {
    console.log('[PhylocanvasRenderer] Decreasing node order');
    if (this.tree && this.tree.descendingNodeOrder) {
      this.tree.descendingNodeOrder();
    } else {
      console.warn('[PhylocanvasRenderer] descendingNodeOrder method not available');
    }
  }

  handleExportLabels(): void {
    console.log('[PhylocanvasRenderer] Export leaf labels');

    let leafLabels: string[] = [];

    if (this.tree && this.tree.prerenderedTree && this.tree.prerenderedTree.leaves) {
      leafLabels = this.tree.prerenderedTree.leaves.map((leaf: any) => leaf.id);
    } else if (this.tree && this.tree.tree && this.tree.tree.leaves) {
      leafLabels = this.tree.tree.leaves.map((leaf: any) => leaf.id);
    }

    if (leafLabels.length === 0) {
      console.warn('[PhylocanvasRenderer] No leaf labels found');
      alert('No leaf labels found to export');
      return;
    }

    const content = leafLabels.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'leaf-labels.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[PhylocanvasRenderer] Exported', leafLabels.length, 'leaf labels');
  }

  handleExportNewick(): void {
    console.log('[PhylocanvasRenderer] Export as Newick');

    if (!this.tree) {
      console.warn('[PhylocanvasRenderer] No tree to export');
      return;
    }

    let newickString = '';

    if (this.tree.exportNewick) {
      try {
        newickString = this.tree.exportNewick();
      } catch (error) {
        console.error('[PhylocanvasRenderer] Error exporting Newick:', error);
        newickString = this.originalNewick;
      }
    } else {
      newickString = this.originalNewick;
    }

    const blob = new Blob([newickString], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tree.nwk';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[PhylocanvasRenderer] Newick exported');
  }

  async handleExportPNG(): Promise<void> {
    console.log('[PhylocanvasRenderer] Export as PNG');

    if (!this.tree) {
      console.warn('[PhylocanvasRenderer] No tree to export');
      return;
    }

    try {
      let pngData: string;

      if (this.tree.exportPNG) {
        pngData = await this.tree.exportPNG();
      } else if (this.tree.deck && this.tree.deck.canvas) {
        const canvas = this.tree.deck.canvas as HTMLCanvasElement;
        pngData = canvas.toDataURL('image/png');
      } else {
        console.error('[PhylocanvasRenderer] No export method available');
        alert('PNG export not available for this renderer');
        return;
      }

      const link = document.createElement('a');
      link.href = pngData;
      link.download = 'tree.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('[PhylocanvasRenderer] PNG exported');
    } catch (error: any) {
      console.error('[PhylocanvasRenderer] Error exporting PNG:', error);
      alert('Failed to export PNG: ' + error.message);
    }
  }

  /**
   * Internal node action handlers
   */
  handleViewSubtree(): void {
    console.log('[PhylocanvasRenderer] View subtree for node:', this.contextMenuNode);

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    if (!this.tree) {
      console.warn('[PhylocanvasRenderer] No tree available');
      return;
    }

    if (this.tree.setRoot) {
      this.tree.setRoot(this.contextMenuNode.id);
      console.log('[PhylocanvasRenderer] Set subtree as root');

      setTimeout(() => {
        if (this.tree && this.tree.fitInCanvas) {
          this.tree.fitInCanvas();
          console.log('[PhylocanvasRenderer] Fitted subtree to canvas');

          setTimeout(() => {
            if (this.tree && this.tree.getView) {
              this.initialViewState = this.tree.getView();
              console.log('[PhylocanvasRenderer] Updated initial view state for subtree:', this.initialViewState);

              if (this.scalebarVisible) {
                this.createScaleBar();
              }
            }
          }, 200);
        }
      }, 100);

      const leafIds = this.getSubtreeLeaves(this.contextMenuNode);
      const message = `Viewing subtree with ${leafIds.length} leaves`;
      this.showTemporaryMessage(message);

      this.emit('subtreeView', { leafIds });
    } else {
      console.warn('[PhylocanvasRenderer] setRoot method not available');
      this.showTemporaryMessage('View Subtree not available');
    }
  }

  /**
   * Show toast notification
   */
  showTemporaryMessage(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'phylo-toast';
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('show');
    }, 10);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }

  handleCollapseSubtree(): void {
    console.log('[PhylocanvasRenderer] Toggle collapse for node:', this.contextMenuNode);

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    if (!this.tree) {
      console.warn('[PhylocanvasRenderer] No tree available');
      return;
    }

    const isCollapsed = this.isNodeCollapsed(this.contextMenuNode);

    if (isCollapsed) {
      console.log('[PhylocanvasRenderer] Expanding subtree');

      const collapsedIds = this.tree.props.collapsedIds || [];
      const newCollapsedIds = collapsedIds.filter((id: string) => id !== this.contextMenuNode.id);

      if (this.tree.setProps) {
        this.tree.setProps({ collapsedIds: newCollapsedIds });
        console.log('[PhylocanvasRenderer] Subtree expanded');
      }
    } else {
      console.log('[PhylocanvasRenderer] Collapsing subtree');

      if (this.tree.collapseNode) {
        this.tree.collapseNode(this.contextMenuNode.id);
        console.log('[PhylocanvasRenderer] Subtree collapsed');
      } else {
        console.warn('[PhylocanvasRenderer] collapseNode method not available');
      }
    }
  }

  handleRotateSubtree(): void {
    console.log('[PhylocanvasRenderer] Rotate subtree for node:', this.contextMenuNode);

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    if (this.tree && this.tree.rotateNode) {
      this.tree.rotateNode(this.contextMenuNode.id);
      console.log('[PhylocanvasRenderer] Subtree rotated');
    } else {
      console.warn('[PhylocanvasRenderer] rotateNode method not available');
    }
  }

  handleRerootNode(): void {
    console.log('[PhylocanvasRenderer] Re-root at node:', this.contextMenuNode);

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    if (this.tree && this.tree.rerootNode) {
      this.tree.rerootNode(this.contextMenuNode.id);
      console.log('[PhylocanvasRenderer] Tree re-rooted');
    } else {
      console.warn('[PhylocanvasRenderer] rerootNode method not available');
    }
  }

  handleExportSubtreeLabels(): void {
    console.log('[PhylocanvasRenderer] Export subtree leaf labels');

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    const leafLabels = this.getSubtreeLeaves(this.contextMenuNode);

    if (leafLabels.length === 0) {
      console.warn('[PhylocanvasRenderer] No leaf labels found in subtree');
      alert('No leaf labels found in subtree');
      return;
    }

    const content = leafLabels.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `subtree-labels-${this.contextMenuNode.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[PhylocanvasRenderer] Exported', leafLabels.length, 'subtree leaf labels');
  }

  handleExportSubtreeNewick(): void {
    console.log('[PhylocanvasRenderer] Export subtree as Newick');

    if (!this.contextMenuNode) {
      console.warn('[PhylocanvasRenderer] No node selected');
      return;
    }

    if (!this.tree) {
      console.warn('[PhylocanvasRenderer] No tree to export');
      return;
    }

    let newickString = '';

    if (this.tree.exportNewick) {
      try {
        newickString = this.tree.exportNewick(this.contextMenuNode.id);
      } catch (error: any) {
        console.error('[PhylocanvasRenderer] Error exporting subtree Newick:', error);
        alert('Failed to export subtree: ' + error.message);
        return;
      }
    } else {
      console.warn('[PhylocanvasRenderer] exportNewick method not available');
      alert('Subtree export not available');
      return;
    }

    const blob = new Blob([newickString], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `subtree-${this.contextMenuNode.id}.nwk`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[PhylocanvasRenderer] Subtree Newick exported');
  }

  /**
   * Get all leaf node IDs from a subtree
   */
  getSubtreeLeaves(node: any): string[] {
    if (!node) return [];

    const leaves: string[] = [];

    const collectLeaves = (n: any) => {
      if (n.isLeaf) {
        leaves.push(n.id);
      } else if (n.children) {
        n.children.forEach((child: any) => collectLeaves(child));
      }
    };

    collectLeaves(node);
    return leaves;
  }

  /**
   * Update metadata blocks from menu checkboxes
   */
  updateMetadataBlocksFromMenu(): void {
    if (!this.menuContainer) return;
    const checkboxes = this.menuContainer.querySelectorAll('.metadata-block-checkbox:checked');
    const selectedFields = Array.from(checkboxes).map(cb => (cb as HTMLInputElement).value);

    console.log('[PhylocanvasRenderer] Selected metadata fields:', selectedFields);

    this.emit('metadataBlocksChange', { fields: selectedFields });
  }

  /**
   * Tick the metadata-block checkboxes for the given fields (or, when no fields
   * are passed, the selection captured by loadSettings() into
   * savedSelectedMetadataFields) and push them to the tree. Shared by the Tableau
   * restore path (applyLoadedSettings) and the standalone renderer switch, so a
   * persisted block selection survives a switch into phylocanvas on either surface.
   */
  restoreSavedMetadataBlocks(fields?: string[]): void {
    const toRestore = fields ?? this.savedSelectedMetadataFields;
    if (toRestore === null || toRestore === undefined) return;

    // Idempotency guard: if the gear menu already shows exactly these blocks, the
    // re-tick / emit / refit below is wasted work and, run twice in one render, a
    // visible double draw. A renderer switch reaches a freshly built (all-unchecked)
    // menu so this never short-circuits a genuine restore; it only collapses the
    // second of the two calls the Tableau load path makes (applyLoadedSettings plus
    // the renderTree replay), keeping load a single apply.
    const currentlyChecked = Array.from(
      this.menuContainer?.querySelectorAll('.metadata-block-checkbox:checked') ?? []
    ).map((checkbox) => (checkbox as HTMLInputElement).value);
    if (sameMetadataBlockSelection(currentlyChecked, toRestore)) {
      this.savedSelectedMetadataFields = null;
      return;
    }

    console.log('[PhylocanvasRenderer] Restoring metadata fields:', toRestore);

    const allCheckboxes = this.menuContainer?.querySelectorAll('.metadata-block-checkbox');
    if (allCheckboxes) {
      allCheckboxes.forEach(checkbox => {
        (checkbox as HTMLInputElement).checked = false;
        const fieldItem = checkbox.closest('.field-color-item');
        if (fieldItem) {
          const controls = fieldItem.querySelector('.field-palette-controls') as HTMLElement | null;
          if (controls) controls.style.display = 'none';
        }
      });
    }

    toRestore.forEach((field: string) => {
      const checkbox = this.menuContainer?.querySelector(`.metadata-block-checkbox[value="${field}"]`) as HTMLInputElement | null;
      if (checkbox) {
        checkbox.checked = true;
        const fieldItem = checkbox.closest('.field-color-item');
        if (fieldItem) {
          const controls = fieldItem.querySelector('.field-palette-controls') as HTMLElement | null;
          if (controls) controls.style.display = 'block';
        }
      }
    });

    console.log('[PhylocanvasRenderer] Applying metadata block colors...');
    this.updateMetadataBlocksFromMenu();

    setTimeout(() => {
      if (this.tree?.fitInCanvas) {
        this.tree.fitInCanvas();
        this.initialViewState = this.tree?.getView?.();
        console.log('[PhylocanvasRenderer] Auto-fit canvas after initial metadata blocks load');

        if (this.scalebarVisible) {
          this.createScaleBar();
        }
      }
    }, 200);

    this.savedSelectedMetadataFields = null;
    console.log('[PhylocanvasRenderer] Metadata blocks restored and applied');
  }

  /**
   * Update block size
   */
  updateBlockSize(): void {
    console.log('[PhylocanvasRenderer] Updating block size to:', this.blockSize);
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({
        blockLength: this.blockSize,
        blockSize: this.blockSize,
        blockWidth: this.blockSize
      });
      console.log('[PhylocanvasRenderer] Block size props set');
    } else {
      console.warn('[PhylocanvasRenderer] Cannot update block size - tree not available');
    }
  }

  /**
   * Persist legend colour overrides through the app settings seam: localStorage
   * in the standalone, the Tableau Settings API in the extension.
   */
  async persistCustomColors(colors: { customColors?: Record<string, string>; leafCustomColors?: Record<string, string> }): Promise<void> {
    if (!this.app || !this.app.saveSettings) return;
    try {
      await this.app.saveSettings(colors);
    } catch (error) {
      console.error('[PhylocanvasRenderer] Failed to persist custom colors:', error);
    }
  }

  /**
   * Persist the metadata-block selection and per-field palettes into the
   * phylocanvas bag through the app settings seam, so a reload restores them.
   */
  async persistMetadataBlockSelection(): Promise<void> {
    if (!this.app || !this.app.saveSettings) return;
    try {
      await this.app.saveSettings({
        phylocanvas: {
          selectedMetadataFields: this.getSelectedMetadataFields(),
          fieldPalettes: Object.fromEntries(this.fieldPalettes || new Map()),
        },
      });
    } catch (error) {
      console.error('[PhylocanvasRenderer] Failed to persist metadata block selection:', error);
    }
  }

  /**
   * Save renderer settings to app
   */
  async saveSettings(): Promise<void> {
    if (!this.app || !this.app.saveSettings) return;

    await this.app.saveSettings({
      phylocanvas: {
        treeLayout: this.treeLayout,
        scalebarVisible: this.scalebarVisible,
        scalebarWidth: this.scalebarWidth,
        scalebarPosition: this.scalebarPosition,
        horizontalZoom: this.horizontalZoom,
        verticalZoom: this.verticalZoom,

        leafNodeSize: this.leafNodeSize,
        labelSize: this.labelSize,
        showLeafBorders: this.showLeafBorders,
        leafBorders: this.showLeafBorders,
        showLeafLabels: this.showLeafLabels,
        showLabels: this.showLabels,
        alignLabels: this.alignLabels,
        showInternalNodes: this.showInternalNodes,
        showInternalLabels: this.showInternalLabels,
        colourInternalEdges: this.colourInternalEdges,
        colourEdges: this.colourInternalEdges,
        solidEdges: this.solidEdges,
        alignmentLength: this.alignmentLength,
        showBranchLengths: this.showBranchLengths,

        displayAsField: this.app?.displayAsField || this.displayAsField,
        enableColorBy: this.enableColorBy,
        colorByEnabled: this.enableColorBy,
        colorByField: this.colorByField,
        colorByPalette: this.colorByPalette,

        selectedMetadataFields: this.getSelectedMetadataFields(),
        fieldPalettes: Object.fromEntries(this.fieldPalettes || new Map()),
        blockSize: this.blockSize,
        hideNullBlocks: this.hideNullBlocks,
        legendVisible: this.legendVisible,
        showLegend: this.legendVisible,
        legendCollapsed: this.legendCollapsed,
        legendColumns: this.legendColumns,
        legendPosition: this.legendPosition,

        tooltipEnabled: this.tooltipEnabled,
        tooltipFields: this.tooltipFields || [],
        highlightSyncMode: this.highlightSyncMode,

        zoomBehavior: this.zoomBehavior
      }
    });
  }

  /**
   * Save color-by state to Tableau settings
   */
  async saveColorByState(field: string | null, enabled: boolean): Promise<void> {
    if (typeof TableauSettingsManager === 'undefined') {
      console.warn('[PhylocanvasRenderer] TableauSettingsManager not available');
      return;
    }

    try {
      const currentSettings = TableauSettingsManager.loadPhylocanvasSettings() || {};
      currentSettings.enableColorBy = enabled;
      currentSettings.colorByEnabled = enabled;
      currentSettings.colorByField = field || '';

      const currentFields = this.getSelectedMetadataFields();
      if (currentFields.length > 0) {
        currentSettings.selectedMetadataFields = currentFields;
      }

      if (this.fieldPalettes && this.fieldPalettes.size > 0) {
        currentSettings.fieldPalettes = Object.fromEntries(this.fieldPalettes);
      }

      await TableauSettingsManager.savePhylocanvasSettings(currentSettings);
      console.log(`[PhylocanvasRenderer] Saved colorByField="${field || ''}", colorByEnabled=${enabled}`);
    } catch (error) {
      console.error('[PhylocanvasRenderer] Failed to save color-by state:', error);
    }
  }

  /**
   * Load renderer settings from saved data (used when called externally)
   */
  loadSettings(settings: any): void {
    if (!settings || !settings.phylocanvas) return;

    const saved = settings.phylocanvas;

    if (saved.treeLayout !== undefined) this.treeLayout = saved.treeLayout;
    else if (saved.type !== undefined) this.treeLayout = saved.type;
    if (saved.scalebarVisible !== undefined) this.scalebarVisible = saved.scalebarVisible;
    if (saved.scalebarWidth !== undefined) this.scalebarWidth = saved.scalebarWidth;
    if (saved.scalebarPosition !== undefined) this.scalebarPosition = saved.scalebarPosition;
    if (saved.horizontalZoom !== undefined) this.horizontalZoom = saved.horizontalZoom;
    if (saved.verticalZoom !== undefined) this.verticalZoom = saved.verticalZoom;

    if (saved.leafNodeSize !== undefined) this.leafNodeSize = saved.leafNodeSize;
    if (saved.labelSize !== undefined) this.labelSize = saved.labelSize;
    if (saved.showLeafBorders !== undefined) this.showLeafBorders = saved.showLeafBorders;
    else if (saved.leafBorders !== undefined) this.showLeafBorders = saved.leafBorders;
    if (saved.showLeafLabels !== undefined) this.showLeafLabels = saved.showLeafLabels;
    if (saved.showLabels !== undefined) this.showLabels = saved.showLabels;
    if (saved.alignLabels !== undefined) this.alignLabels = saved.alignLabels;
    if (saved.showInternalNodes !== undefined) this.showInternalNodes = saved.showInternalNodes;
    if (saved.showInternalLabels !== undefined) this.showInternalLabels = saved.showInternalLabels;
    if (saved.colourInternalEdges !== undefined) this.colourInternalEdges = saved.colourInternalEdges;
    else if (saved.colourEdges !== undefined) this.colourInternalEdges = saved.colourEdges;
    if (saved.solidEdges !== undefined) this.solidEdges = saved.solidEdges;
    if (saved.alignmentLength !== undefined) this.alignmentLength = saved.alignmentLength;
    if (saved.showBranchLengths !== undefined) this.showBranchLengths = saved.showBranchLengths;
    if (saved.treeLengthUnit !== undefined) this.treeLengthUnit = saved.treeLengthUnit;
    if (saved.decimalDigits !== undefined) this.decimalDigits = saved.decimalDigits;

    if (saved.displayAsField !== undefined) this.displayAsField = saved.displayAsField;
    if (saved.enableColorBy !== undefined) this.enableColorBy = saved.enableColorBy;
    else if (saved.colorByEnabled !== undefined) this.enableColorBy = saved.colorByEnabled;
    if (saved.colorByField !== undefined) this.colorByField = saved.colorByField;
    if (saved.colorByPalette !== undefined) this.colorByPalette = saved.colorByPalette;

    if (saved.selectedMetadataFields !== undefined) {
      this.savedSelectedMetadataFields = saved.selectedMetadataFields;
    }
    if (saved.fieldPalettes !== undefined) {
      this.fieldPalettes = new Map(Object.entries(saved.fieldPalettes));
    }
    if (saved.blockSize !== undefined) this.blockSize = saved.blockSize;
    if (saved.legendVisible !== undefined) this.legendVisible = saved.legendVisible;
    else if (saved.showLegend !== undefined) this.legendVisible = saved.showLegend;
    if (saved.legendCollapsed !== undefined) this.legendCollapsed = saved.legendCollapsed;
    if (saved.legendColumns !== undefined) this.legendColumns = saved.legendColumns;
    if (saved.legendPosition !== undefined) this.legendPosition = saved.legendPosition;

    if (saved.tooltipEnabled !== undefined) this.tooltipEnabled = saved.tooltipEnabled;
    if (saved.tooltipFields !== undefined) this.tooltipFields = saved.tooltipFields;
    if (saved.highlightSyncMode !== undefined) this.highlightSyncMode = saved.highlightSyncMode;

    if (saved.zoomBehavior !== undefined) this.zoomBehavior = saved.zoomBehavior;

    console.log('[PhylocanvasRenderer] Settings loaded (external call)');
  }


  /**
   * Apply loaded settings to the rendered tree
   * This method MUST be called after loadSettings() to actually update the visualization
   */
  applyLoadedSettings(): void {
    if (!this.tree || !this.tree.setProps) {
      console.warn('[PhylocanvasRenderer] Cannot apply settings - tree not initialized');
      return;
    }

    console.log('[PhylocanvasRenderer] Applying settings to tree...');

    const props: any = {
      type: this.treeLayout,
      showLabels: this.showLeafLabels || this.showInternalLabels,
      showLeafLabels: this.showLeafLabels,
      showInternalLabels: this.showInternalLabels,
      alignLabels: this.alignLabels,
      showBranchLengths: this.showBranchLengths,
      nodeSize: this.leafNodeSize,
      fontSize: this.labelSize,
      showShapes: this.showInternalNodes,
      showShapeBorders: this.showLeafBorders,
      shapeBorderWidth: this.showLeafBorders ? 1 : 0,
      styleNodeEdges: this.colourInternalEdges
    };

    const isSNPConversionActive = this.showBranchLengths && this.alignmentLength && this.alignmentLength > 0;
    const needsRoundedNewick = this.showBranchLengths && this.decimalDigits === 0;

    if (isSNPConversionActive || needsRoundedNewick) {
      props.source = this.getActiveNewick();
      props.branchLengthsFormat = this.getBranchLengthFormatter();
      console.log('[PhylocanvasRenderer] Including tree source for SNP/rounding:', {
        isSNPConversionActive,
        needsRoundedNewick,
        decimalDigits: this.decimalDigits
      });
    }

    this.tree.setProps(props);
    console.log('[PhylocanvasRenderer] Tree properties applied:', props);

    this.updateUIControls();
    console.log('[PhylocanvasRenderer] UI controls updated');

    if (this.horizontalZoom !== undefined || this.verticalZoom !== undefined) {
      const hZoom = this.horizontalZoom;
      const vZoom = this.verticalZoom;
      setTimeout(() => {
        if (hZoom !== undefined) {
          this.setHorizontalZoom(hZoom);
        }
        if (vZoom !== undefined) {
          this.setVerticalZoom(vZoom);
        }
        console.log('[PhylocanvasRenderer] Zoom applied (deferred 300ms):', { h: hZoom, v: vZoom });
      }, 300);
    }

    if (this.scalebarVisible) {
      this.createScaleBar();
      console.log('[PhylocanvasRenderer] Scale bar created');
    } else if (this.scalebarElement) {
      this.scalebarElement.remove();
      this.scalebarElement = null;
    }

    if (this.legendVisible) {
      this.updateLegend(this.metadataBlocks, this.metadata);
      console.log('[PhylocanvasRenderer] Legend updated');
    } else if (this.legendElement) {
      this.legendElement.style.display = 'none';
    }

    this.restoreSavedMetadataBlocks();

    if (this.tooltipFields && this.tooltipFields.length > 0) {
      console.log('[PhylocanvasRenderer] Restoring tooltip fields:', this.tooltipFields);
      const tooltipCheckboxes = this.tooltipMenuContainer?.querySelectorAll('.tooltip-field-checkbox');
      if (tooltipCheckboxes) {
        tooltipCheckboxes.forEach(checkbox => {
          (checkbox as HTMLInputElement).checked = this.tooltipFields!.includes((checkbox as HTMLInputElement).value);
        });
      }
    }

    if (this.enableColorBy && this.colorByField) {
      console.log('[PhylocanvasRenderer] Restoring leaf coloring for field:', this.colorByField);

      TreeMetadataSync.setColorByEnabled(true);
      TreeMetadataSync.state.colorByField = this.colorByField;

      const columnTypes = TreeMetadataSync.state.columnTypes || this.app?.columnTypes;
      const metadata = TreeMetadataSync.state.metadata || this.app?.metadata;

      if (metadata && columnTypes) {
        const paletteName = this.colorByPalette?.name || this.fieldPalettes?.get(this.colorByField) || 'Set1';
        TreeMetadataSync.setLeafPalette(this.colorByPalette?.category || 'qualitative', paletteName);
        TreeMetadataSync.generateLeafColorMap(metadata, this.colorByField, columnTypes);

        const nodeStyles = TreeMetadataSync.computeNodeStyles(metadata);
        console.log('[PhylocanvasRenderer] Applying restored leaf node styles, count:', Object.keys(nodeStyles).length);
        this.setNodeStyles(nodeStyles);
      } else {
        console.warn('[PhylocanvasRenderer] Cannot apply leaf coloring: missing metadata or columnTypes',
          { hasMetadata: !!metadata, hasColumnTypes: !!columnTypes });
      }

      const enableColorByCheckbox = document.getElementById('enableColorBy') as HTMLInputElement | null;
      const colorBySelect = document.getElementById('colorByField') as HTMLSelectElement | null;
      const colorPaletteBtn = document.getElementById('colorPaletteBtn') as HTMLButtonElement | null;

      if (enableColorByCheckbox) enableColorByCheckbox.checked = true;
      if (colorBySelect) {
        colorBySelect.disabled = false;
        if (colorBySelect.querySelector(`option[value="${this.colorByField}"]`)) {
          colorBySelect.value = this.colorByField!;
        }
      }
      if (colorPaletteBtn) colorPaletteBtn.disabled = false;

      console.log('[PhylocanvasRenderer] Leaf coloring restored for field:', this.colorByField);

      if (this.legendVisible) {
        this.updateLegend(this.metadataBlocks, this.metadata);
        console.log('[PhylocanvasRenderer] Legend updated with leaf colors');
      }
    } else if (!this.enableColorBy) {
      console.log('[PhylocanvasRenderer] Resetting leaf colors (coloring disabled)');

      TreeMetadataSync.setColorByEnabled(false);
      TreeMetadataSync.state.colorByField = null;
      TreeMetadataSync.state.leafColorByField = null;
      TreeMetadataSync.state.leafColorMap = new Map();

      this.setNodeStyles({});

      const enableColorByCheckbox = document.getElementById('enableColorBy') as HTMLInputElement | null;
      const colorBySelect = document.getElementById('colorByField') as HTMLSelectElement | null;
      const colorPaletteBtn = document.getElementById('colorPaletteBtn') as HTMLButtonElement | null;

      if (enableColorByCheckbox) enableColorByCheckbox.checked = false;
      if (colorBySelect) {
        colorBySelect.disabled = true;
        colorBySelect.value = '';
      }
      if (colorPaletteBtn) colorPaletteBtn.disabled = true;

      if (this.legendVisible) {
        this.updateLegend(this.metadataBlocks, this.metadata);
        console.log('[PhylocanvasRenderer] Legend updated (leaf colors removed)');
      }
    }

    if (this.displayAsField && this.app && this.app.updateTreeTipLabels) {
      console.log('[PhylocanvasRenderer] Applying display-as field:', this.displayAsField);
      this.app.updateTreeTipLabels(this.displayAsField);
    }

    if (this.showBranchLengths && this.alignmentLength && this.alignmentLength > 0 && this.tree) {
      console.log('[PhylocanvasRenderer]  SNP conversion enabled - reloading tree with transformed Newick');
      console.log('[PhylocanvasRenderer] alignmentLength:', this.alignmentLength, 'showBranchLengths:', this.showBranchLengths);

      const currentStyles = { ...this.nodeStyles };
      const currentSelectedIds = [...this.selectedIds];

      this.tree.setProps({
        source: this.getActiveNewick(),
        showBranchLengths: true
      });

      setTimeout(() => {
        if (Object.keys(this.tipLabelMap).length > 0) {
          this.updateTipLabels(this.tipLabelMap);
        } else if (Object.keys(currentStyles).length > 0) {
          this.tree.setProps({ styles: currentStyles });
        }

        if (currentSelectedIds.length > 0) {
          this.tree.setProps({ selectedIds: currentSelectedIds });
        }

        if (this.metadataBlocks.length > 0 && Object.keys(this.metadata).length > 0) {
          this.setMetadataBlocks(this.metadataBlocks, this.metadata, true);
        }
      }, 100);

      console.log('[PhylocanvasRenderer] Tree reloaded with SNP-transformed Newick');
    }

    setTimeout(() => {
      if (this.tree?.fitInCanvas) {
        this.tree.fitInCanvas();
        this.initialViewState = this.tree?.getView?.();
        console.log('[PhylocanvasRenderer] Final fitInCanvas after all settings applied');

        if (this.scalebarVisible) {
          this.createScaleBar();
          console.log('[PhylocanvasRenderer] Scalebar recreated with correct branch scale');
        }
      }
    }, 400);

    console.log('[PhylocanvasRenderer] All settings applied successfully');
  }

  /**
   * Update UI controls to reflect current settings
   */
  updateUIControls(): void {
    const layoutSelect = document.getElementById('tree-layout-select') as HTMLSelectElement | null;
    if (layoutSelect) {
      layoutSelect.value = this.treeLayout;
    }

    const scalebarCheckbox = document.getElementById('show-scalebar-checkbox') as HTMLInputElement | null;
    if (scalebarCheckbox) scalebarCheckbox.checked = this.scalebarVisible;

    const scalebarPositionSelect = document.getElementById('scalebar-position-select') as HTMLSelectElement | null;
    if (scalebarPositionSelect) scalebarPositionSelect.value = this.scalebarPosition as string;

    const scalebarWidthSlider = document.getElementById('scalebar-width-slider') as HTMLInputElement | null;
    const scalebarWidthValue = document.getElementById('scalebar-width-value');
    if (scalebarWidthSlider) {
      scalebarWidthSlider.value = String(this.scalebarWidth);
      if (scalebarWidthValue) scalebarWidthValue.textContent = `${this.scalebarWidth}px`;
    }

    const legendCheckbox = document.getElementById('show-legend-checkbox') as HTMLInputElement | null;
    if (legendCheckbox) legendCheckbox.checked = this.legendVisible;

    const legendPositionSelect = document.getElementById('legend-position-select') as HTMLSelectElement | null;
    if (legendPositionSelect) {
      if (typeof this.legendPosition === 'string') {
        legendPositionSelect.value = this.legendPosition;
      }
    }

    const legendColumnsSlider = document.getElementById('legend-columns-slider') as HTMLInputElement | null;
    const legendColumnsValue = document.getElementById('legend-columns-value');
    if (legendColumnsSlider) {
      legendColumnsSlider.value = String(this.legendColumns);
      if (legendColumnsValue) legendColumnsValue.textContent = String(this.legendColumns);
    }

    const blockSizeSlider = document.getElementById('block-size-slider') as HTMLInputElement | null;
    const blockSizeValue = document.getElementById('block-size-value');
    if (blockSizeSlider) {
      blockSizeSlider.value = String(this.blockSize);
      if (blockSizeValue) blockSizeValue.textContent = `${this.blockSize}px`;
    }

    if (this.fieldPalettes && this.fieldPalettes.size > 0) {
      this.fieldPalettes.forEach((palette, field) => {
        const paletteSelect = document.querySelector(`.field-palette-select[data-field="${field}"]`) as HTMLSelectElement | null;
        if (paletteSelect) {
          paletteSelect.value = palette;
          console.log(`[PhylocanvasRenderer] Updated palette dropdown for "${field}" to "${palette}"`);
        }

        const previewDiv = document.querySelector(`.field-palette-preview[data-field="${field}"]`);
        if (previewDiv) {
          const palettes = ColorBrewerPalettes.qualitative || {};
          const paletteObj = palettes[palette];
          if (paletteObj) {
            const sizes = Object.keys(paletteObj).map(Number).sort((a: number, b: number) => a - b);
            const size = sizes.find((s: number) => s >= 8) || sizes[sizes.length - 1]!;
            const colors = paletteObj[size]?.slice(0, 8) || [];
            previewDiv.textContent = '';
            colors.forEach((color: string) => {
              const swatch = document.createElement('div');
              swatch.className = 'palette-color';
              swatch.style.backgroundColor = color;
              previewDiv.appendChild(swatch);
            });
            console.log(`[PhylocanvasRenderer] Updated palette preview for "${field}" to "${palette}"`);
          }
        }
      });
    }

    const tooltipToggle = document.getElementById('enable-tooltip-toggle') as HTMLInputElement | null;
    if (tooltipToggle) tooltipToggle.checked = this.tooltipEnabled;

    const highlightSyncSelect = document.getElementById('highlight-sync-mode-select') as HTMLSelectElement | null;
    if (highlightSyncSelect) highlightSyncSelect.value = this.highlightSyncMode;

    const treeLengthUnitSelect = document.getElementById('treeLengthUnit') as HTMLSelectElement | null;
    if (treeLengthUnitSelect) {
      treeLengthUnitSelect.value = this.treeLengthUnit || 'none';
    }

    const decimalDigitsSelect = document.getElementById('decimalDigits') as HTMLSelectElement | null;
    if (decimalDigitsSelect) {
      decimalDigitsSelect.value = String(this.decimalDigits || 1);

      const shouldDisable = this.shouldDisableDecimalDigits();
      decimalDigitsSelect.disabled = shouldDisable;
      decimalDigitsSelect.style.opacity = shouldDisable ? '0.5' : '1';
      decimalDigitsSelect.style.cursor = shouldDisable ? 'not-allowed' : 'pointer';
      decimalDigitsSelect.title = shouldDisable
        ? 'Rounding disabled: branch values are too small (max < 0.1)'
        : '';
    }

    const alignmentLengthInput = document.getElementById('phylo-alignment-length-nodes') as HTMLInputElement | null;
    if (alignmentLengthInput) {
      alignmentLengthInput.value = String(this.alignmentLength || '');

      if (this.treeLengthUnit === 'snp') {
        alignmentLengthInput.disabled = true;
        alignmentLengthInput.style.opacity = '0.5';
        alignmentLengthInput.style.cursor = 'not-allowed';
      } else {
        alignmentLengthInput.disabled = false;
        alignmentLengthInput.style.opacity = '1';
        alignmentLengthInput.style.cursor = 'text';
      }
    }

    const leafSizeSlider = document.getElementById('leaf-node-size-slider') as HTMLInputElement | null;
    const leafSizeValue = document.getElementById('leaf-node-size-value');
    if (leafSizeSlider) {
      leafSizeSlider.value = String(this.leafNodeSize);
      if (leafSizeValue) leafSizeValue.textContent = `${this.leafNodeSize}px`;
    }

    const labelSizeSlider = document.getElementById('label-size-slider') as HTMLInputElement | null;
    const labelSizeValue = document.getElementById('label-size-value');
    if (labelSizeSlider) {
      labelSizeSlider.value = String(this.labelSize);
      if (labelSizeValue) labelSizeValue.textContent = `${this.labelSize}px`;
    }

    const leafBordersToggle = document.getElementById('leaf-borders-toggle') as HTMLInputElement | null;
    if (leafBordersToggle) leafBordersToggle.checked = this.showLeafBorders;

    const leafLabelsToggle = document.getElementById('leaf-labels-toggle') as HTMLInputElement | null;
    if (leafLabelsToggle) leafLabelsToggle.checked = this.showLeafLabels;

    const alignLabelsToggle = document.getElementById('align-labels-toggle-nodes') as HTMLInputElement | null;
    if (alignLabelsToggle) {
      alignLabelsToggle.checked = this.alignLabels;
      alignLabelsToggle.disabled = !this.showLeafLabels;
      if (!this.showLeafLabels) {
        alignLabelsToggle.style.opacity = '0.5';
        alignLabelsToggle.style.cursor = 'not-allowed';
      } else {
        alignLabelsToggle.style.opacity = '1';
        alignLabelsToggle.style.cursor = 'pointer';
      }
    }

    const internalLabelsToggle = document.getElementById('internal-labels-toggle') as HTMLInputElement | null;
    if (internalLabelsToggle) internalLabelsToggle.checked = this.showInternalLabels;

    const branchLengthsToggle = document.getElementById('branch-lengths-toggle') as HTMLInputElement | null;
    if (branchLengthsToggle) branchLengthsToggle.checked = this.showBranchLengths;

    const colourEdgesToggle = document.getElementById('colour-edges-toggle') as HTMLInputElement | null;
    if (colourEdgesToggle) colourEdgesToggle.checked = this.colourInternalEdges;

    const solidEdgesToggle = document.getElementById('solid-edges-toggle') as HTMLInputElement | null;
    if (solidEdgesToggle) solidEdgesToggle.checked = this.solidEdges;

    const enableColorByCheckbox = document.getElementById('enableColorBy') as HTMLInputElement | null;
    if (enableColorByCheckbox) enableColorByCheckbox.checked = this.enableColorBy || false;

    const colorByFieldSelect = document.getElementById('colorByField') as HTMLSelectElement | null;
    if (colorByFieldSelect) {
      colorByFieldSelect.disabled = !this.enableColorBy;
      if (this.colorByField) {
        colorByFieldSelect.value = this.colorByField;
      }
    }

    const displayAsFieldSelect = document.getElementById('displayAsField') as HTMLSelectElement | null;
    if (displayAsFieldSelect && this.displayAsField) {
      displayAsFieldSelect.value = this.displayAsField;
    }

    const horizontalZoomSlider = document.getElementById('horizontal-zoom-slider') as HTMLInputElement | null;
    const horizontalZoomValue = document.getElementById('horizontal-zoom-value');
    if (horizontalZoomSlider && this.horizontalZoom !== undefined) {
      horizontalZoomSlider.value = String(this.horizontalZoom);
      if (horizontalZoomValue) horizontalZoomValue.textContent = `${this.horizontalZoom.toFixed(1)}x`;
    }

    const verticalZoomSlider = document.getElementById('vertical-zoom-slider') as HTMLInputElement | null;
    const verticalZoomValue = document.getElementById('vertical-zoom-value');
    if (verticalZoomSlider && this.verticalZoom !== undefined) {
      verticalZoomSlider.value = String(this.verticalZoom);
      if (verticalZoomValue) verticalZoomValue.textContent = `${this.verticalZoom.toFixed(1)}x`;
    }
  }

  /**
   * Change tree layout
   */
  changeTreeLayout(layoutType: string): void {
    console.log('[PhylocanvasRenderer] Changing layout to:', layoutType);
    this.treeLayout = layoutType;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ type: layoutType });
    }
  }

  /**
   * Toggle label visibility
   */
  toggleLabels(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle labels:', show);
    this.showLabels = show;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({
        showLabels: show,
        showLeafLabels: show
      });
    }
  }

  /**
   * Toggle label alignment
   */
  toggleLabelAlignment(align: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle label alignment:', align);
    this.alignLabels = align;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ alignLabels: align });
    }
  }


  /**
   * Create custom scale bar overlay
   */
  createScaleBar(): void {
    console.trace('[PhylocanvasRenderer] createScaleBar() called with alignmentLength:', this.alignmentLength);

    if (this.scalebarElement) {
      this.scalebarElement.remove();
    }

    this.scalebarElement = document.createElement('div');
    this.scalebarElement.className = 'phylocanvas-scalebar';

    const currentBranchScale = this.tree && this.tree.getBranchScale ? this.tree.getBranchScale() : 100;

    const isSNPConversionActive = this.alignmentLength && this.alignmentLength > 0;
    if (!isSNPConversionActive) {
      this.originalBranchScale = currentBranchScale;
      console.log('[PhylocanvasRenderer] Updated original branch scale:', this.originalBranchScale);
    }

    const branchScale = this.originalBranchScale || currentBranchScale;
    console.log('[PhylocanvasRenderer] Using branch scale for scale bar:', branchScale, 'current:', currentBranchScale);

    const viewportScale = this.tree && this.tree.getScale ? this.tree.getScale(true) : 1;
    console.log('[PhylocanvasRenderer] Viewport scale:', viewportScale);

    const originalBranchLength = (this.scalebarWidth / branchScale / viewportScale);

    let displayValue: string;
    let numericValue = originalBranchLength;

    if (this.alignmentLength && this.alignmentLength > 0) {
      numericValue = originalBranchLength * this.alignmentLength;
      console.log('[PhylocanvasRenderer] Scale bar with alignment conversion:', {
        originalBranchLength,
        alignmentLength: this.alignmentLength,
        numericValue
      });
    }

    if (numericValue === 0 || isNaN(numericValue)) {
      console.warn('[PhylocanvasRenderer] Invalid branch length for scale bar:', numericValue);
      displayValue = 'N/A';
    } else if (numericValue < 0.001) {
      displayValue = numericValue.toExponential(2);
    } else if (numericValue < 1) {
      displayValue = numericValue.toFixed(3);
    } else if (this.decimalDigits === 0) {
      displayValue = Math.round(numericValue).toString();
    } else {
      displayValue = numericValue.toFixed(this.decimalDigits);
    }

    let labelText = displayValue;
    const unitSuffixes: Record<string, string> = {
      'substitution': ' substitutions/site',
      'snp': ' SNPs',
      'alleles': ' alleles'
    };
    console.log('[PhylocanvasRenderer] Scale bar unit:', this.treeLengthUnit, 'suffix:', unitSuffixes[this.treeLengthUnit]);
    if (this.treeLengthUnit && unitSuffixes[this.treeLengthUnit]) {
      labelText = displayValue + unitSuffixes[this.treeLengthUnit]!;
    }
    console.log('[PhylocanvasRenderer] Scale bar label:', labelText);

    const header = document.createElement('div');
    header.className = 'scalebar-header';
    header.title = 'Drag to reposition';

    const line = document.createElement('div');
    line.className = 'scalebar-line';
    line.style.width = `${this.scalebarWidth}px`;

    const label = document.createElement('div');
    label.className = 'scalebar-label';
    label.textContent = labelText;

    header.appendChild(line);
    header.appendChild(label);
    this.scalebarElement.appendChild(header);

    this.container.appendChild(this.scalebarElement);
    this.updateScalebarStyle();
    this.makeScalebarDraggable();

    console.log('[PhylocanvasRenderer] Scale bar created with label:', labelText);
  }

  /**
   * Update scale bar style based on position and visibility
   */
  updateScalebarStyle(): void {
    if (!this.scalebarElement) return;

    this.scalebarElement.style.display = this.scalebarVisible ? 'block' : 'none';

    const line = this.scalebarElement.querySelector('.scalebar-line') as HTMLElement | null;
    if (line) {
      line.style.width = `${this.scalebarWidth}px`;
    }

    this.scalebarElement.style.top = 'auto';
    this.scalebarElement.style.bottom = 'auto';
    this.scalebarElement.style.left = 'auto';
    this.scalebarElement.style.right = 'auto';

    if (typeof this.scalebarPosition === 'object' && this.scalebarPosition.x !== undefined) {
      this.scalebarElement.style.left = `${this.scalebarPosition.x}px`;
      this.scalebarElement.style.top = `${this.scalebarPosition.y}px`;
    } else {
      const margin = 20;
      switch (this.scalebarPosition) {
        case 'bottom-left':
          this.scalebarElement.style.bottom = `${margin}px`;
          this.scalebarElement.style.left = `${margin}px`;
          break;
        case 'bottom-right':
          this.scalebarElement.style.bottom = `${margin}px`;
          this.scalebarElement.style.right = `${margin}px`;
          break;
        case 'top-left':
          this.scalebarElement.style.top = `${margin}px`;
          this.scalebarElement.style.left = `${margin}px`;
          break;
        case 'top-right':
          this.scalebarElement.style.top = `${margin}px`;
          this.scalebarElement.style.right = `${margin}px`;
          break;
      }
    }
  }

  /**
   * Update scale bar label efficiently without recreating the entire element
   * Called on zoom changes to keep scalebar in sync with visual branch lengths
   */
  updateScalebarLabel(): void {
    if (!this.scalebarElement || !this.tree) return;

    const currentBranchScale = this.tree.getBranchScale ? this.tree.getBranchScale() : 100;
    const branchScale = this.originalBranchScale || currentBranchScale;
    const viewportScale = this.tree.getScale ? this.tree.getScale(true) : 1;

    let numericValue = this.scalebarWidth / branchScale / viewportScale;

    if (this.alignmentLength && this.alignmentLength > 0) {
      numericValue *= this.alignmentLength;
    }

    let displayValue: string;
    if (numericValue === 0 || isNaN(numericValue)) {
      displayValue = 'N/A';
    } else if (numericValue < 0.001) {
      displayValue = numericValue.toExponential(2);
    } else if (numericValue < 1) {
      displayValue = numericValue.toFixed(3);
    } else if (this.decimalDigits === 0) {
      displayValue = Math.round(numericValue).toString();
    } else {
      displayValue = numericValue.toFixed(this.decimalDigits);
    }

    let labelText = displayValue;
    const unitSuffixes: Record<string, string> = {
      'substitution': ' substitutions/site',
      'snp': ' SNPs',
      'alleles': ' alleles'
    };
    if (this.treeLengthUnit && unitSuffixes[this.treeLengthUnit]) {
      labelText = displayValue + unitSuffixes[this.treeLengthUnit]!;
    }

    const label = this.scalebarElement.querySelector('.scalebar-label');
    if (label) {
      label.textContent = labelText;
    }
  }

  /**
   * Toggle scale bar visibility
   */
  toggleScalebar(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle scale bar:', show);
    this.scalebarVisible = show;
    this.updateScalebarStyle();
  }

  /**
   * Update scale bar position
   */
  updateScalebarPosition(position: string | { x: number; y: number }): void {
    console.log('[PhylocanvasRenderer] Update scale bar position:', position);
    this.scalebarPosition = position;
    this.updateScalebarStyle();
  }

  /**
   * Update scale bar width
   */
  updateScalebarWidth(width: number): void {
    console.log('[PhylocanvasRenderer] Update scale bar width:', width);
    this.scalebarWidth = width;
    this.updateScalebarStyle();

    if (this.tree) {
      this.createScaleBar();
    }
  }

  /**
   * Make scale bar draggable by user
   */
  makeScalebarDraggable(): void {
    if (!this.scalebarElement) return;

    const header = this.scalebarElement.querySelector('.scalebar-header');
    if (!header) return;

    const onMouseDown = (e: MouseEvent) => {
      this.scalebarDragging = true;
      this.scalebarElement!.classList.add('dragging');

      const rect = this.scalebarElement!.getBoundingClientRect();
      this.scalebarDragOffset.x = e.clientX - rect.left;
      this.scalebarDragOffset.y = e.clientY - rect.top;

      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!this.scalebarDragging) return;

      const containerRect = this.container.getBoundingClientRect();
      const scalebarRect = this.scalebarElement!.getBoundingClientRect();

      let x = e.clientX - containerRect.left - this.scalebarDragOffset.x;
      let y = e.clientY - containerRect.top - this.scalebarDragOffset.y;

      x = Math.max(0, Math.min(x, containerRect.width - scalebarRect.width));
      y = Math.max(0, Math.min(y, containerRect.height - scalebarRect.height));

      this.scalebarPosition = { x, y };
      this.scalebarElement!.style.left = `${x}px`;
      this.scalebarElement!.style.top = `${y}px`;
      this.scalebarElement!.style.right = 'auto';
      this.scalebarElement!.style.bottom = 'auto';
    };

    const onMouseUp = () => {
      if (!this.scalebarDragging) return;
      this.scalebarDragging = false;
      this.scalebarElement!.classList.remove('dragging');
    };

    header.addEventListener('mousedown', onMouseDown as EventListener);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    (this as any).scalebarDragHandlers = { onMouseDown, onMouseMove, onMouseUp, header };
  }

  /**
   * Clean up scale bar drag event listeners
   */
  cleanupScalebarDrag(): void {
    const handlers = (this as any).scalebarDragHandlers;
    if (handlers) {
      const { onMouseDown, onMouseMove, onMouseUp, header } = handlers;
      header.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      (this as any).scalebarDragHandlers = null;
    }
  }

  /**
   * Set horizontal zoom (branch zoom)
   */
  setHorizontalZoom(zoom: number): void {
    console.log('[PhylocanvasRenderer] Set horizontal zoom:', zoom);
    this.horizontalZoom = zoom;
    if (this.tree && this.tree.setBranchZoom) {
      this.tree.setBranchZoom(zoom);
    } else {
      console.warn('[PhylocanvasRenderer] Cannot set horizontal zoom - tree or method not available');
    }
  }

  /**
   * Set vertical zoom (step zoom)
   */
  setVerticalZoom(zoom: number): void {
    console.log('[PhylocanvasRenderer] Set vertical zoom:', zoom);
    this.verticalZoom = zoom;
    if (this.tree && this.tree.setStepZoom) {
      this.tree.setStepZoom(zoom);
    } else {
      console.warn('[PhylocanvasRenderer] Cannot set vertical zoom - tree or method not available');
    }
  }

  /**
   * Set leaf node size
   */
  setLeafNodeSize(size: number): void {
    console.log('[PhylocanvasRenderer] Set leaf node size:', size);
    this.leafNodeSize = size;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ nodeSize: size });
    }
  }

  /**
   * Set label font size
   */
  setLabelSize(size: number): void {
    console.log('[PhylocanvasRenderer] Set label size:', size);
    this.labelSize = size;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ fontSize: size });
    }
  }

  /**
   * Toggle leaf borders
   */
  toggleLeafBorders(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle leaf borders:', show);
    this.showLeafBorders = show;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({
        showShapeBorders: show,
        shapeBorderWidth: show ? 1 : 0
      });
    }
  }

  /**
   * Toggle leaf labels
   */
  toggleLeafLabels(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle leaf labels:', show);
    this.showLeafLabels = show;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({
        showLabels: show || this.showInternalLabels,
        showLeafLabels: show
      });
    }
  }

  /**
   * Toggle internal nodes
   */
  toggleInternalNodes(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle internal nodes:', show);
    this.showInternalNodes = show;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ showShapes: show });
    }
  }

  /**
   * Toggle internal labels
   */
  toggleInternalLabels(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle internal labels:', show);
    this.showInternalLabels = show;
    if (this.tree && this.tree.setProps) {
      const props = {
        showLabels: this.showLeafLabels || show,
        showInternalLabels: show
      };
      console.log('[PhylocanvasRenderer] Setting props:', props);
      console.log('[PhylocanvasRenderer] Current tree props before update:', {
        showLabels: this.tree.props.showLabels,
        showLeafLabels: this.tree.props.showLeafLabels,
        showInternalLabels: this.tree.props.showInternalLabels
      });
      this.tree.setProps(props);
      console.log('[PhylocanvasRenderer] Tree props after update:', {
        showLabels: this.tree.props.showLabels,
        showLeafLabels: this.tree.props.showLeafLabels,
        showInternalLabels: this.tree.props.showInternalLabels
      });
    }
  }

  /**
   * Toggle branch lengths display
   */
  toggleBranchLengthsDisplay(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle branch lengths display:', show);
    this.showBranchLengths = show;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ showBranchLengths: show });
    }
  }

  /**
   * Toggle colour internal edges
   */
  toggleColourEdges(enable: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle colour internal edges:', enable);
    this.colourInternalEdges = enable;
    if (this.tree && this.tree.setProps) {
      this.tree.setProps({ styleNodeEdges: enable });
    }
  }

  /**
   * Toggle solid edges
   */
  toggleSolidEdges(solid: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle solid edges:', solid);
    this.solidEdges = solid;
    console.warn('[PhylocanvasRenderer] Solid edges toggle not yet fully implemented');
  }


  /**
   * Update legend for metadata blocks and leaf colors (tabbed interface)
   */
  updateLegend(fields: string[], metadata: Record<string, any>): void {
    console.log('[PhylocanvasRenderer] Updating legend for fields:', fields);

    if (this.legendElement) {
      this.legendElement.remove();
      this.legendElement = null;
    }

    let leafColorData: { field: string; colorMap: Map<any, any> } | null = null;
    if (this.enableColorBy && this.colorByField && TreeMetadataSync.state.leafColorMap?.size > 0) {
      leafColorData = {
        field: this.colorByField,
        colorMap: TreeMetadataSync.state.leafColorMap
      };
      console.log('[PhylocanvasRenderer] Including leaf color legend for field:', this.colorByField);
    }

    if ((!fields || fields.length === 0 || !metadata) && !leafColorData) {
      return;
    }

    this.legendElement = document.createElement('div');
    this.legendElement.className = 'phylocanvas-legend';
    if (this.legendCollapsed) {
      this.legendElement.classList.add('collapsed');
    }
    this.legendElement.classList.add(`columns-${this.legendColumns}`);
    this.legendElement.style.setProperty('--legend-columns', String(this.legendColumns));

    const fieldDataMap = new Map<string, Map<any, any>>();
    if (fields && metadata) {
      fields.forEach(field => {
        const valueColorMap = new Map<any, any>();
        Object.keys(metadata).forEach(nodeId => {
          const nodeMetadata = metadata[nodeId];
          if (nodeMetadata && nodeMetadata[field]) {
            const value = nodeMetadata[field].label;
            const color = nodeMetadata[field].colour;
            if (value !== undefined && value !== '' && color) {
              valueColorMap.set(value, color);
            }
          }
        });
        if (valueColorMap.size > 0) {
          fieldDataMap.set(field, valueColorMap);
        }
      });
    }

    const fieldsWithData = Array.from(fieldDataMap.keys());
    const totalTabs = fieldsWithData.length + (leafColorData ? 1 : 0);

    if (totalTabs === 1) {
      this.legendElement.classList.add('single-field');
    }

    this.buildLegendDOM(fieldsWithData, fieldDataMap, leafColorData);
    this.container.appendChild(this.legendElement);

    const collapseBtn = this.legendElement.querySelector('.legend-collapse-btn');
    if (collapseBtn) {
      collapseBtn.addEventListener('click', () => this.toggleLegendCollapse());
    }

    this.legendElement.querySelectorAll('.legend-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        const tabName = (e.target as HTMLElement).dataset['tab'];
        this.switchLegendTab(tabName!);
      });
    });

    this.makeLegendDraggable();
    this.setupLegendColorPickers();
    this.setupLegendLabelClicks();
    this.updateLegendStyle();

    const allTabs = leafColorData ? ['__leaf_colors__', ...fieldsWithData] : fieldsWithData;
    if (this.legendActiveTab && allTabs.includes(this.legendActiveTab)) {
      this.switchLegendTab(this.legendActiveTab);
    }

    console.log('[PhylocanvasRenderer] Legend created with', totalTabs, 'tabs (leaf colors:', !!leafColorData, ', metadata blocks:', fieldsWithData.length, ')');
  }

  /**
   * Build legend DOM structure
   */
  buildLegendDOM(fieldsWithData: string[], fieldDataMap: Map<string, Map<any, any>>, leafColorData: { field: string; colorMap: Map<any, any> } | null = null): void {
    const header = document.createElement('div');
    header.className = 'legend-header';

    const title = document.createElement('div');
    title.className = 'legend-title';
    title.textContent = 'Legend';
    header.appendChild(title);

    const collapseBtn = document.createElement('button');
    collapseBtn.className = 'legend-collapse-btn';
    collapseBtn.title = 'Collapse/Expand';
    collapseBtn.textContent = '▼';
    header.appendChild(collapseBtn);

    this.legendElement!.appendChild(header);

    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'legend-tabs';

    const leafColorTabName = leafColorData ? `● ${leafColorData.field}` : null;
    let isFirstTab = true;

    if (leafColorData) {
      const tab = document.createElement('button');
      tab.className = 'legend-tab legend-tab-leaf-colors' + (isFirstTab ? ' active' : '');
      tab.dataset['tab'] = '__leaf_colors__';
      tab.dataset['type'] = 'leaf-colors';
      tab.title = `Leaf Colors: ${leafColorData.field}`;
      tab.textContent = leafColorTabName;
      tabsContainer.appendChild(tab);
      isFirstTab = false;
    }

    fieldsWithData.forEach((field) => {
      const tab = document.createElement('button');
      tab.className = 'legend-tab legend-tab-metadata' + (isFirstTab ? ' active' : '');
      tab.dataset['tab'] = field;
      tab.dataset['type'] = 'metadata';
      tab.title = `Metadata: ${field}`;
      tab.textContent = `▢ ${field}`;
      tabsContainer.appendChild(tab);
      isFirstTab = false;
    });

    this.legendElement!.appendChild(tabsContainer);

    const contentContainer = document.createElement('div');
    contentContainer.className = 'legend-content';

    isFirstTab = true;

    if (leafColorData) {
      const panel = document.createElement('div');
      panel.className = 'legend-tab-panel legend-panel-leaf-colors' + (isFirstTab ? ' active' : '');
      panel.dataset['panel'] = '__leaf_colors__';

      const itemsContainer = document.createElement('div');
      itemsContainer.className = 'legend-items';

      const sortedEntries = Array.from(leafColorData.colorMap.entries()).sort((a, b) =>
        String(a[0]).localeCompare(String(b[0]))
      );

      sortedEntries.forEach(([value, color]) => {
        if (TreeMetadataSync.isEmptyValue(value)) return;

        const hexColor = Array.isArray(color)
          ? this.rgbToHex(color[0]!, color[1]!, color[2]!)
          : color;

        const item = document.createElement('div');
        item.className = 'legend-item';

        const colorSwatch = document.createElement('div');
        colorSwatch.className = 'legend-color legend-color-clickable';
        colorSwatch.style.backgroundColor = hexColor;
        colorSwatch.dataset['field'] = leafColorData!.field;
        colorSwatch.dataset['value'] = value;
        colorSwatch.dataset['color'] = hexColor;
        colorSwatch.dataset['type'] = 'leaf-colors';
        colorSwatch.title = 'Click to change color';

        const label = document.createElement('div');
        label.className = 'legend-label legend-label-clickable';
        label.textContent = value;
        label.dataset['field'] = leafColorData!.field;
        label.dataset['value'] = value;
        label.dataset['type'] = 'leaf-colors';
        label.title = 'Click to select leaves with this value';

        item.appendChild(colorSwatch);
        item.appendChild(label);
        itemsContainer.appendChild(item);
      });

      panel.appendChild(itemsContainer);
      contentContainer.appendChild(panel);
      isFirstTab = false;
    }

    fieldsWithData.forEach((field) => {
      const panel = document.createElement('div');
      panel.className = 'legend-tab-panel legend-panel-metadata' + (isFirstTab ? ' active' : '');
      panel.dataset['panel'] = field;

      const itemsContainer = document.createElement('div');
      itemsContainer.className = 'legend-items';

      const valueColorMap = fieldDataMap.get(field)!;
      const sortedEntries = Array.from(valueColorMap.entries()).sort((a, b) =>
        String(a[0]).localeCompare(String(b[0]))
      );

      sortedEntries.forEach(([value, color]) => {
        const hexColor = Array.isArray(color)
          ? this.rgbToHex(color[0]!, color[1]!, color[2]!)
          : color;

        const item = document.createElement('div');
        item.className = 'legend-item';

        const colorSwatch = document.createElement('div');
        colorSwatch.className = 'legend-color legend-color-clickable';
        colorSwatch.style.backgroundColor = hexColor;
        colorSwatch.dataset['field'] = field;
        colorSwatch.dataset['value'] = value;
        colorSwatch.dataset['color'] = hexColor;
        colorSwatch.dataset['type'] = 'metadata';
        colorSwatch.title = 'Click to change color';

        const label = document.createElement('div');
        label.className = 'legend-label legend-label-clickable';
        label.textContent = value;
        label.dataset['field'] = field;
        label.dataset['value'] = value;
        label.dataset['type'] = 'metadata';
        label.title = 'Click to select leaves with this value';

        item.appendChild(colorSwatch);
        item.appendChild(label);
        itemsContainer.appendChild(item);
      });

      panel.appendChild(itemsContainer);
      contentContainer.appendChild(panel);
      isFirstTab = false;
    });

    this.legendElement!.appendChild(contentContainer);
  }

  /**
   * Switch legend tab
   */
  switchLegendTab(tabName: string): void {
    if (!this.legendElement) return;

    this.legendElement.querySelectorAll('.legend-tab').forEach(t => t.classList.remove('active'));
    this.legendElement.querySelectorAll('.legend-tab-panel').forEach(p => p.classList.remove('active'));

    const selectedTab = this.legendElement.querySelector(`.legend-tab[data-tab="${tabName}"]`);
    const selectedPanel = this.legendElement.querySelector(`.legend-tab-panel[data-panel="${tabName}"]`);

    if (selectedTab) selectedTab.classList.add('active');
    if (selectedPanel) selectedPanel.classList.add('active');

    this.legendActiveTab = tabName;

    console.log('[PhylocanvasRenderer] Switched legend tab to:', tabName);
  }

  /**
   * Setup click handlers for legend color swatches to allow color customization
   */
  setupLegendColorPickers(): void {
    if (!this.legendElement) return;

    const colorSwatches = this.legendElement.querySelectorAll('.legend-color-clickable');
    colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.stopPropagation();
        const el = swatch as HTMLElement;
        const field = el.dataset['field']!;
        const value = el.dataset['value']!;
        const currentColor = el.dataset['color']!;
        const colorType = el.dataset['type'] || 'metadata';

        this.showColorConfirmModal(field, value, currentColor, el, colorType);
      });
    });
  }

  /**
   * Setup click handlers for legend labels to select matching nodes
   */
  setupLegendLabelClicks(): void {
    if (!this.legendElement) return;

    const labels = this.legendElement.querySelectorAll('.legend-label-clickable');
    labels.forEach(label => {
      label.addEventListener('click', (e) => {
        e.stopPropagation();
        const el = label as HTMLElement;
        const field = el.dataset['field']!;
        const value = el.dataset['value']!;

        this.selectNodesByLegendValue(field, value);
      });
    });
  }

  /**
   * Show color confirmation modal
   * @param {string} colorType - 'leaf-colors' or 'metadata'
   */
  showColorConfirmModal(field: string, value: string, currentColor: string, swatch: HTMLElement, colorType: string = 'metadata'): void {
    const existingModal = document.querySelector('.color-confirm-modal');
    if (existingModal) existingModal.remove();

    const presetColors = [
      '#e53935', '#d81b60', '#8e24aa', '#5e35b1', '#3949ab',
      '#1e88e5', '#039be5', '#00acc1', '#00897b', '#43a047',
      '#7cb342', '#c0ca33', '#fdd835', '#ffb300', '#fb8c00',
      '#f4511e', '#6d4c41', '#757575', '#546e7a', '#263238'
    ];

    const modal = document.createElement('div');
    modal.className = 'modal color-confirm-modal';

    const content = document.createElement('div');
    content.className = 'modal-content';

    const header = document.createElement('div');
    header.className = 'modal-header';
    const headerTitle = document.createElement('h3');
    headerTitle.textContent = 'Change Color';
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.textContent = '×';
    header.appendChild(headerTitle);
    header.appendChild(closeBtn);

    const body = document.createElement('div');
    body.className = 'modal-body color-confirm-body';

    const infoSection = document.createElement('div');
    infoSection.className = 'color-confirm-info';
    const fieldDiv = document.createElement('div');
    fieldDiv.className = 'color-confirm-field';
    fieldDiv.textContent = field;
    const valueDiv = document.createElement('div');
    valueDiv.className = 'color-confirm-value';
    valueDiv.textContent = value;
    infoSection.appendChild(fieldDiv);
    infoSection.appendChild(valueDiv);

    const pickerRow = document.createElement('div');
    pickerRow.className = 'color-confirm-picker-row';
    const pickerLabel = document.createElement('label');
    pickerLabel.textContent = 'Pick color:';
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.className = 'color-confirm-input';
    colorInput.value = currentColor;
    const hexInput = document.createElement('input');
    hexInput.type = 'text';
    hexInput.className = 'color-confirm-hex';
    hexInput.value = currentColor;
    hexInput.maxLength = 7;
    pickerRow.appendChild(pickerLabel);
    pickerRow.appendChild(colorInput);
    pickerRow.appendChild(hexInput);

    const presetsSection = document.createElement('div');
    presetsSection.className = 'color-presets-section';
    const presetsLabel = document.createElement('div');
    presetsLabel.className = 'color-presets-label';
    presetsLabel.textContent = 'Quick colors';
    const presetsContainer = document.createElement('div');
    presetsContainer.className = 'color-presets';

    presetColors.forEach(color => {
      const preset = document.createElement('div');
      preset.className = 'color-preset';
      preset.style.backgroundColor = color;
      preset.dataset['color'] = color;
      if (color.toLowerCase() === currentColor.toLowerCase()) {
        preset.classList.add('selected');
      }
      presetsContainer.appendChild(preset);
    });

    presetsSection.appendChild(presetsLabel);
    presetsSection.appendChild(presetsContainer);

    const previewContainer = document.createElement('div');
    previewContainer.className = 'color-confirm-preview';

    const currentItem = document.createElement('div');
    currentItem.className = 'color-preview-item';
    const currentSwatch = document.createElement('div');
    currentSwatch.className = 'color-preview-swatch';
    currentSwatch.style.backgroundColor = currentColor;
    const currentLabel = document.createElement('div');
    currentLabel.className = 'color-preview-label';
    currentLabel.textContent = 'Current';
    currentItem.appendChild(currentSwatch);
    currentItem.appendChild(currentLabel);

    const arrow = document.createElement('div');
    arrow.className = 'color-preview-arrow';
    arrow.textContent = '→';

    const newItem = document.createElement('div');
    newItem.className = 'color-preview-item';
    const newSwatch = document.createElement('div');
    newSwatch.className = 'color-preview-swatch';
    newSwatch.style.backgroundColor = currentColor;
    const newLabel = document.createElement('div');
    newLabel.className = 'color-preview-label';
    newLabel.textContent = 'New';
    newItem.appendChild(newSwatch);
    newItem.appendChild(newLabel);

    previewContainer.appendChild(currentItem);
    previewContainer.appendChild(arrow);
    previewContainer.appendChild(newItem);

    body.appendChild(infoSection);
    body.appendChild(pickerRow);
    body.appendChild(presetsSection);
    body.appendChild(previewContainer);

    const footer = document.createElement('div');
    footer.className = 'modal-footer';
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn-secondary';
    cancelBtn.textContent = 'Cancel';
    const applyBtn = document.createElement('button');
    applyBtn.className = 'btn-primary';
    applyBtn.textContent = 'Apply';
    (applyBtn as HTMLButtonElement).disabled = true;
    footer.appendChild(cancelBtn);
    footer.appendChild(applyBtn);

    content.appendChild(header);
    content.appendChild(body);
    content.appendChild(footer);
    modal.appendChild(content);
    document.body.appendChild(modal);

    let selectedColor = currentColor;

    const updateColorDisplay = (color: string) => {
      selectedColor = color;
      colorInput.value = color;
      hexInput.value = color;
      newSwatch.style.backgroundColor = color;
      (applyBtn as HTMLButtonElement).disabled = color.toLowerCase() === currentColor.toLowerCase();

      presetsContainer.querySelectorAll('.color-preset').forEach(p => {
        p.classList.toggle('selected', (p as HTMLElement).dataset['color']!.toLowerCase() === color.toLowerCase());
      });
    };

    colorInput.addEventListener('input', (e) => updateColorDisplay((e.target as HTMLInputElement).value));

    hexInput.addEventListener('input', (e) => {
      let hex = (e.target as HTMLInputElement).value;
      if (!hex.startsWith('#')) hex = '#' + hex;
      if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
        updateColorDisplay(hex);
      }
    });

    presetsContainer.addEventListener('click', (e) => {
      const preset = (e.target as HTMLElement).closest('.color-preset') as HTMLElement | null;
      if (preset) {
        updateColorDisplay(preset.dataset['color']!);
      }
    });

    const closeModal = () => modal.remove();

    closeBtn.addEventListener('click', closeModal);
    cancelBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    applyBtn.addEventListener('click', () => {
      if (selectedColor.toLowerCase() === currentColor.toLowerCase()) return;

      swatch.style.backgroundColor = selectedColor;
      swatch.dataset['color'] = selectedColor;

      // Repaint and close before persisting: a failed save must not leave the
      // modal open or the tree on the palette colour.
      if (colorType === 'leaf-colors') {
        TreeMetadataSync.setLeafCustomColor(field, value, selectedColor);
        const metadata = TreeMetadataSync.state.metadata || this.app?.metadata;
        if (metadata) {
          this.setNodeStyles(TreeMetadataSync.computeNodeStyles(metadata));
        }
        closeModal();
        void this.persistCustomColors({ leafCustomColors: TreeMetadataSync.getLeafCustomColorsForSave() });
      } else {
        TreeMetadataSync.setCustomColor(field, value, selectedColor);
        this.updateMetadataColors();
        closeModal();
        void this.persistCustomColors({ customColors: TreeMetadataSync.getCustomColorsForSave() });
      }
    });
  }

  /**
   * Convert RGB array to hex color
   */
  rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(x => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  }

  /**
   * Update legend style based on position and visibility
   */
  updateLegendStyle(): void {
    if (!this.legendElement) return;

    this.legendElement.style.display = this.legendVisible ? 'flex' : 'none';

    this.legendElement.style.top = 'auto';
    this.legendElement.style.bottom = 'auto';
    this.legendElement.style.left = 'auto';
    this.legendElement.style.right = 'auto';

    if (typeof this.legendPosition === 'object' && this.legendPosition.x !== undefined) {
      this.legendElement.style.left = `${this.legendPosition.x}px`;
      this.legendElement.style.top = `${this.legendPosition.y}px`;
    } else {
      const margin = 20;
      const topMargin = 60;

      switch (this.legendPosition) {
        case 'top-left':
          this.legendElement.style.top = `${topMargin}px`;
          this.legendElement.style.left = `${margin}px`;
          break;
        case 'top-right':
          this.legendElement.style.top = `${topMargin}px`;
          this.legendElement.style.right = `${margin}px`;
          break;
        case 'bottom-left':
          this.legendElement.style.bottom = `${margin}px`;
          this.legendElement.style.left = `${margin}px`;
          break;
        case 'bottom-right':
          this.legendElement.style.bottom = `${margin}px`;
          this.legendElement.style.right = `${margin}px`;
          break;
      }
    }
  }

  /**
   * Toggle legend visibility
   */
  toggleLegend(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggle legend:', show);
    this.legendVisible = show;
    this.updateLegendStyle();
  }

  /**
   * Toggle legend collapse/expand
   */
  toggleLegendCollapse(): void {
    this.legendCollapsed = !this.legendCollapsed;
    if (this.legendElement) {
      if (this.legendCollapsed) {
        this.legendElement.classList.add('collapsed');
      } else {
        this.legendElement.classList.remove('collapsed');
      }
    }
    console.log('[PhylocanvasRenderer] Legend collapsed:', this.legendCollapsed);
  }

  /**
   * Update legend columns
   */
  updateLegendColumns(columns: number | string): void {
    this.legendColumns = parseInt(String(columns));
    if (this.legendElement) {
      this.legendElement.classList.remove('columns-1', 'columns-2', 'columns-3', 'columns-4');
      this.legendElement.classList.add(`columns-${this.legendColumns}`);
      this.legendElement.style.setProperty('--legend-columns', String(this.legendColumns));
    }
    console.log('[PhylocanvasRenderer] Legend columns:', this.legendColumns);
  }

  /**
   * Make legend draggable
   */
  makeLegendDraggable(): void {
    if (!this.legendElement) return;

    const header = this.legendElement.querySelector('.legend-header');
    if (!header) return;

    const onMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('.legend-collapse-btn')) return;

      this.legendDragging = true;
      this.legendElement!.classList.add('dragging');

      const rect = this.legendElement!.getBoundingClientRect();

      this.legendDragOffset.x = e.clientX - rect.left;
      this.legendDragOffset.y = e.clientY - rect.top;

      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!this.legendDragging) return;

      const containerRect = this.container.getBoundingClientRect();
      const legendRect = this.legendElement!.getBoundingClientRect();

      let x = e.clientX - containerRect.left - this.legendDragOffset.x;
      let y = e.clientY - containerRect.top - this.legendDragOffset.y;

      x = Math.max(0, Math.min(x, containerRect.width - legendRect.width));
      y = Math.max(0, Math.min(y, containerRect.height - legendRect.height));

      this.legendPosition = { x, y };
      this.legendElement!.style.left = `${x}px`;
      this.legendElement!.style.top = `${y}px`;
      this.legendElement!.style.right = 'auto';
      this.legendElement!.style.bottom = 'auto';
    };

    const onMouseUp = (_e: MouseEvent) => {
      if (!this.legendDragging) return;

      this.legendDragging = false;
      this.legendElement!.classList.remove('dragging');

      console.log('[PhylocanvasRenderer] Legend dragged to:', this.legendPosition);
    };

    header.addEventListener('mousedown', onMouseDown as EventListener);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    (this as any).legendDragHandlers = { onMouseDown, onMouseMove, onMouseUp, header };
  }

  /**
   * Update legend position
   */
  updateLegendPosition(position: string | { x: number; y: number }): void {
    console.log('[PhylocanvasRenderer] Update legend position:', position);
    this.legendPosition = position;
    this.updateLegendStyle();
  }

  /**
   * Create tooltip element
   */
  createTooltip(): void {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
    }

    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'phylocanvas-tooltip';
    this.tooltipElement.style.position = 'absolute';
    this.tooltipElement.style.background = 'rgba(0, 0, 0, 0.85)';
    this.tooltipElement.style.color = 'white';
    this.tooltipElement.style.padding = '8px 12px';
    this.tooltipElement.style.borderRadius = '4px';
    this.tooltipElement.style.fontSize = '12px';
    this.tooltipElement.style.pointerEvents = 'none';
    this.tooltipElement.style.zIndex = '10000';
    this.tooltipElement.style.display = 'none';
    this.tooltipElement.style.maxWidth = '300px';
    this.tooltipElement.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    this.tooltipElement.style.whiteSpace = 'pre-line';

    this.container.appendChild(this.tooltipElement);
  }

  /**
   * Applies the hover highlight at most once per animation frame and only
   * when the hovered node differs from the previously highlighted one.
   */
  scheduleHoverHighlight(nodeId: string | null): void {
    this._pendingHoverNodeId = nodeId;
    if (this._hoverFrame !== null) return;
    this._hoverFrame = requestAnimationFrame(() => {
      this._hoverFrame = null;
      const pending = this._pendingHoverNodeId ?? null;
      this._pendingHoverNodeId = undefined;
      if (!this._hoverTracker.nodeChanged(pending)) return;
      if (this.tree && this.tree.highlightNode) {
        this.tree.highlightNode(pending);
      }
    });
  }

  /**
   * Handle tree node hover for tooltip display and highlighting
   */
  handleTreeNodeHover(info: any, event: MouseEvent | null): void {
    this.scheduleHoverHighlight(info && info.object ? info.object.id : null);

    if (!this.tooltipElement) return;

    if (!this.tooltipEnabled) {
      this.tooltipElement.style.display = 'none';
      return;
    }

    if (info && info.object && info.object.isLeaf) {
      const nodeId = info.object.id;

      const rowData = TreeMetadataSync?.state.rowsByLabel.get(nodeId);

      if (rowData && rowData.length > 0) {
        const metadata = rowData[0]!.row;

        this.tooltipElement.textContent = '';
        const header = document.createElement('strong');
        header.textContent = nodeId;
        this.tooltipElement.appendChild(header);

        const fieldsToShow = this.tooltipFields && this.tooltipFields.length > 0
          ? this.tooltipFields
          : Object.keys(metadata);


        fieldsToShow.forEach(key => {
          if (Object.prototype.hasOwnProperty.call(metadata, key)) {
            const value = metadata[key];
            if (!this.isEmptyValue(value)) {
              this.tooltipElement!.appendChild(document.createElement('br'));
              const label = document.createElement('span');
              label.style.color = '#aaa';
              label.textContent = `${key}: `;
              this.tooltipElement!.appendChild(label);
              this.tooltipElement!.appendChild(document.createTextNode(value as string));
            }
          }
        });

        this.tooltipElement.style.fontWeight = '';
        this.tooltipElement.style.display = 'block';

        const x = info.x || event?.offsetX || 0;
        const y = info.y || event?.offsetY || 0;

        this.tooltipElement.style.left = `${x + 15}px`;
        this.tooltipElement.style.top = `${y + 15}px`;
      } else {
        this.tooltipElement.textContent = nodeId;
        this.tooltipElement.style.fontWeight = 'bold';
        this.tooltipElement.style.display = 'block';

        const x = info.x || event?.offsetX || 0;
        const y = info.y || event?.offsetY || 0;
        this.tooltipElement.style.left = `${x + 15}px`;
        this.tooltipElement.style.top = `${y + 15}px`;
      }
    } else {
      this.tooltipElement.style.display = 'none';
    }
  }

  /**
   * Set tooltip configuration
   */
  setTooltipConfig(config: { enabled?: boolean; fields?: string[] }): void {
    if (config.enabled !== undefined) {
      this.tooltipEnabled = config.enabled;
    }
    if (config.fields !== undefined) {
      this.tooltipFields = config.fields;
    }
    console.log('[PhylocanvasRenderer] Tooltip config updated:', { enabled: this.tooltipEnabled, fields: this.tooltipFields });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this._hoverFrame !== null) {
      cancelAnimationFrame(this._hoverFrame);
      this._hoverFrame = null;
    }
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }

    if (this.internalNodeContextMenu) {
      this.internalNodeContextMenu.remove();
      this.internalNodeContextMenu = null;
    }

    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }

    if (this.scalebarElement) {
      this.scalebarElement.remove();
      this.scalebarElement = null;
    }

    if (this.legendElement) {
      this.legendElement.remove();
      this.legendElement = null;
    }

    if (this.layoutMenuContainer) {
      this.layoutMenuContainer.remove();
      this.layoutMenuContainer = null;
    }

    if (this.menuContainer) {
      this.menuContainer.remove();
      this.menuContainer = null;
    }

    if (this.tooltipMenuContainer) {
      this.tooltipMenuContainer.remove();
      this.tooltipMenuContainer = null;
    }

    if (this.syncMenuContainer) {
      this.syncMenuContainer.remove();
      this.syncMenuContainer = null;
    }

    if (this.tree && this.tree.destroy) {
      this.tree.destroy();
    }

    // phylocanvas.gl paints its watermark as an inline background-image on the
    // container it is handed (the shared #tree-container) and never clears it on
    // teardown. innerHTML='' drops children but not inline styles, so the logo
    // bleeds through the next renderer's transparent SVG. Undo it here.
    if (this.container) {
      this.container.style.backgroundImage = '';
      this.container.style.backgroundRepeat = '';
      this.container.style.backgroundPosition = '';
    }

    if (this._containerResizeObserver) {
      this._containerResizeObserver.disconnect();
      this._containerResizeObserver = null;
    }

    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = null;
    }
    if (this._resizeScalebarTimeout) {
      clearTimeout(this._resizeScalebarTimeout);
      this._resizeScalebarTimeout = null;
    }

    console.log('[PhylocanvasRenderer] Resources cleaned up');
  }

  /**
   * Debounced resize wrapper
   */
  debouncedResize(): void {
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    this._resizeTimeout = setTimeout(() => {
      this.resize();
    }, 100);
  }

  /**
   * Resize handler
   */
  resize(): void {
    console.log('[PhylocanvasRenderer] Resize called');

    this.container.style.width = '100%';
    this.container.style.height = '100%';

    const panel = this.container.closest('.panel');
    const panelHeader = panel?.querySelector('.panel-header');
    const headerHeight = (panelHeader as HTMLElement | null)?.clientHeight || 0;

    console.log('[PhylocanvasRenderer] Panel size:', (panel as HTMLElement | null)?.clientWidth, 'x', (panel as HTMLElement | null)?.clientHeight);
    console.log('[PhylocanvasRenderer] Header height:', headerHeight);
    console.log('[PhylocanvasRenderer] Container size:', this.container.clientWidth, 'x', this.container.clientHeight);

    if (this.tree && this.tree.setProps) {
      const newSize = {
        width: (panel as HTMLElement | null)?.clientWidth || this.container.clientWidth || 800,
        height: ((panel as HTMLElement | null)?.clientHeight || this.container.clientHeight || 600) - headerHeight
      };
      console.log('[PhylocanvasRenderer] Setting new size:', newSize);
      this.tree.setProps({ size: newSize });

      if (this.scalebarVisible && this.scalebarElement) {
        if (this._resizeScalebarTimeout) clearTimeout(this._resizeScalebarTimeout);
        this._resizeScalebarTimeout = setTimeout(() => {
          this.updateScalebarLabel();
        }, 100);
      }
    } else {
      console.warn('[PhylocanvasRenderer] Tree or setProps not available');
    }
  }

  /**
   * Setup ResizeObserver on the tree container and parent panel to detect
   * layout changes that require canvas resizing (scrollbar shifts, panel
   * collapse/expand, flex reflow after initial render).
   */
  setupResizeHandlers(): void {
    if (!window.ResizeObserver) return;

    if (this._containerResizeObserver) {
      this._containerResizeObserver.disconnect();
    }

    this._containerResizeObserver = new ResizeObserver(() => {
      this.debouncedResize();
    });

    this._containerResizeObserver.observe(this.container);

    const panel = this.container.closest('.panel');
    if (panel) {
      this._containerResizeObserver.observe(panel);
    }
  }

  /**
   * Set tree length unit (substitution or SNP)
   */
  setTreeLengthUnit(unit: string): void {
    console.log('[PhylocanvasRenderer] Set tree length unit:', unit, 'current scalebarElement:', !!this.scalebarElement);
    this.treeLengthUnit = unit;

    if (this.tree && this.scalebarElement) {
      this.createScaleBar();
    }

    if (this.app && this.app.saveSettings) {
      this.app.saveSettings({
        phylocanvas: {
          treeLengthUnit: this.treeLengthUnit
        }
      });
    }
  }

  /**
   * Set decimal digits for rounding branch length labels
   * @param {number} digits - Number of decimal digits (0, 1, 2, etc.)
   * @param {boolean} skipSave - If true, skip saving settings (used when applying from external settings)
   */
  setDecimalDigits(digits: number, skipSave: boolean = false): void {
    const previousDigits = this.decimalDigits;
    console.log('[PhylocanvasRenderer] Set decimal digits:', digits, '(previous:', previousDigits + ')');
    this.decimalDigits = digits;

    const isSNPConversionActive = this.showBranchLengths && this.alignmentLength && this.alignmentLength > 0;

    const needsVisualRoundingReload = this.showBranchLengths &&
                                       !isSNPConversionActive &&
                                       (digits === 0 || previousDigits === 0);

    if ((isSNPConversionActive || needsVisualRoundingReload) && this.tree && this.tree.setProps) {
      console.log('[PhylocanvasRenderer] Reloading tree with new decimal digits:', {
        isSNPConversionActive,
        needsVisualRoundingReload,
        digits,
        previousDigits
      });

      const currentStyles = { ...this.nodeStyles };
      const currentSelectedIds = [...this.selectedIds];

      this.tree.setProps({
        source: this.getActiveNewick(),
        showBranchLengths: this.showBranchLengths,
        branchLengthsFormat: this.getBranchLengthFormatter()
      });

      setTimeout(() => {
        if (Object.keys(this.tipLabelMap).length > 0) {
          this.updateTipLabels(this.tipLabelMap);
        } else if (Object.keys(currentStyles).length > 0) {
          this.tree.setProps({ styles: currentStyles });
        }

        if (currentSelectedIds.length > 0) {
          this.tree.setProps({ selectedIds: currentSelectedIds });
        }

        if (this.metadataBlocks.length > 0 && Object.keys(this.metadata).length > 0) {
          this.setMetadataBlocks(this.metadataBlocks, this.metadata);
        }

        if (this.scalebarVisible) {
          this.createScaleBar();
        }
      }, 100);
    } else {
      if (this.tree && this.tree.setProps) {
        const wasShowing = this.showBranchLengths;

        if (wasShowing) {
          this.tree.setProps({ showBranchLengths: false });

          setTimeout(() => {
            this.tree.setProps({
              branchLengthsFormat: this.getBranchLengthFormatter(),
              showBranchLengths: true
            });
          }, 50);
        } else {
          this.tree.setProps({
            branchLengthsFormat: this.getBranchLengthFormatter()
          });
        }
      }

      if (this.tree && this.scalebarVisible) {
        this.createScaleBar();
      }
    }

    if (!skipSave && this.app && this.app.saveSettings) {
      this.app.saveSettings({
        phylocanvas: {
          decimalDigits: this.decimalDigits
        }
      });
    }
  }

  /**
   * Set alignment length for SNP translation
   */
  setAlignmentLength(length: number | null): void {
    const previousLength = this.alignmentLength;
    this.alignmentLength = length;

    if (length === null && previousLength !== null && this.tree && this.tree.setProps) {
      console.log('[PhylocanvasRenderer] Alignment length cleared, reloading tree with original Newick');

      const currentStyles = { ...this.nodeStyles };
      const currentSelectedIds = [...this.selectedIds];

      this.tree.setProps({
        source: this.originalNewick,
        showBranchLengths: this.showBranchLengths
      });

      setTimeout(() => {
        if (Object.keys(this.tipLabelMap).length > 0) {
          this.updateTipLabels(this.tipLabelMap);
        } else if (Object.keys(currentStyles).length > 0) {
          this.tree.setProps({ styles: currentStyles });
        }

        if (currentSelectedIds.length > 0) {
          this.tree.setProps({ selectedIds: currentSelectedIds });
        }

        if (this.metadataBlocks.length > 0 && Object.keys(this.metadata).length > 0) {
          this.setMetadataBlocks(this.metadataBlocks, this.metadata);
        }

        if (this.scalebarElement) {
          this.createScaleBar();
        }
      }, 100);
    } else {
      if (this.scalebarElement && this.tree) {
        this.createScaleBar();
      }
    }

    if (this.showBranchLengths && this.tree && this.tree.setProps) {
      this.toggleBranchLengths(true);
    }

    console.log('[PhylocanvasRenderer] Auto-saving alignment length:', this.alignmentLength);
    if (this.app && this.app.saveSettings) {
      this.app.saveSettings({
        phylocanvas: {
          alignmentLength: this.alignmentLength
        }
      }).then(() => {
        console.log('[PhylocanvasRenderer] Alignment length saved successfully');
      }).catch((err: any) => {
        console.error('[PhylocanvasRenderer] Failed to save alignment length:', err);
      });
    } else {
      console.error('[PhylocanvasRenderer] No app.saveSettings method available!');
    }
  }

  /**
   * Toggle scale bar visibility (alias for toggleScalebar)
   */
  toggleScaleBar(visible: boolean): void {
    this.toggleScalebar(visible);
  }

  /**
   * Update tip labels with custom display labels
   */
  updateTipLabels(labelMap: Record<string, string>): void {
    console.log('[PhylocanvasRenderer] Updating tip labels with map:', labelMap);
    this.tipLabelMap = labelMap || {};

    if (this.tree && this.tree.setProps) {
      console.log('[PhylocanvasRenderer] Current tree props:', this.tree.props);

      const labelStyles: Record<string, any> = {};
      Object.keys(this.tipLabelMap).forEach(nodeId => {
        labelStyles[nodeId] = {
          ...(this.nodeStyles[nodeId] || {}),
          label: this.tipLabelMap[nodeId]
        };
      });

      const mergedStyles = { ...this.nodeStyles, ...labelStyles };

      this.tree.setProps({
        styles: mergedStyles
      });

      console.log('[PhylocanvasRenderer] Tip labels updated via styles');
      console.log('[PhylocanvasRenderer] Label styles sample:', Object.entries(labelStyles).slice(0, 3));
    }
  }

  /**
   * Toggle branch length display
   */
  toggleBranchLengths(show: boolean): void {
    console.log('[PhylocanvasRenderer] Toggling branch lengths:', show);
    console.log('[PhylocanvasRenderer] Current alignment length:', this.alignmentLength);
    this.showBranchLengths = show;

    if (this.tree && this.tree.setProps) {
      const needsReload = (show && this.alignmentLength && this.alignmentLength > 0) ||
                         (!show && this.tree.props.source !== this.originalNewick);

      if (needsReload) {
        console.log('[PhylocanvasRenderer] Reloading tree with', show && this.alignmentLength ? 'SNP-scaled' : 'original', 'branch lengths');

        const currentStyles = { ...this.nodeStyles };
        const currentSelectedIds = [...this.selectedIds];

        this.tree.setProps({
          source: this.getActiveNewick(),
          showBranchLengths: show
        });

        setTimeout(() => {
          if (Object.keys(this.tipLabelMap).length > 0) {
            console.log('[PhylocanvasRenderer] Reapplying tip labels:', Object.keys(this.tipLabelMap).length);
            this.updateTipLabels(this.tipLabelMap);
          } else if (Object.keys(currentStyles).length > 0) {
            this.tree.setProps({ styles: currentStyles });
          }

          if (currentSelectedIds.length > 0) {
            this.tree.setProps({ selectedIds: currentSelectedIds });
          }
          if (this.metadataBlocks.length > 0 && Object.keys(this.metadata).length > 0) {
            this.setMetadataBlocks(this.metadataBlocks, this.metadata);
          }
        }, 100);
      } else {
        this.tree.setProps({
          showBranchLengths: show
        });
      }
    }

    console.log('[PhylocanvasRenderer] Auto-saving showBranchLengths:', this.showBranchLengths);
    if (this.app && this.app.saveSettings) {
      this.app.saveSettings({
        phylocanvas: {
          showBranchLengths: this.showBranchLengths
        }
      }).then(() => {
        console.log('[PhylocanvasRenderer] showBranchLengths saved successfully');
      }).catch((err: any) => {
        console.error('[PhylocanvasRenderer] Failed to save showBranchLengths:', err);
      });
    } else {
      console.error('[PhylocanvasRenderer] No app.saveSettings method available!');
    }
  }
}
